import type { PermissionKey } from '@till-erp/platform/permissions';
import type { RuleDefinition as PlatformRuleDefinition } from '@till-erp/platform/rules';
import type { RecurrenceRuleInput, TaskGroup, TaskLabelColor, TaskPriority, TaskRecurrenceStatus, TaskStage } from '@till-erp/collab/values';
import type { ColumnStates, Density, IncomingForecastViewPreference, OrderChecksResultState, SidebarState, TaskReminderState, ThemeChoice, UserRole, UserStatus, WorkArea } from '@till-erp/platform/user-values';
import type { ChatterItemKind, EmailLanguage, EmailOrigin, EmailStatus } from '@till-erp/collab/values';
import type { TermsSource } from '@till-erp/customers/terms';
import type { PaymentIdentityKind, PaymentIdentityOrigin } from '@till-erp/customers/payment-identity';
import type { AdvertisingBlockSource } from '@till-erp/customers/advertising-block';
import type { CustomerSalutation, CustomerKind, DocumentLanguage, EInvoiceFormat, EmailPurpose, Incoterm } from '@till-erp/customers/values';
import type { FieldResult, VatIdCheckOutcome, VatIdCheckState } from '@till-erp/customers/vat-id';
import type { VatIdStatusState } from '@till-erp/customers/vat-id-status';
import type { SettingInput } from '@till-erp/settings/values';

/** Fehler der Schnittstelle (application/problem+json) mit deutschem Text. */
export class ApiProblem extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly errors: Record<string, string> = {},
    /** Maschinenlesbares Merkmal des Servers, z. B. `second_factor_required`. */
    readonly code: string | null = null,
    /** Bei 409: aktueller Stand des Datensatzes (inklusive wer zuletzt geändert hat). */
    readonly current: unknown = undefined,
  ) {
    super(detail);
    this.name = 'ApiProblem';
  }
}

export type TokenGetter = () => Promise<string | null>;

/** 401 vom Server: Ausweis abgelaufen, ungültig oder zweiter Faktor jetzt verlangt. */
export const SECOND_FACTOR_REQUIRED = 'second_factor_required';

let unauthorizedListener: (() => void) | null = null;

/**
 * Meldet ein 401 mitten in der Arbeit an die App-Hülle, damit sie die Anmeldung neu
 * bewertet (z. B. Code-Schritt, nachdem ein Admin den zweiten Faktor eingeschaltet hat).
 */
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListener = listener;
  return () => {
    if (unauthorizedListener === listener) unauthorizedListener = null;
  };
}

/** Open an authenticated SSE response with the same session-expiry handling as finite API calls. */
export async function apiGetEventStream(
  getToken: TokenGetter,
  path: string,
  init: { signal: AbortSignal; cursor: string | null },
): Promise<Response> {
  const token = await getToken();
  init.signal.throwIfAborted();
  const headers: Record<string, string> = { accept: 'text/event-stream' };
  if (token) headers.authorization = `Bearer ${token}`;
  if (init.cursor !== null) headers['Last-Event-ID'] = init.cursor;
  const response = await fetch(path, { headers, signal: init.signal });
  if (!response.ok) {
    if (response.status === 401) unauthorizedListener?.();
    let problem: { detail?: string; code?: string } = {};
    try { problem = await response.json() as typeof problem; } catch { /* Preserve non-JSON HTTP errors. */ }
    throw new ApiProblem(response.status, problem.detail ?? 'Verbindung unterbrochen', {}, problem.code ?? null);
  }
  return response;
}

export async function apiFetch<T>(
  getToken: TokenGetter,
  path: string,
  init: {
    method?: string;
    headers?: Record<string, string>;
    body?: unknown;
    /** false: 401 nicht melden (die Bewertung selbst). */
    notifyUnauthorized?: boolean;
    /** Bewusst angeforderter Zählvertrag, z. B. count=exact beim Listenabruf. */
    prefer?: string;
    /** Pflicht bei schreibenden Aufrufen: ein neuer Schlüssel je Vorgang (AD-4). */
    idempotencyKey?: string;
    /** Story 5.120: Abbrechen bzw. Zeitüberschreitung (`AbortSignal.timeout`) – dann wie „nicht erreichbar“. */
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = { accept: 'application/json', ...init.headers };
  if (token) headers.authorization = `Bearer ${token}`;
  if (init.body !== undefined) headers['content-type'] = 'application/json';
  if (init.idempotencyKey) headers['idempotency-key'] = init.idempotencyKey;
  if (init.prefer) headers.prefer = init.prefer;
  let res: Response;
  try {
    res = await fetch(path, {
      method: init.method ?? 'GET',
      headers,
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      ...(init.signal ? { signal: init.signal } : {}),
    });
  } catch {
    throw new ApiProblem(0, 'Der Server ist gerade nicht erreichbar. Prüfe die Internetverbindung und versuche es gleich noch einmal.');
  }
  if (res.status === 401 && init.notifyUnauthorized !== false) unauthorizedListener?.();
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const p = (data ?? {}) as { detail?: unknown; errors?: Record<string, string>; code?: string; current?: unknown };
    const detail = typeof p.detail === 'string' && p.detail.trim() ? p.detail : 'Etwas ist schiefgelaufen.';
    throw new ApiProblem(res.status, detail, p.errors ?? {}, p.code ?? null, p.current);
  }
  return data as T;
}

/**
 * Datei der Schnittstelle als Blob lesen (mit Ausweis). Für Dateien im Chatter: ein normaler Link kann
 * keinen Ausweis mitschicken.
 */
export async function apiFetchBlob(
  getToken: TokenGetter,
  path: string,
  init: {
    /** Abbrechen (z. B. Bild nicht mehr angezeigt). */
    signal?: AbortSignal;
    /** false: 401 nicht melden (z. B. viele Vorschaubilder; die Liste selbst meldet es). */
    notifyUnauthorized?: boolean;
  } = {},
): Promise<Blob> {
  const token = await getToken();
  let res: Response;
  try {
    res = await fetch(path, { headers: token ? { authorization: `Bearer ${token}` } : {}, signal: init.signal ?? null });
  } catch (err) {
    if (init.signal?.aborted) throw err;
    throw new ApiProblem(0, 'Der Server ist gerade nicht erreichbar. Prüfe die Internetverbindung und versuche es gleich noch einmal.');
  }
  if (!res.ok) {
    const text = await res.text();
    let p: { detail?: string; code?: string } = {};
    try {
      p = text ? (JSON.parse(text) as typeof p) : {};
    } catch {
      p = {};
    }
    if (res.status === 401 && init.notifyUnauthorized !== false) unauthorizedListener?.();
    throw new ApiProblem(res.status, p.detail ?? 'Etwas ist schiefgelaufen.', {}, p.code ?? null);
  }
  return res.blob();
}

/** Authenticated request-bound POST NDJSON stream, including the existing 401 contract. */
export async function apiPostStream<T>(
  getToken: TokenGetter,
  path: string,
  body: unknown,
  onMessage: (message: T) => void | Promise<void>,
  init: { signal?: AbortSignal; notifyUnauthorized?: boolean } = {},
): Promise<void> {
  const cancellation = new AbortController();
  const abort = () => cancellation.abort();
  init.signal?.addEventListener('abort', abort, { once: true });
  if (init.signal?.aborted) abort();
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  try {
    const token = await getToken();
    cancellation.signal.throwIfAborted();
    const headers: Record<string, string> = { accept: 'application/x-ndjson', 'content-type': 'application/json' };
    if (token) headers.authorization = `Bearer ${token}`;
    let response: Response;
    try {
      response = await fetch(path, { method: 'POST', headers, body: JSON.stringify(body), signal: cancellation.signal });
    } catch (error) {
      if (cancellation.signal.aborted) throw error;
      throw new ApiProblem(0, 'Der Server ist gerade nicht erreichbar. Prüfe die Internetverbindung und versuche es gleich noch einmal.');
    }
    if (!response.ok) {
      if (response.status === 401 && init.notifyUnauthorized !== false) unauthorizedListener?.();
      let problem: { detail?: string; errors?: Record<string, string>; code?: string; current?: unknown } = {};
      try { problem = await response.json() as typeof problem; } catch { /* preserve status for non-JSON failures */ }
      throw new ApiProblem(response.status, problem.detail ?? 'Etwas ist schiefgelaufen.', problem.errors ?? {}, problem.code ?? null, problem.current);
    }
    if (!response.body) throw new ApiProblem(0, 'Die Simulation konnte nicht gestartet werden. Bitte erneut versuchen.');
    reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      let chunk: ReadableStreamReadResult<Uint8Array>;
      try { chunk = await reader.read(); } catch (error) {
        if (cancellation.signal.aborted) throw error;
        throw new ApiProblem(0, 'Die Verbindung zum Server wurde unterbrochen. Prüfe die Internetverbindung und versuche es gleich noch einmal.');
      }
      cancellation.signal.throwIfAborted();
      buffer += decoder.decode(chunk.value, { stream: !chunk.done });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) if (line.trim()) await onMessage(JSON.parse(line) as T);
      if (chunk.done) {
        if (buffer.trim()) await onMessage(JSON.parse(buffer) as T);
        break;
      }
    }
  } finally {
    cancellation.abort();
    init.signal?.removeEventListener('abort', abort);
    if (reader) {
      try { await reader.cancel(); } catch { /* An errored/aborted stream may reject cleanup; preserve the request error. */ } finally { reader.releaseLock(); }
    }
  }
}

/** Dateiname aus `Content-Disposition` (UTF-8-Form `filename*` vor dem ASCII-Ersatz). */
export function fileNameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]!.trim());
    } catch {
      // ungültige Kodierung: ASCII-Ersatz
    }
  }
  const ascii = /filename="([^"]*)"/i.exec(header);
  return ascii?.[1] || fallback;
}

/**
 * Datei per POST abholen (mit Ausweis), z. B. den Export einer Liste (Story 18.8), und im Browser speichern. Fehler
 * kommen als `ApiProblem` mit dem deutschen Satz des Servers. Ohne `body` (`undefined`) per GET, z. B. PDF oder Excel
 * einer Lieferantenbestellung (Story 10.3).
 */
export async function apiDownload(getToken: TokenGetter, path: string, body: unknown, fallbackName: string): Promise<string> {
  const file = await apiFetchFile(getToken, path, body);
  const fileName = fileNameFromDisposition(file.disposition, fallbackName);
  const url = URL.createObjectURL(file.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return fileName;
}

/**
 * Datei per POST (bzw. ohne `body` per GET) mit Ausweis holen, ohne sie zu speichern – z. B. das PDF der Beleg-Vorschau
 * zum Drucken (Story 9.15). Fehler als `ApiProblem` mit dem deutschen Satz des Servers.
 */
export async function apiFetchFile(getToken: TokenGetter, path: string, body: unknown): Promise<{ blob: Blob; disposition: string | null }> {
  const token = await getToken();
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (token) headers.authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(path, body === undefined ? { method: 'GET', headers } : { method: 'POST', headers, body: JSON.stringify(body) });
  } catch {
    throw new ApiProblem(0, 'Der Server ist gerade nicht erreichbar. Prüfe die Internetverbindung und versuche es gleich noch einmal.');
  }
  if (!res.ok) {
    const text = await res.text();
    let p: { detail?: string; code?: string; errors?: Record<string, string> } = {};
    try {
      p = text ? (JSON.parse(text) as typeof p) : {};
    } catch {
      p = {};
    }
    if (res.status === 401) unauthorizedListener?.();
    throw new ApiProblem(res.status, p.detail ?? 'Etwas ist schiefgelaufen.', p.errors ?? {}, p.code ?? null);
  }
  return { blob: await res.blob(), disposition: res.headers.get('content-disposition') };
}

export interface Me {
  access?: { roleId: string; roleName: string; roleVersion: number; permissions: PermissionKey[]; scopes: Partial<Record<PermissionKey, string[]>> };
  id: string;
  displayName: string;
  initials: string;
  email: string;
  role: UserRole;
  workArea: WorkArea | null;
  density: Density;
  /** Wirksames Hell/Dunkel (ohne eigene Wahl `light`). */
  theme: ThemeChoice;
  /** Seitenleiste am Desktop (`collapsed` = schmale Symbolleiste, `expanded` = breit). */
  sidebar: SidebarState;
  /** Eigene Startwerte der Rechen-Leiste im Bestellvorschlag (Story 10.11); `null` = Startwerte der Firma. */
  reorderView: ReorderViewChoice | null;
  /** Spalte „Ergebnis, wenn verletzt“ der Ampel-Prüfungen (Einstellungen › Verkauf › Aufträge, Story 7.40). */
  orderChecksResult: OrderChecksResultState;
  /** Erinnerungen vor Fälligkeit in der Glocke (Story 23.13, Profil › Benachrichtigungen). */
  taskReminders: TaskReminderState;
  /** Einklappbare Spalten je Tabelle (Story 7.41); fehlende Kennung = ausgeklappt. */
  columnStates: ColumnStates;
  /** Schalter der Seite Zahlungseingänge (Story 18.27); `null` = Startwerte. */
  incomingForecastView: IncomingForecastViewPreference | null;
  company: string;
  trialPhase: boolean;
  /** Testuhr dieser Umgebung; `null` = gibt es hier nicht (live). */
  testClock: { active: boolean; /** Fachliches Jetzt beim Laden (ISO). */ now: string } | null;
}

/** Zeitjob-Lauf (Einstellungen › Testuhr). */
export interface TimedJobRun {
  job: string;
  label: string;
  scheduledAt: string;
  status: 'running' | 'done' | 'failed' | 'skipped';
  finishedAt: string | null;
}

/** Umstellung der Testuhr; `null` = echte Zeit. */
export interface TestClockChange {
  changedAt: string;
  changedBy: string;
  oldBusinessAt: string | null;
  newBusinessAt: string | null;
}

export interface TestClockView {
  active: boolean;
  now: string;
  version: number;
  runs: TimedJobRun[];
  history: TestClockChange[];
}

/** Benutzer in der Verwaltung (nur Admins). */
export interface UserView {
  id: string;
  displayName: string;
  initials: string;
  email: string;
  role: UserRole;
  workArea: WorkArea | null;
  status: UserStatus;
  version: number;
  createdAt: string;
}

export interface UserHistoryItem {
  id: string;
  at: string;
  actorName: string;
  text: string;
}

export interface UserDetail extends UserView {
  history: UserHistoryItem[];
}

/** Zustellung des Events einer Änderung (Ereignis-Rückgrat); `null` bei Startwerten. */
export type SettingDelivery = { status: 'pending' } | { status: 'delivered'; seconds: number } | null;

/** Stand einer Version relativ zu jetzt (`planned` nur Altbestand vor Story 1.26). */
export type SettingVersionStatus = 'current' | 'planned' | 'past' | 'superseded';

export interface SettingHistoryItem {
  version: number;
  value: unknown;
  previousValue: unknown;
  /** Speichertag `YYYY-MM-DD`. */
  validFrom: string;
  /** Letzter Tag der Gültigkeit; `null` = offen oder ersetzt. */
  validUntil: string | null;
  /** Ab diesem Zeitpunkt (ISO) gilt die Version. */
  effectiveAt: string;
  /** Ab diesem Zeitpunkt gilt die nächste Version; `null` = offen oder ersetzt. */
  effectiveUntil: string | null;
  supersededBy: number | null;
  status: SettingVersionStatus;
  changedAt: string;
  changedByName: string;
  delivery: SettingDelivery;
}

/** Eine gültige oder geplante Version. */
export interface SettingVersion {
  value: unknown;
  version: number;
  validFrom: string;
  validUntil: string | null;
  effectiveAt: string;
  effectiveUntil: string | null;
}

/** Vorschlag für ein Einzelfeld unter Einstellungen › Firma (Übernahme der alten Sammelfelder). */
export interface CompanyLegacyProposal {
  field: string;
  key: string;
  label: string;
  value: string;
  /** `new` wird übernommen, `same` steht schon so da, `differs` – Feld hat einen anderen Wert (bleibt). */
  status: 'new' | 'same' | 'differs';
  current: string | null;
  /** Version des Einzelfelds (beim Übernehmen mitschicken). */
  version: number;
}

/** Altes Sammelfeld mit Vorschlag der Zerlegung (`GET /api/v1/settings-company/legacy`). */
export interface CompanyLegacyArea {
  area: 'address' | 'contact' | 'register' | 'bank';
  label: string;
  key: string;
  value: string;
  version: number;
  /** Versionen aller Einzelfelder des Bereichs (beim Übernehmen und Erledigen mitschicken). */
  fields: { key: string; version: number }[];
  proposals: CompanyLegacyProposal[];
  rest: string[];
}

export interface CompanyLegacyView {
  areas: CompanyLegacyArea[];
  canChange: boolean;
}

export interface SettingView {
  key: string;
  group: string;
  /** Themen-Abschnitt innerhalb der Gruppe (z. B. „Briefkopf“); `null` = ohne. */
  section: string | null;
  /** Zeile innerhalb der Karte: aufeinanderfolgende Einstellungen mit gleicher Zeile stehen nebeneinander; `null` = eigene Zeile. */
  row: string | null;
  label: string;
  description: string;
  /** Eingabeart: daraus entstehen Formular und Prüfregel (dasselbe Zod-Schema wie auf dem Server). */
  input: SettingInput;
  editable: boolean;
  /** Heutiges fachliches Datum. */
  today: string;
  /** Zuletzt gespeicherter Wert und seine Version (für die Konkurrenzprüfung beim Speichern). */
  value: unknown;
  version: number;
  validFrom: string | null;
  /** Ab hier gilt die zuletzt gespeicherte Version; `null`, solange nie gesetzt. */
  effectiveAt: string | null;
  /** Zeitpunkt und Person der letzten Änderung; `null`, solange nie gesetzt. */
  changedAt: string | null;
  changedByName: string | null;
  /** Was jetzt gilt; `null`, solange nichts gilt. */
  effective: SettingVersion | null;
  /** Gespeicherte Werte, die erst später gelten (früheste zuerst; nur Altbestand vor Story 1.26). */
  planned: SettingVersion[];
  history: SettingHistoryItem[];
  /** Cursor für ältere Verlaufseinträge (`GET …/history`), `null` wenn keine. */
  historyNextCursor: string | null;
}

export interface SettingGroupSummary {
  id: string;
  label: string;
  description: string;
  /** `section`: Kartentitel innerhalb der Gruppe (`null` = Karte der Gruppe), für den Pfad der Einstellungs-Suche. */
  settings: { key: string; label: string; description: string; section: string | null }[];
}

/**
 * `GET /api/v1/settings/choices` (Ticket 1.262): Vorschläge für Zuordnungs-Einstellungen – Versand-/Zahlart-Namen aus
 * gelesenen Shopware-Aufträgen und an Artikeln gelesene Shopware-Einheiten (je häufigste zuerst).
 */
export interface SettingChoices {
  shippingMethods: string[];
  paymentMethods: string[];
  shopwareUnits: { id: string; articles: number }[];
}

/** `GET /api/v1/settings/overview` (Ticket 1.209): fehlende Pflichtangaben und zuletzt geänderte Einstellungen. */
export interface SettingsOverview {
  missing: { key: string; group: string; label: string }[];
  recent: {
    key: string;
    group: string;
    label: string;
    input: SettingInput;
    value: unknown;
    /** `null` bei der ersten Version. */
    previousValue: unknown;
    changedAt: string;
    changedByName: string;
  }[];
}

/** Angelegte Wiedervorlage im Chatter. */
export interface ChatterTask {
  id: string;
  title: string;
  assigneeId: string;
  assigneeName: string;
  /** Arbeitsbereich ohne zuständige Person; `null` = Admins. */
  area?: WorkArea | null;
  /** Gruppe der Aufgabenseite; `approvals` = Freigabe, sonst Wiedervorlage. */
  group?: TaskGroup;
  /** ISO-Datum `YYYY-MM-DD`. */
  dueDate: string;
  status: 'open' | 'done';
}

/** Angehängte Datei im Chatter. */
export interface ChatterAttachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  /** Der Browser darf sie anzeigen (PDF, Bild, Text); sonst nur herunterladen. */
  safeToOpen: boolean;
}

/** Eintrag im Chatter eines Datensatzes. */
export interface ChatterItem {
  id: string;
  kind: ChatterItemKind;
  at: string;
  authorName: string;
  body: string | null;
  task: ChatterTask | null;
  attachment: ChatterAttachment | null;
  /** Änderung aus dem Protokoll als Satz; `link` = Teil des Satzes, der einen anderen Datensatz öffnet (Story 9.23). */
  change: { text: string; link?: { label: string; href: string } } | null;
  /** E-Mail an den Datensatz. */
  email: ChatterEmail | null;
  /** Herkunft bei zusammengeführten Datensätzen (Story 4.11), z. B. „20120 · Alt GmbH“; `null`/fehlt = dieser Datensatz. */
  fromRecord?: string | null;
  /** Nur Notizen (Story 23.20): erwähnte Personen (Vorbelegung „Als Aufgabe …“). */
  mentions?: { id: string; displayName: string; initials: string }[];
  /** Nur Notizen (Story 23.20): nicht gelöschte Aufgaben aus dieser Notiz (Pill „→ Aufgabe“). */
  noteTasks?: ChatterTask[];
}

/** E-Mail im Chatter (Story 2.2). */
export interface ChatterEmail {
  id: string;
  subject: string;
  recipient: string;
  origin: EmailOrigin;
  status: EmailStatus;
  statusAt: string | null;
  detail: string | null;
  resendOf: string | null;
  /** Text der E-Mail (Schnappschuss, Story 2.7). */
  body: string;
  language: EmailLanguage | null;
  template: { id: string; name: string; version: number } | null;
  /** Anhänge; `document` = Beleg aus dem Archiv (Story 3.10), `sha256` = Prüfsumme des Inhalts. */
  attachments: { fileName: string; contentType: string; sizeBytes: number; sha256: string; document: { number: string; versionNo: number } | null }[];
}

/** E-Mail-Vorlage im neuesten Stand bzw. eine Version (Story 2.7). */
export interface MailTemplate {
  id: string;
  version: number;
  name: string;
  subjectDe: string;
  bodyDe: string;
  subjectEn: string;
  bodyEn: string;
  /** Anlass der Vorlage (Story 2.16), z. B. `invoice`; `manual` = frei. */
  occasion: string;
  occasionLabel: string;
  changedByName: string | null;
  changedAt: string;
}

/** Anlass der E-Mail-Vorlagen (Story 2.16, `GET /mail/occasions`). */
export interface MailOccasion {
  code: string;
  label: string;
  entityTypes: string[] | null;
  placeholders: string[];
}

/** Platzhalter in der Auswahl des Vorlagen-Editors (Story 2.15, `GET /mail/placeholders`). */
export interface MailPlaceholderItem {
  name: string;
  type: 'text' | 'number' | 'money' | 'date' | 'enum' | 'block' | 'table' | 'link' | 'attachment';
  languageIndependent?: boolean;
  /** Grauer Beispielwert (deutscher Text). */
  example: string;
  synonyms: string[];
  options: { code: string; de: string; en: string }[] | null;
}

export interface MailPlaceholderCatalog {
  occasion: string;
  groups: { id: string; label: string; placeholders: MailPlaceholderItem[] }[];
  examples: { id: string; label: string }[];
  entityTypes: string[];
}

/** Live-Vorschau im Vorlagen-Editor (Story 2.15, `POST /mail/templates/preview`). */
export interface MailTemplatePreview {
  subject: string;
  html: string;
  text: string;
  empty: string[];
  unknown: string[];
  errors: { subject?: string; body?: string };
  fieldErrors: Partial<Record<'subjectDe' | 'bodyDe' | 'subjectEn' | 'bodyEn', string>>;
  onlyIn: { de: string[]; en: string[] };
  version: string;
  source: { kind: 'example'; id: string; label: string } | { kind: 'record'; entityType: string; entityId: string; label: string };
}

export interface MailTemplateList {
  items: MailTemplate[];
  placeholders: string[];
  canEdit: boolean;
}

/** Was der Knopf „E-Mail“ im Chatter braucht (Story 2.7). */
export interface MailCompose {
  available: boolean;
  recipients: { address: string; label: string }[];
  language: EmailLanguage;
  templates: MailTemplate[];
  placeholders: string[];
  attachments: { id: string; fileName: string; contentType: string; sizeBytes: number }[];
  attachmentMaxBytes: number;
  /** Belege des Datensatzes je Fassung, neueste zuerst (Story 3.10, „Beleg anhängen“). */
  documents: MailComposeDocument[];
}

/** Fassung eines Belegs im E-Mail-Fenster: nur `attachable` ist wählbar, sonst `reason`. */
export interface MailComposeDocument {
  documentId: string;
  versionId: string;
  number: string;
  versionNo: number;
  status: 'rendering' | 'sealed' | 'invalid';
  attachable: boolean;
  reason: string | null;
  artifacts: { id: string; kind: string; fileName: string; contentType: string; sizeBytes: number; sha256: string }[];
}

export interface MailPreview {
  subject: string;
  body: string;
  missing: string[];
  missingText: string | null;
  /** HTML-Fassung (Story 2.14, Render-Kern). */
  html: string;
  /** Dateien der Anhang-Platzhalter (bleiben im Text stehen). */
  attachments: { name: string; fileName: string }[];
  renderVersion: string;
  template: { id: string; version: number; name: string };
}

/** Eigene E-Mail-Signatur (Story 2.14, Profil). */
export interface MailSignature {
  text: string;
  /** Gelesene Version; 0 = noch keine. */
  version: number;
  changedAt: string | null;
  /** Höchstlänge heute (Einstellung `mail_signature_max_chars`). */
  maxChars: number;
}

/** Zeile der Versandliste unter Einstellungen › E-Mail. */
export interface OutboundMail extends ChatterEmail {
  entityType: string;
  entityId: string;
  entityLabel: string;
  entityName: string;
  requestedByName: string | null;
  createdAt: string;
}

/** Einstellungen › E-Mail: Postfach, Zustand, Zähler, Versandliste. */
export interface MailOverview {
  sender: string | null;
  missing: string[];
  credential: boolean;
  switchMode: 'off' | 'shadow' | 'live';
  reachable: boolean;
  unreachableSince: string | null;
  connected: boolean;
  counts: { sent: number; waiting: number; failed: number; shadowed: number; skipped: number; unsubscribed: number };
  quota: { used: number; limit: number } | null;
  /** Hinweis an die Admins ab so viel Prozent der Tagesgrenze (Story 2.9); `null` = kein Hinweis. */
  quotaWarnPercent: number | null;
  /** So viele E-Mails warten, weil die Tagesgrenze erreicht ist. */
  waitingForQuota: number;
  /** Ab wann sie hinausgehen (00:00 des Folgetags). */
  quotaResumesAt: string | null;
  rateLimit: number | null;
  items: OutboundMail[];
  canSendTest: boolean;
}

export interface MailActionResult {
  mail: { id: string; created: boolean };
  overview: MailOverview;
}

/** Person, die einem Datensatz folgt. */
export interface Follower {
  id: string;
  displayName: string;
}

/** Folgen am Datensatz: folgt die angemeldete Person, und wer folgt („Folgen: …“). */
export interface FollowState {
  following: boolean;
  followers: Follower[];
}

export interface ChatterPage extends FollowState {
  items: ChatterItem[];
  nextCursor: string | null;
  /** Heutiges fachliches Datum (Server). */
  today: string;
  /** Höchstgröße einer Datei in Bytes (Einstellung). */
  attachmentMaxBytes: number;
  /** Art und Anzeigename des Datensatzes (Story 23.20). */
  record?: { label: string; name: string };
}

/** Eintrag der Karte „Aktivität“ auf der Startseite. */
export interface ActivityItem {
  id: string;
  kind: ChatterItemKind;
  at: string;
  text: string;
  entityType: string;
  entityId: string;
  entityLabel: string;
  entityName: string;
}

/** Meldung zum Wareneingang (Story 7.17, `GET /api/v1/orders/receipt-notices/{id}`); die Seite zeigt die Aufträge in der Auftragsliste. */
export interface ReceiptNotice {
  id: string;
  reference: string | null;
  text: string | null;
  ready: { orderId: string; number: string; customer: string | null; partial: boolean }[];
  waiting: { orderId: string; number: string; customer: string | null; reasons: string[] }[];
}

/** Eintrag der Glocke: @Erwähnung oder Änderung an einem gefolgten Datensatz. */
export interface NotificationItem {
  id: string;
  /** `area` = Bereichs-Hinweis (Story 7.17), z. B. „3 Aufträge können jetzt raus – Wareneingang …“. */
  kind: 'mention' | 'change' | 'system' | 'watch' | 'area' | 'person';
  at: string;
  text: string;
  entityType: string;
  entityId: string;
  entityLabel: string;
  entityName: string;
  /** Text der Notiz (nur bei Erwähnung). */
  body: string | null;
  /** Darf die Person den Datensatz öffnen? Sonst ohne Link. */
  canOpen: boolean;
  /** Systemhinweis: eigener Ort (z. B. `/einstellungen/e-mail/postausgang`); sonst `null` = der Datensatz. */
  href: string | null;
  /** Knopftext eines Links zum `href` (nur `person`, z. B. „Jetzt gehen“, Story 22.6/22.18); `null` = ganze Meldung führt hin. */
  linkLabel?: string | null;
  /** Neuer als der Gelesen-Stand der Person (Reiter „Ungelesen“, Ticket 1.243; fehlt bei älteren Antworten). */
  unread?: boolean;
}

/** „Neu im ERP“ (Story 1.99): eine Neuerung nach einer Auslieferung. */
export interface ReleaseNote {
  id: string;
  /** Ticketnummer, z. B. „6.22“. */
  key: string;
  /** Datum des Merges `YYYY-MM-DD`. */
  date: string;
  text: string;
  href: string;
  unread: boolean;
}

export interface NotificationList {
  items: NotificationItem[];
  /** „Neu im ERP“: neueste Neuerungen (fehlt bei älteren Antworten). */
  news?: ReleaseNote[];
  /** Neues seit dem letzten Öffnen (Warnpunkt). */
  unseen: boolean;
}

/** Wichtiger Hinweis: eine nicht erreichbare Gegenstelle. */
export interface CounterpartNotice {
  counterpart: string;
  label: string;
  since: string;
  waiting: number;
}

/** Stille Gegenstelle (Story 1.147): länger ohne Meldung als ihr Mindest-Meldetakt. */
export interface SilentCounterpart {
  counterpart: string;
  label: string;
  silentSince: string;
  silentMinutes: number;
  expectedMinutes: number;
  /** „Shopware: seit 20 Minuten keine Meldung (erwartet alle 15 Minuten)“. */
  text: string;
}

/** Antwort von `/api/v1/notices`. */
export interface NoticesResponse {
  items: CounterpartNotice[];
  /** Fehlt bei älteren Antworten. */
  silent?: SilentCounterpart[];
}

/** Zeile der Aufgabenseite (Wiedervorlage). */
/** Zeile im Papierkorb der Aufgaben ([RUDOLF] 29.10.2026). */
export interface TaskTrashItem {
  id: string;
  title: string;
  group: TaskGroup;
  entityType: string | null;
  entityId: string | null;
  entityLabel: string | null;
  entityName: string | null;
  /** Story 23.17: Rolle Mitarbeiter – Bezug nur mit Nummer (`entityName`), ohne Link. */
  entityLocked: boolean;
  deletedAt: string;
  deletedByName: string | null;
  /** Endgültig entfernt am (`YYYY-MM-DD`, Europe/Berlin). */
  purgeOn: string;
}

/** Papierkorb: `days` aus der Einstellung „Papierkorb der Aufgaben“. */
export interface TaskTrash {
  today: string;
  days: number;
  items: TaskTrashItem[];
}

export interface TaskItem {
  id: string;
  title: string;
  dueDate: string;
  overdue: boolean;
  /** `null` = freie Aufgabe ohne Datensatz (Story 23.1). */
  entityType: string | null;
  entityId: string | null;
  entityLabel: string | null;
  /** Anzeigename des Datensatzes; bei `entityLocked` nur die Nummer (oder `null`, wenn die Art keine hat). */
  entityName: string | null;
  /** Story 23.17: Rolle Mitarbeiter liest den Datensatz nicht – nur Art und Nummer, kein Link, Schloss. */
  entityLocked: boolean;
  priority: TaskPriority;
  stage: TaskStage;
  startDate: string | null;
  /** `null` = gehört dem Bereich. */
  assigneeId: string | null;
  assigneeName: string | null;
  area: WorkArea | null;
  group: TaskGroup;
  /** Wer angelegt hat; `null` = vom System. */
  createdBy: string | null;
  createdByName: string;
  /** Darf die angemeldete Person löschen (Papierkorb)? Ersteller und Admins; vom System angelegte nur Admins. */
  canDelete: boolean;
  createdAt: string;
  /** Erledigt-Zeitpunkt (ISO), `null` bei offenen (Story 23.2). */
  closedAt?: string | null;
  version: number;
  /** Fortschritt der Checkliste; `null` = keine Checkliste (Story 23.3). */
  checklist: { done: number; total: number } | null;
  /** Labels nach Name sortiert (Story 23.4). */
  labels: TaskLabel[];
  /** Wiederholung (Story 23.11), nur laufend oder pausiert; `null` = wiederholt sich nicht. */
  recurrence?: TaskRecurrence | null;
  /** Projekt der Aufgabe (Story 23.7); `null` = ohne Projekt. */
  project?: { id: string; name: string } | null;
}

/** Wiederholung einer Aufgabe (Story 23.11), wie Server und Commands sie zurückgeben. */
export interface TaskRecurrence {
  id: string;
  text: string;
  status: TaskRecurrenceStatus;
  rule: RecurrenceRuleInput;
  /** Termine vor `rule.start` aus früheren Regeln; `rule` zeigt „n-mal“ für die ganze Reihe. */
  countOffset: number;
  version: number;
}

/** Termin der Vorschau unter dem Menü: verschobene mit Grund (`reason`). */
export interface TaskRecurrencePreviewItem {
  index: number;
  date: string;
  scheduledDate: string;
  shifted: boolean;
  reason: string | null;
  createDate: string;
}

/** Antwort von `GET /api/v1/tasks/recurrence-preview`. */
export interface TaskRecurrencePreview {
  text: string;
  sentence: string;
  next: TaskRecurrencePreviewItem[];
  leadWorkdays: number;
  leadText: string;
  kernelVersion: string;
}

/** Zeile von `GET /api/v1/tasks/recurrences` (Einstellungen › Aufgaben › Wiederholungen). */
export interface TaskRecurrenceListItem extends TaskRecurrence {
  title: string;
  assigneeId: string | null;
  assigneeName: string | null;
  nextDate: string | null;
  latestTaskId: string | null;
}

export interface TaskRecurrenceList {
  today: string;
  leadWorkdays: number;
  leadVersion: number;
  /** Beispiel zum Vorlauf: Fälligkeit und Entstehungstag je Vorlauf (Index = Arbeitstage). */
  leadExample: { dueDate: string; createDates: string[] };
  items: TaskRecurrenceListItem[];
}


/** Projekt der Aufgaben (Story 23.7, `GET /api/v1/tasks/projects`): Fortschritt beim Lesen gezählt, nie gespeichert. */
export interface TaskProject {
  id: string;
  name: string;
  description: string | null;
  /** Ziel-Datum; `null` = ohne Ziel. */
  targetDate: string | null;
  state: 'active' | 'closed';
  version: number;
  createdAt: string;
  closedAt: string | null;
  /** Erledigte Aufgaben (Status „Erledigt“). */
  done: number;
  /** Alle Aufgaben des Projekts (ohne Papierkorb). */
  total: number;
  overdue: boolean;
  overdueDays: number;
  /** Zuständige der Aufgaben, nach Name. */
  people: { id: string; name: string; initials: string }[];
}

/** Antwort von `GET /api/v1/tasks/projects`: Reihenfolge vom Server (überfällig, Ziel-Datum, Name). */
export interface TaskProjectList {
  today: string;
  items: TaskProject[];
}

/** Label an einer Aufgabe. */
export interface TaskLabel {
  id: string;
  name: string;
  color: TaskLabelColor;
}

/** Eintrag von `GET /api/v1/tasks/labels`. */
export interface TaskLabelEntry extends TaskLabel {
  version: number;
  /** Anzahl Aufgaben mit diesem Label. */
  taskCount: number;
}

/** Vorlage für Aufgaben (Story 23.12), wie die Commands sie zurückgeben. */
export interface TaskTemplate {
  id: string;
  name: string;
  title: string;
  description: string | null;
  priority: TaskPriority;
  checklist: string[];
  dueInWorkdays: number;
  defaultAssigneeId: string | null;
  labels: TaskLabel[];
  archived: boolean;
  version: number;
}

/** Eintrag von `GET /api/v1/tasks/templates`: zusätzlich Fälligkeit bei Anlage heute und Nutzungszähler. */
export interface TaskTemplateEntry extends TaskTemplate {
  dueDate: string;
  defaultAssigneeName: string | null;
  usageCount: number;
}

/** Antwort von `GET /api/v1/tasks/templates`. */
export interface TaskTemplateList {
  today: string;
  items: TaskTemplateEntry[];
}

/** Punkt der Checkliste im Detail-Blatt (Story 23.3). */
export interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
  doneAt: string | null;
  doneByName: string | null;
}

/** Antwort von `GET /api/v1/tasks/calendar` (Story 23.6): offene Wiedervorlagen mit Fälligkeit von `from` bis `to`. */
export interface TaskCalendar {
  today: string;
  from: string;
  to: string;
  /** Schnellwahl „Morgen“ und „Nächster Mo“ (Server-Regel wie `TaskList.postpone`). */
  postpone: { tomorrow: string; nextWeek: string };
  items: TaskItem[];
}

/** Antwort von `GET /api/v1/tasks/timeline` (Story 23.8): offene Aufgaben mit Balken im Zeitraum und ihre Projekte. */
export interface TaskTimeline {
  today: string;
  from: string;
  to: string;
  /** Nur Projekte mit Balken, in der Reihenfolge der Balken. */
  projects: { id: string; name: string; targetDate: string | null; state: 'active' | 'closed'; done: number; total: number }[];
  /** `barStart` = Start, ohne Start der Tag der Anlage; nie nach Fällig. */
  items: (TaskItem & { barStart: string })[];
}

/** Antwort von `GET /api/v1/tasks/workload` (Story 23.9): je aktiver Person offene Aufgaben (nur Gruppe „Wiedervorlage“). */
export interface TaskWorkload {
  today: string;
  /** Sonntag der laufenden Woche. */
  weekEnd: string;
  /** Nächste 4 Wochen: erste = heute bis Sonntag, dann Mo–So. */
  weeks: { from: string; to: string; label: string }[];
  people: {
    assigneeId: string;
    displayName: string;
    initials: string;
    open: number;
    overdue: number;
    thisWeek: number;
    urgentHigh: number;
    weekCounts: number[];
  }[];
}

/** Antwort von `GET /api/v1/tasks/board` (Story 23.2): nur Gruppe „Wiedervorlage“. */
export interface TaskBoard {
  today: string;
  /** Erledigte der letzten N Tage (Einstellung „Board: Erledigt zeigen“). */
  doneDays: number;
  items: TaskItem[];
}

export interface TaskList {
  today: string;
  /** „Morgen“ und „Nächste Woche“ (Arbeitstage Bayern). */
  postpone: { tomorrow: string; nextWeek: string };
  /** Heute erledigte Wiedervorlagen der Ansicht (Kennzahl „Erledigt heute“). */
  doneToday: number;
  /** Davon mit Fälligkeit heute (Ring „Heute“, Ticket 1.294). */
  doneDueToday: number;
  /** Davon Gruppe „Freigaben“ (Kachel „Heute“ benennt sie, Story 12.11). */
  doneDueTodayApprovals?: number;
  doneDueTodayFree?: number;
  items: TaskItem[];
}

/** Stand nach einer Aktion an einer Wiedervorlage (für Rückgängig). */
export interface TaskState {
  id: string;
  status: 'open' | 'done';
  stage: TaskStage;
  priority: TaskPriority;
  title: string;
  description: string | null;
  startDate: string | null;
  dueDate: string;
  assigneeId: string | null;
  version: number;
}

/** Antwort von `POST /api/v1/tasks` (Story 23.1). */
export interface CreatedTask {
  id: string;
  title: string;
  assigneeId: string | null;
  dueDate: string;
  status: 'open' | 'done';
  entityType: string | null;
  entityId: string | null;
  version: number;
}

/**
 * KI-Deutung der Schnell-Erfassung (Story 23.15, `/api/v1/assistant/task-interpretations`): Stand der eigenen Deutung.
 * `proposed` = KI-Vorschlag liegt vor (angelegt wird erst mit `POST /api/v1/ai/suggestions/{id}/accept`).
 */
export interface TaskInterpretation {
  interpretationId: string;
  taskId: string;
  state: 'waiting' | 'proposed' | 'none';
  suggestion: {
    id: string;
    version: number;
    title: string;
    confidence: number;
    reason: string;
    after: TaskInterpretationValues;
  } | null;
}

/** Felder des Vorschlags (auch `values` beim „geändert übernommen“). */
export interface TaskInterpretationValues {
  title: string;
  dueDate: string;
  priority: 'urgent' | 'high' | 'normal' | 'low' | null;
  assigneeId: string;
  labelIds: string[];
  projectId: string | null;
  record: { entityType: 'customer' | 'order' | 'article'; entityId: string } | null;
}

/** Start: Satz ohne erkannte Wörter und was Regel und Chips schon festlegen. */
export interface TaskInterpretationStart {
  sentence: string;
  fixed: {
    dueDate: string | null;
    priority: 'urgent' | 'high' | 'normal' | 'low' | null;
    personId: string | null;
    labelIds: string[];
    projectId: string | null;
    record: { entityType: 'customer' | 'order' | 'article'; entityId: string } | null;
  };
}

/**
 * KI-Entwurf der Beschreibung (Story 23.21, `/api/v1/assistant/task-description-drafts`): Stand des eigenen Entwurfs.
 * `proposed` = KI-Vorschlag liegt vor (gespeichert wird erst mit `POST /api/v1/ai/suggestions/{id}/accept`).
 */
export interface TaskDescriptionDraft {
  draftId: string;
  taskId: string;
  state: 'waiting' | 'proposed' | 'none';
  suggestion: {
    id: string;
    version: number;
    confidence: number;
    reason: string;
    description: string;
  } | null;
}

/** Aufgabe im Detail-Blatt (`GET /api/v1/tasks/{id}`, Story 23.1). */
export interface TaskDetail extends TaskItem {
  description: string | null;
  status: 'open' | 'done';
  /** Automatisch geschlossen (Satz des Moduls): lässt sich nicht wieder öffnen. */
  closeNote: string | null;
  history: { id: string; at: string; authorName: string; text: string }[];
  /** Punkte der Checkliste in ihrer Reihenfolge (Story 23.3). */
  checklistItems: ChecklistItem[];
  /** Aus einer Notiz angelegt (Story 23.20): Band „Aus Notiz von …“; `null` ohne Quelle oder bei gesperrtem Bezug. */
  sourceNote?: TaskSourceNote | null;
}

/** Quelle einer Aufgabe aus einer Notiz (Story 23.20). */
export interface TaskSourceNote {
  entryId: string;
  authorName: string;
  at: string;
  entityType: string;
  entityId: string;
}

/** Antwort von `POST /api/v1/chatter/{entryId}/task` (Story 23.20); `existing` = gab es schon (200). */
export interface CreatedTaskFromNote extends CreatedTask {
  existing: boolean;
}

/** Änderbare Felder einer Aufgabe (`POST /api/v1/tasks/{id}/fields`). */
export interface TaskFieldsPatch {
  title?: string;
  priority?: TaskPriority;
  description?: string | null;
  startDate?: string | null;
  stage?: TaskStage;
  dueDate?: string;
  assigneeId?: string | null;
}

/** Sammelzeile der Karte „Meine Aufgaben“ (Startseite). */
export interface TaskSummaryLine {
  group: 'cases' | TaskGroup;
  label: string;
  detail: string;
  count: number;
  overdue: number;
}

/** Offener Klärfall (Gruppe „Klärfälle“ der Aufgabenseite). */
export interface CaseItem {
  id: string;
  caseKey: string;
  reasonCode: string;
  /** Titel des Grunds aus dem Katalog (Story 2.31). */
  reasonTitle: string;
  title: string;
  /** Grund als ganzer Satz. */
  detail: string;
  area: string | null;
  entityType: string;
  entityId: string;
  entityLabel: string | null;
  occurrences: number;
  openedAt: string;
  lastRaisedAt: string;
  assigneeId: string | null;
  assigneeName: string | null;
  version: number;
}

export interface CaseList {
  items: CaseItem[];
  /** Mindestzahl gleicher Klärfälle für ein Bündel (Einstellung `case_group_min_size`, Story 2.31). */
  groupMinSize: number;
}

/** Ergebnis von „USt-ID erneut prüfen“ je Klärfall (Story 2.36, `orders.recheckVatIdForCase`). */
export interface CaseVatIdRecheck {
  orderId: string;
  mode: 'off' | 'shadow' | 'live';
  /** `preview` = nichts angestoßen (Schalter aus/Probelauf). */
  outcome: 'preview' | 'started' | 'unreachable' | 'not_checkable';
  checkId: string | null;
  reason: string | null;
}

/** Stand der nach „Freigeben“ gestarteten Prüfungen (Story 2.36, nur lesen). */
export interface CaseVatIdRecheckProgress {
  items: {
    checkId: string;
    /** `result` = Ergebnis da; `unreachable` = Prüfdienst nicht erreichbar; sonst Zustand im Ledger. */
    state: 'result' | 'running' | 'unreachable' | 'waiting' | 'not_sent' | 'failed';
    outcome: 'confirmed' | 'mismatch' | 'unconfirmed' | 'invalid' | null;
  }[];
}

/** Antwort von „Bestand erneut an eBay senden“ (Story 2.37): Stand des Betriebsschalters „eBay – schreiben“. */
export interface CaseResendStock {
  id: string;
  articleId: string;
  mode: 'off' | 'shadow' | 'live';
}

/** Ergebnis von „An DHL erneut senden“ je Klärfall (Story 2.35, `shipping.resendLabelForCase`). */
export interface CaseDhlResend {
  orderId: string;
  mode: 'off' | 'shadow' | 'live';
  /** `preview` = nichts eingereiht (Schalter aus/Probelauf); `nothing_to_send` = Grund in `reason`. */
  outcome: 'preview' | 'sent' | 'nothing_to_send';
  /** Vorgänge (Pakete), die gesendet würden bzw. eingereiht wurden. */
  attempts: number;
  reason: string | null;
}

export interface CaseDetail extends CaseItem {
  status: 'open' | 'resolved';
  resolvedAt: string | null;
  resolution: string | null;
}

export interface Assignee {
  id: string;
  displayName: string;
  initials: string;
}

/** Neuer Schlüssel je Speichern (Idempotency-Key). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** Endgültiger Fehlschlag einer Außenwirkung (Einstellungen › Schnittstellen). */
export interface SideEffectFailure {
  title: string;
  /** Grund als deutscher Satz. */
  detail: string;
  reasonCode: string | null;
  operation: string;
  key: string;
  finishedAt: string;
}

/** Zustand einer Gegenstelle. */
export interface Counterpart {
  id: string;
  label: string;
  reachable: boolean;
  unreachableSince: string | null;
  nextProbeAt: string | null;
  lastSuccessAt: string | null;
  /** Offene Vorgänge (wartend oder laufend). */
  waiting: number;
  counts: Record<'queued' | 'running' | 'pending_external' | 'deferred' | 'succeeded' | 'failed' | 'shadowed' | 'skipped', number>;
  quota: { period: string; limit: number; used: number } | null;
  recentFailures: SideEffectFailure[];
  /**
   * Mindest-Meldetakt; `checked: false` = kein Betriebsschalter ruft die Gegenstelle auf, nicht geprüft (Ticket 1.170);
   * `probeOnly` = nur „schreiben: Probe“. `null` = kein Takt.
   */
  heartbeat: { minutes: number; checked: boolean; probeOnly: boolean } | null;
}

export interface LoopbackTestRun {
  id: string;
  mode: 'ok' | 'reject' | 'fail';
  requested: number;
  executed: number;
  duplicates: number;
  startedAt: string;
}

export interface CounterpartOverview {
  counterparts: Counterpart[];
  /** Prüf-Gegenstelle „Verbindungstest“; `null` auf live. */
  loopback: { outage: { active: boolean; since: string | null }; lastRun: LoopbackTestRun | null } | null;
  /** Nachgestellter USt-Prüfdienst mit „Ausfall nachstellen“ (Story 4.3); `null` auf live. */
  evatrSimulation: { outage: { active: boolean; since: string | null } } | null;
  /** Nachgestelltes Shopware der Testumgebung (Story 5.1); `null` auf live. */
  shopwareSimulation: { outage: { active: boolean; since: string | null } } | null;
  /** Nachgestelltes eBay der Testumgebung mit „Testbestellung eBay“ (Story 6.12); `false` auf live. */
  ebaySimulation: boolean;
  /** Admin: darf „Jetzt erneut versuchen“ und den Verbindungstest bedienen. */
  canOperate: boolean;
}

/** Betriebsschalter (AD-7). */
export type SwitchMode = 'off' | 'shadow' | 'live';
export type SwitchDirection = 'read' | 'write';

export interface IntegrationSwitch {
  direction: SwitchDirection;
  mode: SwitchMode;
  version: number;
  changedAt: string | null;
  changedByName: string | null;
}

/** Ein Eintrag im Protokoll „würde senden“. */
export interface ShadowedSideEffect {
  id: string;
  key: string;
  operation: string;
  runMode: 'live' | 'probe';
  /** Nur für Admins (Personendaten), sonst `null`. */
  payload: unknown;
  result: unknown;
  finishedAt: string;
}

export interface CounterpartSwitches {
  id: string;
  label: string;
  switches: IntegrationSwitch[];
  shadowLog: ShadowedSideEffect[];
}

export interface IntegrationSwitchOverview {
  counterparts: CounterpartSwitches[];
  /** Admin: darf umschalten. */
  canChange: boolean;
}

/** Probephase je Baustein (Story 8.1): `mixed` = abweichend (ein Schalter von Hand anders gestellt). */
export type TrialBlockState = 'off' | 'probe' | 'mixed';
export type TrialBlockMode = 'off' | 'probe';

export interface TrialBlockSwitch {
  counterpart: string;
  direction: SwitchDirection;
  label: string;
  mode: SwitchMode;
  version: number;
}

/** Schnittstelle eines Bausteins (Story 8.5): `null` = Richtung gehört nicht zum Baustein. */
export interface TrialBlockInterface {
  counterpart: string;
  label: string;
  internal: boolean;
  read: SwitchMode | null;
  write: SwitchMode | null;
}

export interface TrialBlock {
  id: string;
  no: number;
  label: string;
  state: TrialBlockState;
  switches: TrialBlockSwitch[];
  interfaces: TrialBlockInterface[];
  missingCredentials: { counterpart: string; label: string }[];
}

export interface TrialBlockOverview {
  blocks: TrialBlock[];
  canChange: boolean;
}

/** Vergleichsbericht der Probephase (Story 8.7): Zahlen je Baustein an einem Tag. */
export interface ComparisonBlockCount {
  block: string;
  no: number;
  label: string;
  unexplained: number;
  expected: number;
  failedSources: number;
}

export interface ComparisonDaySummary {
  reportId: string;
  date: string;
  unexplained: number;
  expected: number;
  comparedSources: number;
  failedSources: number;
  blocks: ComparisonBlockCount[];
}

export interface ComparisonOverview {
  daysWithoutUnexplained: number;
  days: ComparisonDaySummary[];
  canEditRules: boolean;
}

export type ComparisonDifferenceStatus = 'expected' | 'unexplained';

export interface ComparisonSourceView {
  sourceId: string;
  block: string;
  blockNo: number;
  blockLabel: string;
  lineKind: string;
  label: string;
  keyLabel: string;
  status: 'compared' | 'failed';
  unexplained: number;
  expected: number;
  error: string | null;
}

export interface ComparisonDifference {
  id: string;
  sourceId: string;
  block: string;
  lineKind: string;
  key: string;
  field: string;
  fieldLabel: string;
  erpValue: string | null;
  xentralValue: string | null;
  status: ComparisonDifferenceStatus;
  ruleId: string | null;
  ruleVersion: number | null;
  reason: string | null;
}

export interface ComparisonDay {
  reportId: string;
  date: string;
  runAt: string;
  unexplained: number;
  expected: number;
  sources: ComparisonSourceView[];
  differences: ComparisonDifference[];
  truncated: boolean;
}

export interface ComparisonRule {
  id: string;
  version: number;
  active: boolean;
  block: string;
  blockNo: number;
  blockLabel: string;
  lineKind: string;
  lineKindLabel: string;
  field: string | null;
  fieldLabel: string | null;
  erpPrefix: string | null;
  xentralPrefix: string | null;
  reason: string;
  createdAt: string;
  updatedAt: string;
  changedBy: string | null;
}

export interface ComparisonRules {
  rules: ComparisonRule[];
  canEditRules: boolean;
}

/** Hochgeladene Xentral-Rechnungsübersicht eines fachlichen Tages (Story 12.13). */
export interface XentralInvoiceOverview {
  id: string;
  businessDay: string;
  rowCount: number;
  version: number;
  uploadedAt: string;
}

export interface XentralInvoiceOverviews {
  uploads: XentralInvoiceOverview[];
}

/** Einstellungen › Start & Umschalttag › Umschalttag (Story 20.1): Schritt mit Stand im letzten Lauf. */
export type CutoverStepStatus = 'open' | 'planned' | 'running' | 'done' | 'failed';

export interface CutoverOverview {
  run: { id: string; kind: 'trial'; status: 'requested' | 'running' | 'done' | 'failed'; requestedAt: string; startedAt: string | null; finishedAt: string | null } | null;
  steps: { name: string; label: string; status: CutoverStepStatus; startedAt: string | null; finishedAt: string | null; summary: string | null; detail: string | null }[];
  /** `manual` = von Hand abzuhaken (Admin, Story 20.4); dann Name und Zeitpunkt der Bestätigung. */
  checklist: { id: string; label: string; status: 'open' | 'done'; manual: boolean; confirmedBy: string | null; confirmedAt: string | null }[];
  /** Prüfbericht der Nummernkreis-Startwerte (Story 20.4, R-27). */
  numberRanges: CutoverNumberRangeReport;
  probeClosedAt: string | null;
  running: boolean;
  cutoverReady: boolean;
  canStart: boolean;
}

/** Stand eines Kreises: fehlt, passt nicht (Grund in `message`), geprüft, schon übernommen. */
export type CutoverNumberRangeStatus = 'missing' | 'error' | 'ready' | 'applied';

export interface CutoverNumberRange {
  rangeKey: 'invoice' | 'credit_note' | 'proforma' | 'delivery_note' | 'sales_order' | 'purchase_order';
  label: string;
  /** Beispiel im Format des Kreises (Zähler 1, heutiges Jahr). */
  example: string | null;
  lastNumber: string | null;
  /** Version der Eingabe für `expectedVersion`; `null` = nie eingetragen. */
  version: number | null;
  status: CutoverNumberRangeStatus;
  /** Erste ERP-Nummer nach der Übernahme. */
  nextNumber: string | null;
  message: string | null;
  code: string | null;
}

export interface CutoverNumberRangeReport {
  rows: CutoverNumberRange[];
  checked: number;
  total: number;
  ok: boolean;
}

/** Einstellungen › Betrieb (Story 1.11a): Zustand einer Alarmart. */
export interface OpsAlarm {
  code: 'outbox_backlog' | 'workflow_errors' | 'tls_expiry' | 'disk_space' | 'backup_dump' | 'archive_copy' | 'metrics_stale' | 'day_close_missing' | 'day_close_labels_pending';
  title: string;
  status: 'ok' | 'raised' | 'not_configured';
  /** Probealarm von Hand. */
  probe: boolean;
  since: string;
  detail: string | null;
  checkedAt: string | null;
  runbookUrl: string;
}

/** Lauf von Sicherung oder Zweitkopie (Story 1.12). */
export interface OpsRun {
  ok: boolean;
  at: string;
  detail: string | null;
}

/** Archiv-Stand (Story 1.12). */
export interface OpsArchive {
  objects: number;
  lastStoredAt: string | null;
  lastCopiedAt: string | null;
  uncopied: number;
  oldestUncopiedAt: string | null;
  lastCopyRun: OpsRun | null;
}

/** Schutzprobe des Archivs (Story 1.12). */
export interface OpsArchiveProbe {
  status: 'requested' | 'passed' | 'failed' | 'not_configured';
  requestedAt: string;
  finishedAt: string | null;
  detail: string | null;
}

export interface OpsOverview {
  worker: { lastBeatAt: string | null; alive: boolean; maxSeconds: number };
  alarms: OpsAlarm[];
  archive: OpsArchive;
  backup: { lastRun: OpsRun | null };
  archiveProbe: OpsArchiveProbe | null;
  downRunbookUrl: string;
  /** Admin: darf Probealarm und Schutzprobe auslösen. */
  canProbe: boolean;
}

/** Abgleich-Schritt einer Wiederherstellung (Story 1.13). */
export interface OpsRestoreStep {
  name: string;
  label: string;
  status: 'running' | 'done' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  checked: number | null;
  settled: number | null;
  discrepancies: number | null;
  detail: string | null;
}

export interface OpsRestoreSwitch {
  counterpart: string;
  label: string;
  direction: SwitchDirection;
  before: SwitchMode;
  now: SwitchMode;
  restorable: boolean;
}

/** Nach einer Wiederherstellung gesperrter Vorgang (bis zur Entscheidung des Admins). */
export interface OpsRestoreHold {
  sideEffectId: string;
  restoreId: string;
  counterpartLabel: string;
  operation: string;
  key: string;
  reason: 'no_evidence' | 'check_failed' | 'read_off' | 'read_probe';
  reasonText: string;
  decision: 'executed' | 'send' | 'cancel' | null;
  version: number;
}

export interface OpsRestoreRun {
  id: string;
  /** Für „Wieder live schalten“ (gelesener Stand). */
  version: number;
  restoredTo: string;
  kind: 'dump' | 'pitr';
  reportedAt: string;
  status: 'reconciling' | 'reconciled' | 'released';
  holding: boolean;
  failed: boolean;
  reconcileStartedAt: string | null;
  reconcileFinishedAt: string | null;
  durationSeconds: number | null;
  steps: OpsRestoreStep[];
  discrepancies: number;
  switches: OpsRestoreSwitch[];
  holds: OpsRestoreHold[];
  releasedAt: string | null;
  releasedBy: string | null;
}

/** Bericht der jüngsten Wiederherstellung (Einstellungen › Betrieb, Band oben). */
export interface OpsRestore {
  restore: OpsRestoreRun | null;
  /** Admin: darf „Wieder live schalten“. */
  canResume: boolean;
  /** Handbuch „Wiederherstellung“. */
  runbookUrl: string;
}

/** Kunde in Liste und Ansicht (Kundenstamm). */
export interface CustomerView {
  id: string;
  number: string;
  name: string;
  salutation: CustomerSalutation | null;
  title: string | null;
  firstName: string | null;
  lastName: string | null;
  kind: CustomerKind;
  language: DocumentLanguage;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  /** Telefon im ERP gepflegt ([RUDOLF] 29.10.2026); `null` = es gilt das Telefon der Standard-Rechnungsadresse aus Shopware. */
  phone: string | null;
  countryCode: string;
  /** USt-ID (normalisiert); `null` = keine. */
  vatId: string | null;
  /** Story 9.37: Lieferbedingung des Kunden (Kürzel Incoterms 2020); `null` = Standard aus den Einstellungen. */
  deliveryTerms: Incoterm | null;
  version: number;
  createdAt: string;
  /** Kunde seit (fachliches Datum, Story 4.23); `null` = unbekannt. */
  customerSince: string | null;
}

/** Prüfung der USt-ID beim BZSt (Story 4.2): Auftrag, Zustand, Nachweis. */
export interface VatIdCheckView {
  id: string;
  vatId: string;
  requestedAt: string;
  requestedByName: string;
  /** Prüfung eines zusammengeführten Kunden (Story 4.11): dessen Kundennummer; sonst `null`. */
  fromCustomerNumber: string | null;
  state: VatIdCheckState;
  stateText: string;
  request: { requesterVatId: string; name: string; street: string | null; postalCode: string | null; city: string | null };
  result: {
    outcome: VatIdCheckOutcome;
    statusCode: string;
    nameResult: FieldResult | null;
    streetResult: FieldResult | null;
    postalCodeResult: FieldResult | null;
    cityResult: FieldResult | null;
    validFrom: string | null;
    validUntil: string | null;
    serviceReference: string | null;
    serviceAt: string | null;
    checkedAt: string;
    probe: boolean;
  } | null;
}

/** Konditionen am Kunden (Story 4.8): eigene Werte und geltende Zahlungsbedingung (eigene oder Standard). */
export interface CustomerTermsView {
  termsMaintained: boolean;
  /** Eigenes Zahlungsziel; `null` = keins bzw. nicht gepflegt. */
  paymentTermDays: number | null;
  /** Skonto: Prozent als Dezimaltext („2.00“), Tage. */
  cashDiscount: { percent: string; days: number } | null;
  /** Kreditlimit in der kleinsten Einheit als exakte Ganzzahl-Zeichenfolge; `null` = keins. */
  creditLimit: { amountMinor: string; currency: string } | null;
  effective: {
    source: TermsSource;
    paymentTermDays: number | null;
    cashDiscount: { percent: string; days: number } | null;
    /** z. B. „30 Tage netto, 2 % Skonto in 10 Tagen“. */
    text: string;
    /** „Konditionen nicht gepflegt – es gilt das Standard-Zahlungsziel 14 Tage“ oder `null`. */
    hint: string | null;
  };
  /** Währung des Kreditlimits (vorhandenes) bzw. für ein neues (Einstellung); `null` = Einstellung ungültig. */
  currency: string | null;
  /** Satz, wenn die Einstellung der Währung ungültig ist. */
  currencyProblem: string | null;
  /** Längstes erlaubtes Zahlungsziel in Tagen (Einstellung). */
  maxDays: number;
}

/** E-Mail-Adressen am Kunden (Story 4.9): Hauptadresse und je Zweck die eigene bzw. geltende Adresse. */
export interface CustomerEmailsView {
  /** Hauptadresse; `null` = keine. */
  email: string | null;
  /** Alle Zwecke in fester Reihenfolge. */
  purposes: {
    purpose: EmailPurpose;
    label: string;
    /** Eigene Adresse des Zwecks; `null` = es gilt die Hauptadresse. */
    email: string | null;
    /** Geltende Adresse (eigene oder Hauptadresse); `null` = keine. */
    recipient: string | null;
    source: 'purpose' | 'main' | null;
  }[];
}

/** E-Rechnungsangaben am Kunden (Story 4.9); `format: null` = nur PDF. */
export interface CustomerEInvoiceView {
  format: EInvoiceFormat | null;
  buyerReference: string | null;
  /** „Käuferreferenz fehlt“ bei XRechnung ohne Käuferreferenz (R-06); sonst `null`. */
  problem: string | null;
}

/** Bekannte Bankverbindung oder PayPal-Adresse am Kunden (Story 4.14). */
export interface PaymentIdentityView {
  id: string;
  kind: PaymentIdentityKind;
  kindLabel: string;
  value: string;
  /** IBAN in Vierergruppen, PayPal-Adresse wie gespeichert. */
  display: string;
  origin: PaymentIdentityOrigin;
  originLabel: string;
  reference: string | null;
  createdAt: string;
  createdByName: string;
  /** Andere Kunden (Kundennummern) mit derselben Kennung. */
  alsoAt: string[];
  alsoAtText: string | null;
  /** Ganzer Hinweis „Diese IBAN ist auch bei 20116 / 20109 hinterlegt – …“ (vom Server); `null`, wenn nur hier. */
  sharedText: string | null;
}

/** Werbe-Sperre am Kunden (Story 2.29): an diese Adresse gehen keine Werbe-Mails. */
export interface AdvertisingBlockView {
  id: string;
  address: string;
  blockedAt: string;
  blockedOn: string;
  source: AdvertisingBlockSource;
  sourceLabel: string;
  /** „Werbe-Mails: abgemeldet am 12.02.2027 (Link)“. */
  text: string;
  createdByName: string;
}

export interface CustomerDetail extends CustomerView {
  terms: CustomerTermsView;
  emails: CustomerEmailsView;
  eInvoice: CustomerEInvoiceView;
  /** Bekannte Bankverbindungen und PayPal-Adressen (älteste zuerst). */
  paymentIdentities: PaymentIdentityView[];
  /** Werbe-Sperren je Adresse (Story 2.29); leer = Werbe-Mails erlaubt. */
  advertisingBlocks: AdvertisingBlockView[];
  /** Liefersperre mit Grund. */
  deliveryBlock: { blocked: boolean; reason: string | null };
  dunningBlock: boolean;
  /** Kunden-Tags im ERP (Story 17.20); leer = keine (fehlt bei älteren Servern und Attrappen). */
  tags?: string[];
  /** Notiz am Kunden (Story 4.21): Text (`null` = keine) und Höchstlänge in Zeichen aus der Einstellung. */
  note: { text: string | null; maxLength: number };
  /** Lässt sich die gespeicherte USt-ID jetzt prüfen? */
  vatIdCheckable: boolean;
  /** Warum nicht prüfbar (Satz), sonst `null`. */
  vatIdHint: string | null;
  /** Prüfungen, neueste zuerst. */
  vatIdChecks: VatIdCheckView[];
  /** Stand der gespeicherten USt-ID (Story 4.3, A-13), z. B. „Prüfdienst nicht erreichbar … · zuletzt bestätigt am …“. */
  vatIdStatus: { state: VatIdStatusState; lastConfirmedAt: string | null; lastConfirmedOn: string | null; text: string };
  /** Adressbuch aus Shopware (Story 4.18, nur lesend): Standard-Rechnung, Standard-Lieferung, dann die übrigen. */
  addresses: CustomerAddressView[];
  /** Primärprofil aus Shopware (Story 4.18, nur lesend); `null` = noch nicht gelesen. */
  shop: CustomerShopView | null;
  /** Separat erhaltene Shopkonten, jeweils mit eigenem Lesestand (Story 4.11). */
  shopAccounts: CustomerShopAccountView[];
  /** Letzte Änderung laut Protokoll (wer, wann). */
  lastChange: { at: string; actorName: string } | null;
  /** Mögliche Dublette von (Story 4.10/4.11, über „zusammengeführt mit …“ aufgelöst); `null` = keine. */
  possibleDuplicateOf: { id: string; number: string; name: string } | null;
  /** Dieser Kunde wurde zusammengeführt (Story 4.11): bleibender Kunde, wann, von wem; `null` = nicht zusammengeführt. */
  mergedInto: { id: string; number: string; at: string; byName: string } | null;
  /** Mit diesem Kunden zusammengeführte Kunden, ältestes zuerst. */
  mergedFrom: { id: string; number: string; name: string; at: string; byName: string }[];
}

/** Ein Kunde in der Vorschau „Kunden zusammenführen“ (Story 4.11). */
export interface CustomerMergePreviewCustomer {
  id: string;
  number: string;
  name: string;
  address: string;
  country: string;
  vatId: string | null;
  email: string | null;
  version: number;
}

/** Vorschau „Kunden zusammenführen“ (Story 4.11). */
export interface CustomerMergePreview {
  keep: CustomerMergePreviewCustomer;
  merge: CustomerMergePreviewCustomer;
  moved: { key: string; label: string; items: string[] }[];
  stays: string[];
  differences: { field: string; label: string; keep: string; merge: string }[];
  blockers: string[];
  buttonLabel: string;
  /** Beim Zusammenführen mitschicken (409, wenn sich seit der Vorschau etwas geändert hat). */
  previewToken: string;
}

/** Adresse aus dem Shopware-Adressbuch (Story 4.18). */
export interface CustomerAddressView {
  /** Stabile gespeicherte ID, unabhängig von Shop-Adress-IDs anderer Konten. */
  id: string;
  /** Herkunftskonto; `null` bei nicht eindeutig zugeordnetem Altstand. */
  sourceChannelLinkId: string | null;
  addressId: string;
  defaultBilling: boolean;
  defaultShipping: boolean;
  company: string | null;
  department: string | null;
  firstName: string | null;
  lastName: string | null;
  street: string | null;
  additionalLine1: string | null;
  additionalLine2: string | null;
  postalCode: string | null;
  city: string | null;
  countryCode: string | null;
  stateCode: string | null;
  stateName: string | null;
  phone: string | null;
  /** Story 4.25: `shopware` = aus Shopware gelesen (dort gepflegt), `erp` = im ERP angelegt. */
  origin: 'shopware' | 'erp';
  /** Übertragung einer ERP-Adresse nach Shopware; `null` bei Shopware-Adressen. */
  transferState: 'local' | 'pending' | 'transferred' | 'failed' | null;
  /** Grund des Fehlschlags (deutscher Satz). */
  transferError: string | null;
  /** Hinweis, z. B. „Wird übertragen, sobald …“ bei Schalter aus oder Probe. */
  transferHint: string | null;
}

/** Zusatzfeld eines Objekts, das Shopware führt (Kategorie, Kunde, Adresse) – nur lesend (Story 5.21). */
export interface MirrorCustomFieldView {
  path: string;
  key: string;
  label: string;
  setLabel: string | null;
  kind: string;
  lead: string;
  /** „Shopware führt“, „nur lesen“ bzw. „in Shopware nicht mehr vorhanden (verwaist)“. */
  hint: string;
  value: unknown;
  /** Anzeigetext (`–` ohne Wert). */
  display: string;
}

/** Karte „Zusatzfelder“ eines Kunden (Story 5.21, nur lesend). */
export interface CustomerCustomFieldsView {
  customerId: string;
  readAt: string | null;
  probe: boolean;
  fields: MirrorCustomFieldView[];
  addresses: { addressId: string; label: string; defaultBilling: boolean; defaultShipping: boolean; fields: MirrorCustomFieldView[] }[];
}

/** Shop-Merkmale und Shop-Historie aus Shopware (Story 4.18); Umsatz als exakte Ganzzahl-Zeichenfolge. */
export interface CustomerShopView {
  shopCustomerNumber: string;
  customerGroup: string | null;
  salesChannel: string | null;
  tags: string[];
  guest: boolean;
  active: boolean;
  lastLoginAt: string | null;
  rawSalutation: string | null;
  rawTitle: string | null;
  campaignCode: string | null;
  history: { orderCount: number; orderTotal: { amountMinor: string; currency: string } | null; lastOrderDate: string | null };
  readAt: string;
  /** `probe` = im Probebetrieb gelesen (gilt auch für die Adressen). */
  runMode: 'live' | 'probe';
}

/** Eigenes Shopkonto: Merkmale und `readAt` gehören zu dieser Herkunft, nicht zum Primärprofil. */
export interface CustomerShopAccountView extends CustomerShopView {
  id: string;
  sourceChannelLinkId: string | null;
  isPrimary: boolean;
}

export interface CustomerPage {
  items: CustomerView[];
  nextCursor: string | null;
}

/** Artikel (Story 5.1). Gewicht und Preis als Dezimaltext, nie Gleitkomma. */
export interface ArticleView {
  id: string;
  number: string;
  name: string;
  ean: string | null;
  /** kg, drei Nachkommastellen, z. B. „0.080“. */
  weightKg: string | null;
  /** Netto, vier Nachkommastellen, z. B. „38.9000“. */
  salesPriceNet: string | null;
  currency: string;
  binCode: string | null;
  version: number;
  createdAt: string;
  /** Story 5.98: ausgeblendet seit (ISO); `null` = sichtbar. */
  archivedAt: string | null;
}

/** Story 5.98: Antwort von `GET /api/v1/articles/{id}/removal` (Vorschau „Löschen …“, ändert nichts). */
/** Story 5.100: Verweis auf einen betroffenen Beleg (`goods_receipt` zeigt auf die Bestellung; Entwurf ohne Nummer). */
export interface ArticleRemovalDocumentLink {
  kind: 'order' | 'blanket_order' | 'quote' | 'quote_draft' | 'purchase_order' | 'goods_receipt' | 'return' | 'invoice';
  id: string;
  number: string | null;
  /** `YYYY-MM-DD` (Europe/Berlin). */
  date: string | null;
}

export interface ArticleRemovalPreview {
  articleId: string;
  number: string;
  version: number;
  kind: 'main' | 'variant' | 'single';
  archived: boolean;
  /** `delete` endgültig · `archive` in Shopware löschen und im ERP ausblenden · `blocked` gesperrt · `restore` schon ausgeblendet. */
  action: 'delete' | 'archive' | 'blocked' | 'restore';
  /** Stand in Shopware: gelöscht, Löschen zur aktuellen Version beantragt bzw. nichts. */
  shopware: 'removed' | 'requested' | 'none';
  sentence: string;
  reasons: string[];
  documents: number;
  /** Story 5.100: nur bei `archive` – die neuesten Belege (höchstens 10), neueste zuerst. */
  documentLinks: ArticleRemovalDocumentLink[];
  /** Übrige Belege (nicht als Link). */
  moreDocuments: number;
  /** Anzahl je Belegart (nur bei `archive`); Summe = `documents`. */
  documentKinds: { kind: ArticleRemovalDocumentLink['kind']; count: number }[];
  /** Hinweis zu Shopware (nicht live bzw. schon gelöscht; beim Wiederherstellen, was in Shopware geschieht). */
  shopwareNotice: string | null;
  ruleVersion: number;
}

export type ArticleSyncState = 'none' | 'queued' | 'waiting' | 'sent' | 'shadowed' | 'off' | 'failed' | 'unconfirmed';

export type ArticleStockSyncState = 'none' | 'queued' | 'waiting' | 'sent' | 'shadowed' | 'off' | 'failed';

export interface ArticleDetail extends ArticleView {
  lastChange: { at: string; actorName: string } | null;
  /** Zustand der letzten Übertragung an Shopware. */
  shopwareSync: { state: ArticleSyncState; text: string; detail: string | null; at: string | null };
  /** Zustand der letzten Übertragung des verfügbaren Bestands an Shopware (Story 5.6). */
  shopwareStockSync: { state: ArticleStockSyncState; text: string; detail: string | null; at: string | null; sentStock: number | null };
  /** Nur Testumgebung: wie das nachgestellte Shopware den Artikel hat (`stock` = vom ERP gesendeter Bestand). */
  simulatedShopware: { name: string; weightKg: string | null; ean: string | null; stock: number | null; writeCount: number; updatedAt: string } | null;
  /** Hauptbild aus Shopware (Story 5.24); `null` = kein Bild oder noch keine Kopie → Platzhalter. */
  image: { version: string; source: 'own' | 'parent' } | null;
  /** Zuordnung zum eBay-Angebot und Stand der Bestandsübertragung an eBay (Story 5.7). */
  ebayStock: {
    state: ArticleEbayStockState;
    text: string;
    detail: string | null;
    at: string | null;
    sentQuantity: number | null;
    listing: { itemId: string; sku: string | null; version: number } | null;
  };
  /** Nur Testumgebung: Menge im nachgestellten eBay (`null` = nie gesendet, 0 = pausiert). */
  simulatedEbay: { title: string; quantity: number | null; writeCount: number; updatedAt: string } | null;
  /** Story 5.8: Formularfelder, Hauptartikel/Varianten und Vererbung. */
  form: ArticleForm;
  /** Story 5.8 Teil B: Neuanlage in Shopware (nur im ERP angelegte Artikel). */
  shopwareCreate: { state: 'none' | 'queued' | 'waiting' | 'created' | 'shadowed' | 'off' | 'failed'; text: string; detail: string | null; at: string | null } | null;
  /** Story 5.22: Grundpreis (PAngV); `null` = Einheit, Inhalt, Grundeinheit oder Bruttopreis fehlt. */
  basePrice: { price: string; text: string; version: number } | null;
  /** Story 5.22: Texte für Shopware (Anlage-Stand bzw. Spiegel aus Shopware), nur lesend. */
  shopTexts: ArticleShopTexts;
  /** Story 5.22: Startwerte einer Neuanlage in Shopware. */
  createDefaults: ArticleCreateDefaults;
  /** Story 25.3: Herkunft der EAN laut Register – `pool` „aus Pool“, `foreign` „Hersteller-EAN“; `null` = keine/unbekannt (ältere Antworten: fehlt). */
  eanOrigin?: 'pool' | 'foreign' | null;
  eanBatch?: { id: string; source: string; purchasedOn: string | null } | null;
  /** Story 25.3: Schalter „EAN aus Pool“ des Artikels (ältere Antworten: fehlt). */
  gtinFromPool?: boolean;
}

/** Story 25.3: aus dem Pool vergebene Nummern einer Neuanlage und ob der Pool leer war (Antwort `POST /api/v1/articles`). */
export interface GtinPoolResultView {
  taken: { articleId: string; number: string; ean: string }[];
  empty: boolean;
}

/** Antwort von `POST /api/v1/articles` (Story 25.3: mit `gtinPool`). */
export type ArticleCreatedView = ArticleDetail & { gtinPool?: GtinPoolResultView };

/** Merkmal einer Variante, z. B. Durchmesser = 8 mm (Story 5.8). */
export interface ArticleOptionView {
  group: string;
  value: string;
}

/** Eigene, gespeicherte Werte des Artikelformulars (bei Varianten heißt `null` „erbt“); beim Speichern als `from`. */
export interface ArticleFormValues {
  ean: string | null;
  weightKg: string | null;
  lengthMm: string | null;
  widthMm: string | null;
  heightMm: string | null;
  manufacturerId: string | null;
  customsTariffNumber: string | null;
  originCountry: string | null;
  active: boolean | null;
  binCode: string | null;
  minimumStock: string | null;
  // Story 5.22: weitere Standardfelder (Kaufmengen und Tage als ganze Zahl, Inhalt/Grundeinheit als Dezimaltext „5.0000“).
  manufacturerNumber: string | null;
  isCloseout: boolean | null;
  minPurchase: number | null;
  maxPurchase: number | null;
  purchaseSteps: number | null;
  /** Kürzel aus Einstellungen › Artikel › Lieferzeiten. */
  deliveryTime: string | null;
  restockDays: number | null;
  /** Kürzel aus Einstellungen › Artikel › Einheiten. */
  unit: string | null;
  purchaseUnit: string | null;
  referenceUnit: string | null;
  packUnit: string | null;
  packUnitPlural: string | null;
  /** Story 10.16: Auslaufartikel – nicht nachbestellen (eigener Wert, nicht vererbt). */
  discontinued: boolean;
  // Story 5.84: Nebenkosten für den Einstandspreis (Dezimaltext „2.7000“; bei Varianten `null` = erbt; Sonstiges nur % oder €).
  freightRatePerKg: string | null;
  customsRatePercent: string | null;
  otherCostPercent: string | null;
  otherCostAmount: string | null;
}

/** Story 5.22: Auswahl im Artikelformular (`GET /api/v1/articles/form-options`). */
export interface ArticleFormOptions {
  units: { key: string; label: string; symbol: string }[];
  deliveryTimes: { key: string; label: string }[];
  createDefaults: ArticleCreateDefaults;
  /** Story 25.3: freie Nummern im Pool (nur Anzeige) und Vorgabe des Schalters „EAN aus Pool“ (ältere Antworten: fehlt). */
  gtinPool?: { free: number; fromPoolDefault: boolean };
  /** Gibt es gerade einen KI-Vorschlag für die Texte? */
  aiAvailable: boolean;
  /** Story 5.34: ERP-geführtes Zusatzfeld für die Kernmerkmale mit Grenzen aus den Einstellungen (Anzahl, Länge mit „; “ verbunden); `null` = ausgeblendet. */
  featuresField: { path: string; label: string; maxCount: number; maxLength: number } | null;
  /** Story 5.103: Führung des Titels, erlaubte HTML-Tags und Höchstlängen der Texte (ältere Antworten: fehlt). */
  shopTexts?: {
    nameLedByErp: boolean;
    /** Story 5.121: Beschreibung führt das ERP (sonst keine KI-Entwürfe für fehlende Beschreibungen). Fehlt = unbekannt. */
    descriptionLedByErp?: boolean;
    htmlTags: string[];
    limits: { nameEn: number; description: number; metaTitle: number; metaDescription: number; keywordsCount: number; keywordLength: number; metaTitleRecommended: number; metaDescriptionRecommended: number };
    /** Story 5.120: Host des Shops für die Suchergebnis-Vorschau; `null`/fehlt = unbekannt („Ihr Shop“). */
    previewHost?: string | null;
  };
}

/** Story 5.105/5.107: Felder des Artikel-Assistenten (wie `assistantFieldKeys` in `@till-erp/prompts`). */
export const ASSISTANT_FIELD_KEYS = ['title', 'titleEn', 'description', 'features', 'seo', 'keywords', 'properties', 'category', 'manufacturer', 'mediaTexts', 'customs'] as const;
export type AssistantFieldKey = (typeof ASSISTANT_FIELD_KEYS)[number];

/** Quelle einer Aussage im Entwurf (`notes:<n>`, `file:<n>`, `page`, `field:<key>`). */
export interface AssistantSource {
  ref: string;
  page: number | null;
  quote: string | null;
}

/** Rote Aussage des Faktenchecks (`path` = `<feld>.<n>`). */
export interface AssistantFactCheckItem {
  path: string;
  uncovered: string[];
  badSources: string[];
  noSource: boolean;
}

/** Entwurf je Feld (`GET /api/v1/articles/assistant-runs/{id}` › `fields`). */
export interface AssistantField {
  origin: 'ai' | 'manual';
  value: unknown;
  proposal: unknown;
  factCheck: { red: boolean; items: unknown[] };
  red: boolean;
  blocked: boolean;
  prefill: boolean;
  runId: string;
  at: string;
}

export interface AssistantRead {
  index: number;
  kind: 'pdf' | 'image' | 'page';
  name: string;
  status: string;
  hint: string | null;
  facts: number;
  ignored: boolean;
}

/** Stand des Artikel-Assistenten (Story 5.105); nur Entwurf, nie ein Artikelwert (AD-16). */
export interface AssistantDraft {
  eanSuggestions?: { ean: string; source: string }[];
  draftId: string;
  articleId: string | null;
  status: 'open' | 'closed';
  state: 'reading' | 'planning' | 'done' | 'failed' | 'shadow' | 'idle' | 'closed';
  expiresAt: string;
  reads: AssistantRead[];
  fields: Partial<Record<AssistantFieldKey, AssistantField>>;
  hints: string[];
  lastRun: { fields: string[]; status: string; detail: string | null } | null;
  notice: string;
  version: number;
  /** Story 5.108: Vorher-Werte des Artikels („Mit KI verbessern“, nur offen); unverändert als `expected` zurückschicken. */
  current?: AssistantCurrent | null;
}

/** Story 5.108: Vorher-Werte eines Artikels genau so, wie das Änderungspaket sie liest. */
export interface AssistantCurrent {
  title: string | null;
  titleEn: string | null;
  description: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  /** Suchbegriffe als ein Text („a, b“). */
  keywords: string | null;
  /** Kernmerkmale wie gespeichert („a; b“). */
  features: string | null;
  featuresAvailable: boolean;
  properties: { groupId: string; optionId: string }[];
  categoryIds: string[];
  categoriesLocked: boolean;
  manufacturerId: string | null;
  customsTariffNumber: string | null;
  /** `articleMediaId` bestimmt beim Übernehmen das Bild; `index` (ab 1, Galerie) nur Name und Zuordnung zum Entwurf. */
  mediaTexts: { index: number; articleMediaId: string; alt: string | null; title: string | null }[];
}

/** Was eine Neuanlage in Shopware mitbekommt (Steuerklasse mit Satz aus den Einstellungen, nie eine verkürzte Regel). */
export interface ArticleCreateDefaults {
  taxClass: { key: string; label: string; rate: number } | null;
  productType: 'physical' | 'digital' | null;
  salesChannels: number;
}

/** Story 5.22: Stand des KI-Text-Vorschlags beim Anlegen (`GET /api/v1/articles/shop-text-checks/{id}`). */
export interface ArticleShopTextCheck {
  checkId: string;
  state: 'waiting' | 'proposed' | 'none';
  proposal: ArticleShopTextProposal | null;
}

/** Story 5.34: Quelle einer Aussage (`field:<key>` oder `datasheet` mit Seite und Zitat). */
export interface ArticleShopTextSource {
  ref: string;
  page: number | null;
  quote: string | null;
}

export interface ArticleShopTextStatement {
  text: string;
  sources: ArticleShopTextSource[];
}

/** Story 5.34: Vorschlag „Produkttexte“ mit Quellen je Aussage und Faktencheck des Servers. */
export interface ArticleShopTextProposal {
  v: 2;
  features: ArticleShopTextStatement[];
  sentences: (ArticleShopTextStatement & { paragraph: 1 | 2 | 3 })[];
  metaTitle: ArticleShopTextStatement;
  metaDescription: ArticleShopTextStatement;
  description: string;
  reason: string;
  facts: { ref: string; label: string; page: number | null; text: string }[];
  factCheck: { version: number; red: boolean; items: { path: string; uncovered: string[]; badSources: string[]; noSource: boolean }[] };
}

/** Story 5.103: offene Änderung aus Shopware an einem Text (Klärfall „In Shopware geändert“). */
export interface ArticleShopTextConflict {
  /** Schlüssel (`name`, `keywords`, `description`, `metaTitle`, `metaDescription`). */
  key: string;
  language: string;
  label: string;
  /** Zuletzt bestätigter Wert (vorher), Wert jetzt in Shopware (Rohtext) und Wert im ERP. */
  before: string | null;
  shopware: string | null;
  erp: string | null;
  at: string;
}

/** Story 5.22/5.103: Texte für Shopware an der Artikelansicht (bearbeitbar, sobald das ERP sie führt). */
export interface ArticleShopTexts {
  /** `true` = mindestens ein Text wird (noch) nicht vom ERP geführt. */
  ledByShopware: boolean;
  created: { description: string | null; metaTitle: string | null; metaDescription: string | null };
  shopware: {
    /** Rohtext aus Shopware – nie als HTML anzeigen. */
    description: string | null;
    metaTitle: string | null;
    metaDescription: string | null;
    englishName: string | null;
    keywords?: string | null;
    shippingFree: boolean | null;
    readAt: string;
  } | null;
  /** Story 5.103 (ältere Antworten ohne diese Felder: nur lesend). */
  erp?: { name: string; nameEn: string | null; keywords: string[]; description: string | null; metaTitle: string | null; metaDescription: string | null };
  editable?: { name: boolean; nameEn: boolean; keywords: boolean; shopDescription: boolean; shopMetaTitle: boolean; shopMetaDescription: boolean };
  htmlTags?: string[];
  limits?: { nameEn: number; description: number; metaTitle: number; metaDescription: number; keywordsCount: number; keywordLength: number; metaTitleRecommended: number; metaDescriptionRecommended: number };
  conflicts?: ArticleShopTextConflict[];
}

export interface ArticleForm {
  kind: 'main' | 'variant' | 'single';
  origin: 'erp' | 'shopware';
  parent: { id: string; number: string; name: string } | null;
  options: ArticleOptionView[];
  groups: string[];
  /** Story 5.92: `active` eigener Wert („aktiv“), `null` = erbt; `version` für das sofortige Speichern je Zeile. */
  /** Story 5.98: `archived` = ausgeblendet (nur mit „anzeigen“ sichtbar). */
  variants: { id: string; number: string; name: string; options: ArticleOptionView[]; active: boolean | null; version: number; archived: boolean }[];
  values: ArticleFormValues;
  /** Wirksamer Wert je vererbbarem Feld mit Herkunft. */
  effective: Record<string, { value: string | boolean | number | null; source: 'own' | 'parent' | 'none' }>;
  manufacturerName: string | null;
  /** Story 10.16: „Abverkauf“ laut Shopware – nur Vorschlag fürs Häkchen; `null` = unbekannt. */
  shopCloseout: boolean | null;
  /** Story 10.15: Vorschlag „Wiederauffülldauer“ aus der Lieferzeit des Hauptlieferanten (Kalendertage); `null` = keiner. */
  restockSuggestion: { value: number; unit: 'workdays' | 'weeks'; days: number } | null;
}

/** Story 5.93: Vorschau des Varianten-Generators (`POST /api/v1/articles/variant-plan`). */
export interface VariantPlanRow {
  values: string[];
  /** Vorgeschlagene Nummer; `null` = Kombination gibt es schon bzw. keine gültige Nummer möglich. */
  number: string | null;
  exists: boolean;
}

export interface VariantPlan {
  parentNumber: string;
  /** Story 5.99: `newValues` = Werte, die es noch nicht gibt (stehen auch in `values`; erst beim Anlegen geschrieben). */
  groups: { name: string; values: string[]; newValues: { value: string; similar: string | null }[] }[];
  rows: VariantPlanRow[];
  newCount: number;
  valueCounts: number[];
  limits: { maxVariantsPerCreate: number; maxOptionGroups: number };
  version: number;
}

/** Story 5.94: Merkmalswert einer Variante ändern (`POST /api/v1/articles/{id}/options`, Antwort `ArticleDetail`). */
export interface ChangeVariantOptionInput {
  /** Gelesene Version der Variante. */
  version: number;
  group: string;
  value: string;
  /** Story 5.99: unbekannten Wert in den Stammdaten anlegen (nur mit `canCreateOptions`). */
  create?: boolean;
  /** Story 5.99: ähnlichen vorhandenen Wert übergehen („trotzdem neu“). */
  confirmSimilar?: boolean;
}

/** Story 5.99: „aktiv“ mehrerer Varianten (`POST /api/v1/articles/{id}/variants/active`, „Alle Varianten erben lassen“). */
export interface VariantActiveState {
  id: string;
  version: number;
  /** `null` = erbt vom Hauptartikel. */
  active: boolean | null;
}

export interface SetVariantsActiveInput {
  variants: VariantActiveState[];
}

export interface VariantsActive {
  variants: VariantActiveState[];
}

/** Story 5.95: Merkmal an einem Hauptartikel ergänzen (`POST /api/v1/articles/{id}/variant-groups`, Antwort `ArticleDetail` des Hauptartikels). */
export interface AddVariantGroupInput {
  /** Eigenschaft aus den Stammdaten (Groß/klein egal). */
  group: string;
  /** Je Variante genau ein Wert dieser Eigenschaft. */
  values: { variantId: string; value: string }[];
}

/** Story 5.93: Antwort von `POST /api/v1/articles/{id}/variants/generate`. */
export interface GeneratedVariants {
  variants: { id: string; number: string; name: string }[];
  /** Story 25.3: aus dem Pool vergebene EANs und „Pool leer“ (ältere Antworten: fehlt). */
  gtinPool?: GtinPoolResultView;
}

/** Story 5.96: Prüfung vor „Zu Hauptartikel mit Varianten machen“ (`GET /api/v1/articles/{id}/convert-check`). */
export interface MainConversionCheck {
  kind: 'single' | 'main' | 'variant';
  number: string;
  /** Bestand in Stück, der an die Bestands-Variante geht. */
  onHand: number;
  binCode: string | null;
  minimumStock: number | null;
  ean: string | null;
  /** Sperrgründe als Sätze; leer = umwandeln möglich. */
  reasons: string[];
  /** Hinweis, wenn es ein eBay-Angebot zum Artikel gibt (eBay bleibt unverändert). */
  ebayHint: string | null;
  /** Story 5.112: Hinweis, wenn es Bestell-Entwürfe mit dem Artikel gibt (sperren nicht). */
  purchaseDraftHint: string | null;
}

/** Story 5.96: Antwort von `POST /api/v1/articles/{id}/convert-to-main`. */
export interface ConvertedToMain {
  variants: { id: string; number: string; name: string }[];
  stockVariant: { id: string; number: string };
  quantity: number;
  binCode: string | null;
  minimumStock: number | null;
  movedSuppliers: number;
  /** Story 25.3: aus dem Pool vergebene EANs der übrigen Varianten und „Pool leer“ (ältere Antworten: fehlt). */
  gtinPool?: GtinPoolResultView;
}

export interface Manufacturer {
  id: string;
  name: string;
}

/** Nachzähl-Markierung (Story 5.48, `GET /api/v1/inventory/recounts/{id}`). */
export interface RecountMark {
  id: string;
  articleId: string;
  articleNumber: string;
  articleName: string;
  binCode: string | null;
  open: boolean;
  /** `manual` von Hand · `packing` beim Packen. */
  occasions: string[];
  refs: string[];
  reportedAt: string;
  reportedByName: string | null;
  /** Buchbestand jetzt; geht beim Speichern als `expectedOnHand` mit. */
  onHand: number;
  /** Änderungsmarke der Stückzahl jetzt; geht beim Speichern als `expectedVersion` mit (AD-4). */
  onHandVersion: number;
  countedQuantity: number | null;
  countedAt: string | null;
  countedByName: string | null;
  movementId: string | null;
  version: number;
}

export type ArticleEbayStockState = 'unlinked' | 'none' | 'queued' | 'waiting' | 'active' | 'paused' | 'shadowed' | 'off' | 'failed';

/** Bestand je Artikel (Story 5.3), ganze Stück. */
export interface StockItem {
  articleId: string;
  number: string;
  name: string;
  binCode: string | null;
  onHand: number;
  /** An offene Reservierungen zugeteilt (Story 5.4). */
  reserved: number;
  /** Bestand − reserviert. */
  available: number;
  /** Fehlmenge offener Reservierungen. */
  short: number;
  minimumStock: number | null;
  version: number;
}

/** Offene Reservierung einer Auftragsposition (Story 5.4). */
export interface ReservationView {
  id: string;
  lineId: string;
  orderNumber: string;
  source: 'order' | 'example';
  quantity: number;
  reserved: number;
  short: number;
  priorityTs: string;
}

export interface ReservationList {
  stock: StockItem;
  items: ReservationView[];
  /** Beispiel-Reservierungen möglich (nur Testumgebung). */
  examples: boolean;
}

export interface StockPage {
  items: StockItem[];
  nextCursor: string | null;
}

export interface BinLocationView {
  id: string;
  code: string;
  /** Bereich bzw. Zone (Story 5.39). */
  area: string | null;
  note: string | null;
  /** Gesperrt: nicht mehr zuordenbar. */
  locked: boolean;
  articleCount: number;
  version: number;
  createdAt: string;
}

/** Lagerplatz mit den Artikeln darauf (Seitenspalte der Lagerplatz-Liste, Story 5.39). */
export interface BinLocationDetail extends BinLocationView {
  articles: { articleId: string; articleLabel: string; onHand: number }[];
}

/** Antwort der Zuordnung: je Artikel Lagerplatz und Wert davor (Rückgängig). */
export interface BinAssignmentResult {
  assignments: { articleId: string; binCode: string | null; previousBinCode: string | null; changed: boolean }[];
}

export interface StockMovement {
  id: string;
  articleId: string;
  articleLabel: string;
  quantity: number;
  /** Änderung der reservierten Menge (Zuteilung/Freigabe); sonst 0. */
  reservedChange: number;
  onHandAfter: number;
  kind: 'correction' | 'initial' | 'reservation' | 'release' | 'receipt' | 'return';
  reason: string;
  note: string | null;
  actorName: string;
  occurredAt: string;
}

export interface StockMovementPage {
  items: StockMovement[];
  nextCursor: string | null;
}

export interface BinAssignment {
  articleId: string;
  binCode: string | null;
  previousBinCode: string | null;
  changed: boolean;
}

/** KI-Anbieter (Einstellungen › KI, Story 2.10). */
export interface AiProviderView {
  id: 'openai' | 'anthropic';
  label: string;
  model: string | null;
  /** Cent je 1 Mio. Token. */
  inputPriceMinor: number | null;
  outputPriceMinor: number | null;
  currency: string | null;
  reachable: boolean;
  unreachableSince: string | null;
  nextProbeAt: string | null;
  waiting: number;
  switchMode: SwitchMode;
  /** Zugang unter Einstellungen › Zugänge hinterlegt (nie mit Wert). */
  credentialSet: boolean;
  simulated: boolean;
  simulatedOutage: { active: boolean; since: string | null } | null;
}

export interface AiOverview {
  providers: AiProviderView[];
  defaultProvider: 'openai' | 'anthropic' | null;
  /**
   * `samples[].lines`: Zeilen des synthetischen Test-Dokuments (Beispielfall der Karte „Beispiel-Prüfung“, Ticket 1.301);
   * `samples[].expected`: erwartetes Ergebnis (Muster der Vorschau, nicht von der KI) oder `null`.
   */
  prompts: { id: string; version: number; label: string; samples: { id: string; label: string; lines: string[]; expected: unknown }[] }[];
  simulation: boolean;
  /** Admin: darf die Beispiel-Prüfung starten und Ausfälle nachstellen. */
  canOperate: boolean;
}

export type AiCallStatus = 'pending' | 'waiting' | 'not_sent' | 'failed' | 'succeeded' | 'invalid_output' | 'refused' | 'cancelled';

/** Zeile im KI-Protokoll. */
export interface AiCall {
  id: string;
  requestedAt: string;
  provider: 'openai' | 'anthropic';
  providerLabel: string;
  model: string;
  promptLabel: string;
  status: AiCallStatus;
  statusDetail: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  /** Millionstel von `currency` als ganze Zahl (Text). */
  costMicro: string | null;
  currency: string;
  durationMs: number | null;
  removedFields: string[];
  finishedAt: string | null;
}

export interface AiCallPage {
  items: AiCall[];
  nextCursor: string | null;
}

export interface AiCallDetail extends AiCall {
  inputSha256: string;
  entityType: string;
  entityId: string;
  /** Eingabe und Ausgabe nur für Admins. */
  contentVisible: boolean;
  input: { data: unknown; document: { name: string; sizeBytes: number; text: string | null } | null } | null;
  output: unknown;
  /** Eingabe und Ausgabe nach Ablauf der Aufbewahrungsfrist entfernt (ISO-Zeitpunkt); sonst `null`. */
  contentPurgedAt: string | null;
}

export type AiAgentMode = 'off' | 'shadow' | 'live';
export type AiAgentRunStatus =
  | 'running'
  | 'waiting_model'
  | 'parked'
  | 'not_sent'
  | 'call_failed'
  | 'succeeded'
  | 'failed'
  | 'stopped_limit'
  | 'stopped_off';
export type AiToolKind = 'read' | 'propose' | 'draft' | 'plan' | null;

/** KI-Agent (Einstellungen › KI › Agenten, Story 2.19). */
export interface AiAgent {
  id: string;
  label: string;
  area: string;
  areaLabel: string;
  goal: string;
  version: number;
  promptLabel: string;
  modelClass: 'small' | 'large';
  modelClassLabel: string;
  /** Höchste Stufe (AD-16): 1 Vorschlag, 2 Entwurf, 3 mit einem Klick, 4 automatisch. */
  stage: number;
  stageLabel: string;
  mode: AiAgentMode;
  modeLabel: string;
  tools: { name: string; label: string; kind: AiToolKind }[];
  lastRun: { id: string; status: AiAgentRunStatus; startedAt: string } | null;
}

/** Ein Topf des KI-Budgets (Story 2.21); Beträge in Millionstel US-Dollar als Text. */
export interface AiBudgetPot {
  area: string;
  label: string;
  /** `null` = kein Teilbudget. */
  limitMicro: string | null;
  usedMicro: string;
  reservedMicro: string;
  spentMicro: string;
  percent: number | null;
  exhausted: boolean;
}

/** Einstellungen › KI › Budget (nur Admins). */
export interface AiBudget {
  period: string;
  resumesOn: string;
  version: number;
  warningPercent: number;
  alarmRecipient: string | null;
  total: AiBudgetPot;
  areas: AiBudgetPot[];
  pausedText: string | null;
}

/** Einstellungen › KI › Artikel-Assistent (Story 5.102): Abschnitt eines Prompts. */
export interface AiArticleAssistantSection {
  key: string;
  label: string;
  text: string;
  /** Agenten, in denen der Text wirkt; leer = wirkt erst mit dem Artikel-Assistenten. */
  usedBy: string[];
}

export interface AiArticleAssistantHistoryEntry {
  version: number;
  changedAt: string;
  changedByName: string;
  sections: Record<string, string>;
  changed: string[];
}

/** Seite älterer Fassungen (`GET /api/v1/ai/article-assistant/history`). */
export interface AiArticleAssistantHistoryPage {
  history: AiArticleAssistantHistoryEntry[];
  historyNextCursor: number | null;
}

export interface AiArticleAssistant {
  prompts: { version: number; changedAt: string | null; changedByName: string | null; sections: AiArticleAssistantSection[] };
  fields: { version: number; items: { key: string; label: string; included: boolean; hint: string | null }[] };
  maxChars: number;
  history: AiArticleAssistantHistoryEntry[];
  /** Ältere Fassungen: `cursor` für `GET …/article-assistant/history`; `null` = keine älteren. */
  historyNextCursor: number | null;
  test: { agentMode: 'off' | 'shadow' | 'live'; exampleNumber: string; notesMax: number };
}

export interface AiArticleAssistantTestSide {
  state: 'waiting' | 'succeeded' | 'failed';
  steeringVersion: number | null;
  features: string[];
  paragraphs: string[];
  metaTitle: string | null;
  metaDescription: string | null;
  reason: string | null;
  /** Faktencheck des Servers: `true` = nicht belegt. */
  unsupported: { metaTitle: boolean; metaDescription: boolean; features: boolean[]; paragraphs: boolean[] };
}

export interface AiArticleAssistantTest {
  testId: string;
  saved: AiArticleAssistantTestSide;
  /** `null`: neue Fassung gleich der gespeicherten – nur „Gespeichert“. */
  draft: AiArticleAssistantTestSide | null;
}

/** MCP-Schlüssel ohne Schlüssel und ohne Hash (Einstellungen › KI › MCP-Schlüssel, epic-ki-assistent-mcp Story 1). */
export interface McpKey {
  id: string;
  name: string;
  userId: string;
  expiresAt: string;
  revokedAt: string | null;
  version: number;
  status: 'active' | 'expired' | 'revoked';
  ownerName: string;
  scope: 'read' | 'read+draft';
  scopeLabel: string;
  createdAt: string;
  createdByName: string;
}

/** Antwort von „Schlüssel erzeugen“: der Schlüssel – nur dieses eine Mal. */
export interface McpKeyCreated {
  id: string;
  key: string;
  createdAt: string;
}

/** Ein Zugriff über /mcp aus dem Protokoll. */
export interface McpAccess {
  id: string;
  occurredAt: string;
  keyId: string;
  tool: string;
  toolLabel: string;
  outcome: 'ok' | 'not_found' | 'error' | 'rejected';
  outcomeLabel: string;
  subject: string | null;
  onBehalfOfName: string;
}

/** Ein Punkt von „Heute wichtig“ (Story 2.23): fester Titel, Grund vom Modell, Ort der Arbeit. */
export interface BriefingItem {
  id: string;
  kind: 'cases' | 'approvals' | 'shipping' | 'money' | 'follow_up' | 'suggestions' | 'kpi';
  title: string;
  reason: string;
  path: string;
  action: string;
}

/** Tagesbriefing „Heute wichtig“ der angemeldeten Person (Story 2.23). */
export interface Briefing {
  state: 'paused' | 'none' | 'waiting' | 'ready' | 'failed';
  pausedTitle: string | null;
  pausedText: string | null;
  items: BriefingItem[];
  readyAt: string | null;
  requestedAt: string | null;
  trigger: 'schedule' | 'manual' | null;
  /** Lauf offen, aber hängend (Grenze aus den Einstellungen): „aktualisieren“ startet neu. */
  stale: boolean;
}

/** Band der Startseite (alle Angemeldeten). */
export interface AiBudgetStatus {
  pausedText: string | null;
  resumesOn: string;
}

/** Bewertung je Agent (Story 2.21, nur Admins). */
export interface AiAgentMetric {
  agentId: string;
  label: string;
  areaLabel: string;
  costMicro: string;
  accepted: number;
  modified: number;
  rejected: number;
  acceptanceRate: number | null;
  correctionRate: number | null;
  minutesSaved: number;
  /** Zehntelstunden (15 = 1,5 h). */
  hoursSavedTenths: number;
  previous: { costMicro: string; acceptanceRate: number | null };
  reasons: Record<SuggestionRejectReason | 'none', number>;
  evalCandidates: { kindLabel: string; fields: string[]; decision: 'rejected' | 'modified'; reason: SuggestionRejectReason | null; reasonLabel: string | null; date: string }[];
}

export interface AiAgentMetrics {
  period: string;
  minutesPerAcceptance: number;
  agents: AiAgentMetric[];
}

export interface AiAgentList {
  agents: AiAgent[];
  /** Version von `ai_agent_switch`; beim Schalten als `expectedVersion` mitschicken. */
  switchVersion: number;
}

/** Zeile im Lauf-Protokoll der KI-Agenten. */
export interface AiAgentRun {
  id: string;
  agentId: string;
  agentLabel: string;
  agentVersion: number;
  promptLabel: string;
  mode: 'shadow' | 'live';
  modeLabel: string;
  trigger: string;
  startedByName: string | null;
  status: AiAgentRunStatus;
  statusDetail: string | null;
  toolCalls: number;
  applied: number;
  callId: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  /** Millionstel von `currency` als ganze Zahl (Text). */
  costMicro: string | null;
  currency: string | null;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
}

export interface AiAgentRunPage {
  items: AiAgentRun[];
  nextCursor: string | null;
}

export interface AiAgentRunStep {
  seq: number;
  phase: 'read' | 'plan';
  tool: string;
  toolKind: AiToolKind;
  label: string;
  outcome: 'ok' | 'shadowed' | 'rejected' | 'failed' | 'rolled_back';
  detail: string | null;
  input: unknown;
  result: unknown;
  createdAt: string;
}

export interface AiAgentRunDetail extends AiAgentRun {
  result: unknown;
  steps: AiAgentRunStep[];
}

/** Zeile der Steuersatz-Tabelle (Einstellungen › Steuer). */
export interface TaxRateEntry {
  country: string;
  countryName: string;
  /** Prozent als Dezimaltext („25.5“). */
  rate: string;
  validFrom: string;
  validUntil: string | null;
  tableVersion: number;
  status: 'current' | 'planned' | 'past' | 'superseded';
  supersededBy: number | null;
  createdAt: string;
  createdByName: string;
}

export interface TaxRatesView {
  tableVersion: number;
  tableVersionLabel: string;
  today: string;
  countries: { code: string; name: string }[];
  entries: TaxRateEntry[];
  canChange: boolean;
}

export interface TaxNoteVersion {
  version: number;
  text: string;
  createdAt: string;
  createdByName: string;
}

export interface TaxNote {
  kind: 'intra_community_supply' | 'export' | 'delivery_terms';
  kindLabel: string;
  language: 'de' | 'en';
  languageLabel: string;
  current: TaxNoteVersion | null;
  /** Frühere Versionen, neueste zuerst. */
  history: TaxNoteVersion[];
}

export interface TaxNotesView {
  notes: TaxNote[];
  canChange: boolean;
}

/** Zeile der Versandkosten-Tabelle (Einstellungen › Versandkosten, Story 4.6). */
export interface ShippingCostEntry {
  shippingMethod: string;
  /** ISO-2 oder `null` = alle Länder. */
  country: string | null;
  countryName: string | null;
  /** Endbetrag in ganzen Cent. */
  amountMinor: number;
  currency: string;
  validFrom: string;
  validUntil: string | null;
  tableVersion: number;
  status: 'current' | 'planned' | 'past' | 'superseded';
  supersededBy: number | null;
  /** Netto bei deutscher USt (`null` bei ersetzten Zeilen). */
  net: { rate: string; netMinor: number } | null;
  createdAt: string;
  createdByName: string;
}

export interface ShippingCostsView {
  tableVersion: number;
  tableVersionLabel: string;
  today: string;
  shippingMethods: string[];
  countries: { code: string; name: string }[];
  entries: ShippingCostEntry[];
  canChange: boolean;
}

export interface ShippingCostSaved extends ShippingCostsView {
  changed: boolean;
  /** Hinweis nach dem Speichern (z. B. „Shopware berechnet für diese Versandart noch 16,90 € …“). */
  notice: string | null;
}

/** Zugänge zu Gegenstellen (AD-31): nie mit Wert, nur „hinterlegt am … von …“. */
export interface CredentialField {
  id: string;
  label: string;
  /** Mehrzeiliger Wert (PEM-Schlüssel). */
  multiline: boolean;
  set: boolean;
  version: number;
  setAt: string | null;
  setByName: string | null;
  /** Zugänge-Wächter (Story 1.85): Ablaufdatum (Gegenstelle vor Admin) und Zustand gegen die Vorwarnfrist. */
  expiry: {
    expiresOn: string | null;
    source: 'counterpart' | 'admin' | null;
    state: 'ok' | 'expiring' | 'expired' | 'none';
    daysLeft: number | null;
    adminExpiresOn: string | null;
  };
  expiryVersion: number;
  /** Vom ERP selbst erzeugter Schlüssel (EBICS, Story 13.22): kein Eingabefeld. */
  generated: boolean;
}

export interface CredentialTest {
  status: 'running' | 'ok' | 'failed';
  message: string;
  requestedAt: string;
  finishedAt: string | null;
}

export interface CredentialCounterpart {
  ref: string;
  label: string;
  counterpart: string;
  note: string | null;
  editable: boolean;
  fields: CredentialField[];
  complete: boolean;
  switches: { direction: SwitchDirection; mode: SwitchMode }[];
  testAvailable: boolean;
  lastTest: CredentialTest | null;
  /** Letzte Nachtprüfung (Story 1.85); `null` = noch keine. */
  watch: { status: 'ok' | 'failed' | 'skipped'; message: string; checkedAt: string } | null;
  /** Letzte Änderungen und Tests, neueste zuerst (nie mit Wert). */
  history: { at: string; text: string; fieldLabel: string | null; byName: string }[];
}

export interface CredentialOverview {
  counterparts: CredentialCounterpart[];
  /** Admin: darf eintragen, entfernen, testen. */
  canChange: boolean;
  /** Server hat den Schlüssel zum Versiegeln. */
  configured: boolean;
}

/** Beispiel-Rechnung der Prüf-Gegenstelle (nur Testumgebung). */
export interface ExampleInvoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  status: 'open' | 'paid';
}

/** KI-Vorschlag in Karte und Liste (Story 2.11). */
export interface SuggestionItem {
  id: string;
  kind: string;
  title: string;
  /** Art und Name des Datensatzes, z. B. „Benutzer · Sabine (Test)“. */
  meta: string;
  entityType: string;
  entityId: string;
  entityName: string;
  /** Sicherheit in Prozent (0–100). */
  confidence: number;
  status: 'open' | 'accepted' | 'rejected';
  createdAt: string;
  decidedAt: string | null;
  decidedByName: string | null;
  /** Version des Vorschlags; „Rückgängig“ schickt sie mit (Story 2.17). */
  version: number;
  /** Grund beim Verwerfen (Story 2.21); `null` = keiner. */
  rejectReason: SuggestionRejectReason | null;
  rejectReasonLabel: string | null;
  /** Mit geänderten Werten übernommen. */
  modified: boolean;
  /** Entschieden wird im Besitzer (Story 2.18): `mass_edit` → „In Massenbearbeitung öffnen“ statt „Übernehmen“. */
  decideVia: 'mass_edit' | null;
}

/** Grund beim Verwerfen (EXPERIENCE §4.13). */
export type SuggestionRejectReason = 'wrong' | 'unnecessary' | 'already_done';

export interface SuggestionPage {
  items: SuggestionItem[];
  nextCursor: string | null;
  /** Anzahl offener Vorschläge (nur Ansicht „Offen“). */
  total: number | null;
}

export interface SuggestionDetail extends SuggestionItem {
  reason: string;
  sourceRef: string | null;
  callId: string | null;
  changes: { field: string; label: string; before: unknown; after: unknown; appliedChanged: boolean; applied: unknown }[];
  /** Die Daten haben sich seit dem Vorschlag geändert: nur Verwerfen. */
  stale: boolean;
  canAccept: boolean;
  /** Entschieden: bis wann „Rückgängig“ geht (Frist des Servers); offen: `null` (Story 2.17). */
  undoUntil: string | null;
  /** Restzeit für „Rückgängig“ in ms beim Antworten des Servers; offen: `null`. */
  undoRemainingMs: number | null;
}

/** Beleg (jüngste Fassung) für Einstellungen › Belegvorlagen (Story 3.1). */
export interface DocumentItem {
  documentId: string;
  versionId: string;
  number: string;
  versionNo: number;
  status: 'rendering' | 'sealed' | 'invalid';
  /** Belegsprache der Fassung (Story 3.8). */
  language: 'de' | 'en';
  issuedAt: string;
  issuedByName: string | null;
  sealedAt: string | null;
  templateVersion: string;
  /** Version des gemeinsamen Layouts, mit der die Fassung ausgestellt wurde (Story 3.4). */
  layoutVersion: number;
  /** Vorlagen-Version der Belegart; `null` bei Fassungen vor Story 3.4. */
  kindVersion: string | null;
  /** Goldener Fall des Musters (Story 3.3); `null` bei Mustern davor. */
  sampleCase: 'R-01' | 'R-02' | 'R-09' | 'R-11' | null;
  /** Summen aus dem Snapshot (Text in kleinster Einheit). */
  totals: { priceMode: 'net' | 'gross'; currency: { code: string; minorUnits: number }; net: string; tax: string; gross: string } | null;
  pdf: { sha256: string; sizeBytes: number } | null;
  render: { attempts: number; lastError: string | null; nextAttemptAt: string } | null;
  /** Nur bei ungültiger Fassung (Story 3.6). */
  invalid: DocumentInvalidState | null;
  /** Versand erlaubt erst bei `sendable` (Story 3.6); `purged` = nach Fristende entfernt (Story 3.9). */
  sendState: DocumentSendState;
}

/** Ungültige Fassung (Story 3.6): Versuche, Grund und nächster Schritt. */
export interface DocumentInvalidState {
  attempts: number;
  reason: string | null;
  /** `regenerate_when_service_back` = das ERP legt die neue Fassung selbst an; `admin_decision` = „Neu erzeugen“ (Admin). */
  nextStep: 'regenerate_when_service_back' | 'admin_decision' | 'enter_buyer_reference';
}

export type DocumentSendState = 'sendable' | 'pending_send' | 'purged';

/** Aufbewahrungsfristen je Belegart (Story 3.9, nur lesen). */
export interface DocumentRetention {
  version: number;
  validFrom: string;
  rows: { kind: string; label: string; years: number }[];
}

export interface DocumentSamples {
  items: DocumentItem[];
  canIssue: boolean;
  /** Gemeinsames Layout heute (Story 3.4): Version, Gültig-ab, Fußzeile mit Platzhaltern. */
  layout: { version: number; validFrom: string; footer: { column: number; text: string }[] };
  /** Angemeldete Belegarten mit Vorlagen-Version. */
  kinds: { kind: string; label: string; version: string }[];
  /** Aufbewahrungsfristen je Belegart; `null`, wenn die Einstellung fehlt (Story 3.9). */
  retention: DocumentRetention | null;
}

/** Geänderte Angabe zwischen zwei Belegfassungen (Story 3.5). */
export interface DocumentChange {
  field: string;
  label: string;
  before: string | null;
  after: string | null;
}

/** Fassung in der Beleg-Ansicht (Story 3.5). */
export interface DocumentVersionView {
  versionId: string;
  versionNo: number;
  status: 'rendering' | 'sealed' | 'invalid';
  createdAt: string;
  createdByName: string | null;
  sealedAt: string | null;
  language: string;
  templateVersion: string;
  layoutVersion: number;
  kindVersion: string | null;
  date: string | null;
  changes: DocumentChange[];
  artifacts: { kind: string; sha256: string; sizeBytes: number; createdAt: string }[];
  render: { attempts: number; lastError: string | null; nextAttemptAt: string } | null;
  invalid: DocumentInvalidState | null;
  /** Aufzubewahren bis (Story 3.9), fachliches Datum. */
  retainUntil: string;
  /** Inhalt nach Fristende entfernt (Story 3.9); sonst `null`. */
  purged: { purgedAt: string; sha256: string } | null;
  /** E-Rechnung der Fassung (Story 12.1); `null` = nur PDF. */
  /** Story 12.1/12.2: E-Rechnung der Fassung; `problem` = XRechnung ohne Leitweg-ID. */
  eInvoice: {
    format: 'zugferd' | 'xrechnung';
    profile: 'EN16931';
    /** 380 = Handelsrechnung, 381 = Stornorechnung (Story 12.10), 384 = korrigierte Rechnung (Story 12.9, Berichtigung). */
    typeCode: '380' | '381' | '384';
    buyerReference: string | null;
    orderReference: string | null;
    dueDate: string;
    cashDiscount: { until: string; amountMinor: string; payableMinor: string; percent: string; days: number } | null;
    problem: 'buyer_reference_missing' | null;
    /** Story 12.9: vorausgehende Rechnung (BT-25/BT-26) bei Typ 384; sonst `null`. */
    precedingInvoice?: { number: string; date: string } | null;
  } | null;
}

/** Beleg mit allen Fassungen (`GET /api/v1/documents/{id}`, Story 3.5). */
export interface DocumentView {
  documentId: string;
  kind: string;
  kindLabel: string;
  number: string;
  /** `probe` = Beleg der Probephase (Nummer „P-…“, nichts geht hinaus). */
  runMode: 'live' | 'probe';
  issuedAt: string;
  issuedByName: string | null;
  sampleCase: string | null;
  /** Neueste zuerst. */
  versions: DocumentVersionView[];
  /** Felder des Bearbeiten-Dialogs (nur Muster). */
  sample: { recipientLines: string[]; notes: string[] } | null;
  canRevise: boolean;
  /** „Neu erzeugen“ erlaubt (Admin, jüngste Fassung ungültig, nicht entfernt; Story 3.6). */
  canRecreate: boolean;
  sendState: DocumentSendState;
  /** Späteste Frist aller Fassungen (Story 3.9). */
  retainUntil: string | null;
  /** Inhalt aller Fassungen nach Fristende entfernt (Story 3.9). */
  purged: boolean;
  /** Story 12.1: Fälligkeit und offener Posten der Rechnung (nur Beleg-Ansicht); `null`/fehlt = keine Rechnung. */
  openItem?: {
    dueDate: string;
    openMinor: string;
    currency: string;
    cashDiscount?: { until: string; amountMinor: string; payableMinor: string } | null;
    /** Story 12.6: Zahlstand (dieselbe Quelle wie der Chip in „Belege“ am Auftrag). */
    payment?: InvoicePaymentStanding;
  } | null;
  /** Story 12.2: Kunde der Rechnung (für „Leitweg-ID eintragen“ in der Karte „E-Rechnung“). */
  invoiceCustomer?: { id: string; number: string; name: string; version: number; eInvoiceFormat: 'zugferd_en16931' | 'xrechnung' | null; buyerReference: string | null } | null;
  /** Story 12.8: Versandstand (Rechnung, Proforma); `null`/fehlt = Beleg wird nicht über „Beleg senden“ versendet. */
  dispatch?: DocumentDispatch | null;
  /** Story 12.9: „Bearbeiten“ an der Rechnung; `null`/fehlt = keine Rechnung aus dem ERP. */
  invoiceEdit?: { editable: boolean; reason: string | null } | null;
  /** Story 12.9: Karte „Berichtigung“ – an der Rechnung ihre Berichtigungen, an der Berichtigung ihr Bezug. */
  correction?:
    | { role: 'invoice'; invoiceNumber: string; corrections: InvoiceCorrectionCard[] }
    | { role: 'correction'; invoice: { documentId: string; number: string; date: string }; correction: InvoiceCorrectionCard }
    | null;
  /** Story 12.11: Sendeentscheidung „Erneut an den Kunden senden?“ einer Berichtigung; `null`/fehlt = keine. */
  resendDecision?: CorrectionResendDecision | null;
  /** Story 12.10: Ketten „alt → Storno → neu“ (bei A → B → C an B zwei); leer/fehlt = nichts ersetzt. */
  replacements?: InvoiceReplacementChain[];
  /** Story 16.6 (D-04): DATEV-Stapel, in dem die Rechnung bzw. Stornorechnung exportiert ist; `null`/fehlt = nicht exportiert. */
  datevExport?: { batchId: string; periodFrom: string; periodTo: string; createdAt: string } | null;
}

/** Stand der Sendeentscheidung einer Berichtigung (Story 12.11). Abnahme durch Steuerberater/Rudolf nötig. */
export interface CorrectionResendDecision {
  status: 'pending' | 'sent' | 'discarded';
  /** Tag der Wiedervorlage (Tag der Berichtigung). */
  startDate: string;
  decidedAt: string | null;
  decisionDate: string | null;
  /** Nach „senden“: Tag, an dem die Berichtigung versendet ist; `null` = noch nicht beim Kunden. */
  deliveredDate?: string | null;
  decidedByName: string | null;
  /** Darf die ansehende Person jetzt entscheiden (offen und Admin bzw. Vertretung)? */
  canDecide: boolean;
  /** Eingetragene Vertretung; `null` = nur Admins. */
  deputyName: string | null;
  /** Empfänger wie beim Versand: Name der Rechnungsadresse und E-Mail-Adresse (`null` = keine gültige hinterlegt). */
  recipient?: { name: string; address: string | null } | null;
  /** Rechtshinweis als Satz („Die Berichtigung gilt erst, wenn der Kunde sie erhalten hat.“). */
  hint: string;
}

/** `POST /api/v1/billing/documents/{id}/resend-decision` (Story 12.11). */
export interface CorrectionResendDecided {
  outcome: 'sent' | 'discarded';
  decisionDate: string;
  dispatchStatus: 'enqueued' | 'no_recipient' | 'skipped' | null;
}

/** Geänderte Angabe einer Berichtigung (alt → neu; Story 12.9). */
export interface InvoiceFactChange {
  label: string;
  old: string | null;
  new: string | null;
}

/** Eine Berichtigung mit Person, Zeit und Änderungen (Story 12.9). Abnahme durch Steuerberater/Rudolf nötig. */
export interface InvoiceCorrectionCard {
  correctionId: string;
  documentId: string;
  number: string;
  businessDate: string;
  createdAt: string;
  createdByName: string | null;
  changes: InvoiceFactChange[];
  reasons: { field: string; outcome: string; label: string }[];
  kernelVersion: string;
  tableVersion: number;
}

/** Anschrift wie am Auftrag (`orderAddressSchema`). */
export interface InvoiceEditAddress {
  company: string | null;
  department?: string | undefined;
  firstName: string | null;
  lastName: string | null;
  street: string | null;
  additionalLine: string | null;
  postalCode: string | null;
  city: string | null;
  countryCode: string;
}

/** Belegdaten einer Rechnung, die „Rechnung bearbeiten“ ändern kann (Story 12.9). */
export interface InvoiceEditFacts {
  billingAddress: InvoiceEditAddress;
  vatId: string | null;
  customerOrderRef: string | null;
  serviceDate: string;
  headerText: string | null;
  paymentTermsText: string | null;
}

/** `GET /api/v1/billing/documents/{id}/edit` (Story 12.9). */
export type InvoiceEdit =
  | { editable: false; code: 'not_invoice' | 'is_correction' | 'is_cancellation' | 'replaced' | 'no_facts' | 'voided' | 'not_sealed' | 'run_mode'; reason: string }
  | {
      editable: true;
      invoice: { documentId: string; number: string; date: string };
      base: { documentId: string; number: string };
      facts: InvoiceEditFacts;
      lines: { position: number; articleNumber: string; name: string; quantity: string; unit: string; netMinor: string }[];
      grossMinor: string;
      currency: string;
      eInvoice: boolean;
      corrections: InvoiceCorrectionCard[];
      /** Story 12.10: Rechnungskunde (wechselbar nur per Storno + neue Rechnung). */
      customer: { id: string; number: string | null; name: string | null };
      /** Story 12.10: Lieferland der Rechnung. */
      deliveryCountryCode: string;
      approvalNote: string;
    };

/** `POST /api/v1/billing/documents/{id}/correction-preview` – Ergebnis des Kerns `classifyInvoiceChange` (Story 12.9). */
export interface InvoiceCorrectionPreview {
  kind: 'none' | 'invoice_correction' | 'cancel_and_reissue';
  /** `technicalLimit`: Tabelle sagt Berichtigung, das ERP kann diese Angabe aber technisch nur per Storno + neue Rechnung. */
  reasons: { field: string; fieldLabel: string; outcome: string; outcomeLabel: string; label: string; technicalLimit: boolean }[];
  kernelVersion: string;
  tableVersion: number;
  changes: InvoiceFactChange[];
  canSave: boolean;
  blockedReason: string | null;
  blockedBy: 'table' | 'technical' | null;
  baseDocumentId: string;
  /** Story 12.10: bei „Storno + neue Rechnung“ (oder neuem Rechnungskunden/Lieferland) die Vorschau beider Belege. */
  replacement?: InvoiceReplacementPreview | null;
}

/** Story 12.10: ein Beleg der Vorschau „Storno + neue Rechnung“ (Beträge positiv, kleinste Einheit). */
interface InvoiceReplacementParty {
  netMinor: string;
  taxMinor: string;
  grossMinor: string;
  currency: string;
  customerId: string;
  customerNumber: string;
  customerName: string | null;
}

/** Story 12.10: Vorschau „Storno + neue Rechnung“ (alles verworfen, keine Nummer). */
export interface InvoiceReplacementPreview {
  kind: 'none' | 'invoice_correction' | 'cancel_and_reissue';
  reasons: { field: string; fieldLabel: string; outcome: string; outcomeLabel: string; label: string }[];
  kernelVersion: string;
  tableVersion: number;
  baseDocumentId: string;
  changes: InvoiceFactChange[];
  canSave: boolean;
  blockedReason: string | null;
  invoice: { documentId: string; number: string; date: string };
  cancellation: InvoiceReplacementParty;
  newInvoice: (InvoiceReplacementParty & { invoiceDate: string; serviceDate: string; dueDate: string; openMinor: string; taxNotes: string[] }) | null;
  movedMinor: string;
  overpaidMinor: string;
}

/** Story 12.10: `POST /api/v1/billing/documents/{id}/replacement`. */
export interface InvoiceReplaced {
  replacementId: string;
  cancellation: { documentId: string; versionId: string; number: string };
  newInvoice: { invoiceId: string; documentId: string; versionId: string; number: string; openItemId: string; dueDate: string };
  businessDate: string;
  preview: InvoiceReplacementPreview;
}

/** Story 14.2: `GET /api/v1/billing/documents/{id}/credit-note/draft` – was sich gutschreiben lässt. Abnahme durch Steuerberater/Rudolf nötig. */
export type CreditNoteDraft =
  | { allowed: false; code: string; reason: string }
  | {
      allowed: true;
      invoice: { documentId: string; number: string; date: string };
      base: { documentId: string; number: string };
      currency: string;
      priceMode: 'net' | 'gross';
      eInvoice: boolean;
      lines: { position: number; articleNumber: string; name: string; quantity: string; unit: string; amountMinor: string; remainder: boolean }[];
      shipping: { label: string; amountMinor: string } | null;
      creditNotes: { documentId: string; number: string; date: string; grossMinor: string }[];
      approvalNote: string;
    };

/** Story 14.2: `POST …/credit-note/preview` (Summen über `computeDocumentTotals`, nur lesend). */
export interface CreditNotePreview {
  netMinor: string;
  taxMinor: string;
  grossMinor: string;
  currency: string;
}

/** Story 14.2: `POST /api/v1/billing/documents/{id}/credit-note`. */
export interface CreditNoteIssued {
  created: boolean;
  creditNoteId: string;
  documentId: string;
  versionId: string;
  number: string;
  invoice: { documentId: string; number: string };
  grossMinor: string;
  currency: string;
  includesShipping: boolean;
  businessDate: string;
}

/** Story 12.10: Kette „alt → Storno → neu“ an allen drei Belegen. */
export interface InvoiceReplacementChain {
  role: 'original' | 'cancellation' | 'new';
  original: { documentId: string; number: string; date: string };
  cancellation: { documentId: string; number: string; date: string };
  newInvoice: { documentId: string; number: string; date: string };
}

/** `POST /api/v1/billing/documents/{id}/corrections` (Story 12.9). */
export type InvoiceCorrected =
  | { status: 'none'; preview: InvoiceCorrectionPreview }
  | { status: 'issued'; correctionId: string; documentId: string; versionId: string; number: string; businessDate: string; preview: InvoiceCorrectionPreview };

/** Versandstand eines Belegs (Story 12.8). */
export type DocumentDispatchState = 'waiting' | 'in_transit' | 'sent' | 'shadow' | 'failed' | 'undeliverable' | 'awaiting' | 'discarded';
export type DocumentDispatchReason = 'no_address' | 'invalid_address' | 'send_failed' | 'bounced' | 'switch_off' | 'document_invalid';

export interface DocumentDispatch {
  state: DocumentDispatchState;
  stateLabel: string;
  reasonCode: DocumentDispatchReason | null;
  reasonLabel: string | null;
  lastAttemptAt: string | null;
  sentAt: string | null;
  /** „Beleg senden“ möglich (fertige Fassung vorhanden, keine E-Mail mehr offen); alle angemeldeten Benutzer dürfen senden. */
  canSend: boolean;
  /** Die letzte E-Mail zu diesem Beleg wartet noch auf den Versand (dann ist „Beleg senden“ gesperrt). */
  mailOpen: boolean;
}

/** `POST /api/v1/documents/{id}/send` (Story 12.8). */
export interface DocumentSendResult {
  status: 'enqueued' | 'no_recipient' | 'skipped';
  dispatch: DocumentDispatch;
}

/** `GET /api/v1/documents/dispatch-summary` – Kachel „Nicht versendet“ (Story 12.8). */
export interface DocumentDispatchSummary {
  notSent: number;
  byReason: { reasonCode: DocumentDispatchReason | null; reasonLabel: string | null; count: number }[];
}

export interface DocumentRecreateResult {
  versionNo: number;
  document: DocumentView;
}

export interface DocumentRevisionResult {
  changed: boolean;
  versionNo: number;
  document: DocumentView;
}

/** Nummernkreis für Einstellungen › Nummernkreise (Story 3.7). */
export interface NumberRange {
  rangeKey: string;
  label: string;
  runMode: 'live' | 'probe';
  pattern: string;
  digits: number | null;
  counter: number;
  counterYear: number | null;
  lastNumber: string | null;
  nextNumber: string | null;
  full: boolean;
  kinds: { kind: string; label: string }[];
}

export interface NumberRanges {
  ranges: NumberRange[];
  report: {
    checkedDocuments: number;
    errors: { type: 'duplicate' | 'unexplained_gap' | 'above_counter'; rangeKey: string; runMode: 'live' | 'probe'; counterYear: number | null; firstNumber: string; lastNumber: string }[];
    gaps: {
      id: string;
      rangeKey: string;
      runMode: 'live' | 'probe';
      counterYear: number | null;
      firstNumber: string;
      lastNumber: string;
      reasonCode: 'taken_over' | 'manual' | 'restore';
      reason: string;
      createdAt: string;
    }[];
    /** Story 11.25: ungültig abgelegte Belege – Nummer bleibt vergeben (keine Lücke), neueste zuerst. */
    voided: { documentId: string; rangeKey: string; runMode: 'live' | 'probe'; number: string; reason: string; voidedAt: string }[];
  };
  canSet: boolean;
}

// ---------------------------------------------------------------------------
// Aufträge (epic-auftragseingang Story 1): Formen wie `apps/server/src/routes/orders.ts`.

/** Betrag in der kleinsten Einheit als exakte Ganzzahl-Zeichenfolge (nie Gleitkomma) mit Währung. */
export interface OrderMoney {
  amountMinor: string;
  currency: string;
}

export interface OrderAddress {
  company: string | null;
  /** Story 9.36: Abteilung (Zeile unter der Firma); fehlt bei Adressen ohne Abteilung. */
  department?: string | null;
  firstName: string | null;
  lastName: string | null;
  street: string | null;
  additionalLine: string | null;
  postalCode: string | null;
  city: string | null;
  countryCode: string;
}

/** Steuer zu einem Satz, unverändert aus dem Shop (Story 4). */
export interface OrderTax {
  rate: string;
  amount: OrderMoney;
}

/** Rundungseinstellung des Shops (Shopware CashRoundingConfig), unverändert (Story 4). */
export interface OrderRounding {
  decimals: number;
  /** Rundungsschritt als Dezimaltext („0.01“). */
  interval: string;
  roundForNet: boolean;
}

export interface OrderLine {
  id: string;
  position: number;
  kind: 'product' | 'promotion' | 'other';
  kindLabel: string;
  externalId: string;
  productNumber: string | null;
  label: string;
  quantity: number;
  /** Story 7.12: davon storniert (Storno aus Shop oder ERP, Teilstorno). */
  cancelledQuantity: number;
  /** Story 7.12: offene Menge = bestellt − storniert. */
  openQuantity: number;
  /** Einzelpreis im Preis-Modus des Auftrags, Dezimaltext mit 4 Nachkommastellen („38.9000“). */
  unitPrice: string;
  total: OrderMoney;
  /** `null` = keiner angegeben oder mehrere Sätze (dann gilt `taxes`). */
  taxRate: string | null;
  /** Steuer der Position je Satz; leer = nicht übermittelt. */
  taxes: OrderTax[];
  promotionCode: string | null;
  /** Zusatzfelder der Position wie im Shop (Story 16). */
  customFields: Record<string, unknown>;
  /** Artikelstand bei Bestellung wie im Shop (Shopware `payload`; Story 16). */
  payload: Record<string, unknown>;
  /** Reservierung im Lager (Story 6.6); `null` = keine (Rabatt, freie Position, unbekannte Artikelnummer, Probebetrieb). */
  reservation: OrderLineReservation | null;
  /** Story 6.10: Shop-Preis neben dem ERP-Preis im Preis-Modus; `null` = keine Warenposition, Altauftrag oder Preis-Modus unklar. */
  comparison: OrderLineComparison | null;
}

/**
 * Vergleich Shop ↔ ERP-Preis einer Warenposition (Story 6.10): `compared` mit beiden Einzelpreisen (Dezimaltext im
 * Preis-Modus), sonst der Grund, warum nicht verglichen wurde.
 */
export interface OrderLineComparison {
  state: 'compared' | 'no_erp_price' | 'foreign_currency' | 'decision_required' | 'not_compared';
  /** Shop-Einzelpreis im Preis-Modus; bei `basis: 'net'` im Brutto-Auftrag das herausgerechnete Shop-Netto, auf Cent gerundet. */
  shop: string | null;
  /** ERP-Preis im Preis-Modus (Brutto: deutscher Bruttopreis); bei `basis: 'net'` im Brutto-Auftrag das ERP-Netto, auf Cent gerundet. */
  erp: string | null;
  equal: boolean | null;
  /** Basis des Vergleichs (Story 6.36): wie der Preis-Modus; Brutto-Shop bei steuerfreier Lieferung netto. `null` = nicht verglichen. */
  basis: 'gross' | 'net' | null;
}

/** Versandkosten Shop ↔ Regel (Story 6.10): `compared` mit beiden Beträgen im Preis-Modus, sonst der Grund. */
export interface OrderShippingComparison {
  state: 'compared' | 'decision_required' | 'no_rule_value' | 'not_compared';
  shop: OrderMoney | null;
  rule: OrderMoney | null;
  equal: boolean | null;
}

/** Reservierung einer Auftragsposition: bestellt, davon reserviert, Fehlmenge; `released` = freigegeben. */
export interface OrderLineReservation {
  quantity: number;
  reserved: number;
  short: number;
  released: boolean;
}

/** Story 12.6: Zahlstand einer Rechnung – `paid` bezahlt (offen 0), `partial` teilweise, `open` offen. */
export type InvoicePaymentState = 'paid' | 'partial' | 'open';

/** Story 12.6: Zahlstand-Leiter am Beleg (Rechnungsbetrag, verrechnete Zahlungen, sonst ausgeglichen). */
export interface InvoicePaymentStanding {
  state: InvoicePaymentState;
  grossMinor: string;
  /** `method` `null` = Überweisung bzw. unbekannt. */
  settlements: { method: string | null; date: string; amountMinor: string }[];
  otherMinor: string;
  /** Story 13.7: mit Zahlungseingängen gewährter Skonto (nicht in `otherMinor`); `0` = keiner. Fehlt bei älteren Antworten. */
  cashDiscountMinor?: string;
}

/** Story 12.6: Zahlstand je Rechnung des Auftrags (Chip im Reiter „Belege“). */
export interface OrderInvoicePayment {
  documentId: string;
  documentNumber: string;
  /** Rechnungsdatum (Tagesabschluss, Europe/Berlin), nicht der Zeitpunkt der Ausstellung. */
  invoiceDate: string;
  state: InvoicePaymentState;
  gross: OrderMoney;
  open: OrderMoney;
  /** Fälligkeit des offenen Postens (fehlt bei älteren Antworten). */
  dueDate?: string;
  /**
   * Story 12.10: storniert und durch diese Rechnung ersetzt; `null`/fehlt = nicht ersetzt. `cancellationGross` ist positiv
   * (die Oberfläche zeigt ihn mit Minus) = letzter gültiger Stand inkl. Berichtigung.
   */
  replacedBy?: {
    documentId: string;
    documentNumber: string;
    cancellationDocumentId: string;
    cancellationNumber: string;
    cancellationDate: string;
    cancellationGross: OrderMoney;
    overpaid: OrderMoney;
  } | null;
  /** Story 12.10: diese Rechnung ersetzt eine andere; `null`/fehlt = nein. */
  replaces?: { documentId: string; documentNumber: string } | null;
}

/** Zahlung eines Auftrags (Story 6.7: Checkout-Zahlung aus dem Shop, z. B. PayPal; Story 12.6: Überweisung). */
export interface OrderPayment {
  id: string;
  source: 'checkout' | 'transfer';
  /** Anzeigename der Zahlart („PayPal“). */
  paymentMethod: string;
  status: 'open' | 'paid' | 'failed' | 'refunded' | 'charged_back';
  statusLabel: string;
  /** Satz zur Zahlung („PayPal im Checkout bezahlt“). */
  text: string;
  transactionId: string;
  amount: OrderMoney;
  recordedAt: string;
  statusChangedAt: string;
  example: boolean;
  /** Story 12.6: mit diesen Rechnungen verrechnet, älteste zuerst; leer = noch nicht verrechnet. */
  settledWith?: { documentId: string; documentNumber: string; amount: OrderMoney; replaced?: boolean }[];
}

/** Story 7.11: Verweis auf einen Auftrag (Herkunft/Verbleib). */
export interface OrderRef {
  id: string;
  number: string;
}

/** Story 7.11 ([RUDOLF 15.10.2026]): Herkunft und Verbleib beim Zusammenführen/Teilen. */
export interface OrderOrigin {
  /** Aufnehmender (ältester) Auftrag: die aufgenommenen Aufträge. */
  absorbed: { orders: OrderRef[]; twoShippingCosts: boolean } | null;
  mergedInto: OrderRef | null;
  splitOff: OrderRef[];
  splitFrom: OrderRef | null;
}

/** Story 7.11: mögliche Partner zum Zusammenführen und ob Teilen geht. */
export interface OrderMergeOptions {
  candidates: { id: string; number: string; version: number; mergeable: boolean; reason: string | null }[];
  splittable: boolean;
}

/** Story 7.12: Liefermodus, nötige Erstattung und mögliche Aktionen. */
export interface OrderFulfilment {
  /** `null` = nicht gewählt (Story 9.20). */
  deliveryMode: 'complete' | 'partial' | null;
  /** Story 9.20: Versandkosten der zweiten Sendung (beim Anlegen gefragt); `null` = nicht gefragt. */
  secondShipment: { charged: boolean; amount: OrderMoney } | null;
  /** Summe der nötigen Erstattungen; `null` = keine. */
  refundRequired: OrderMoney | null;
  canReleasePartial: boolean;
  canCancel: boolean;
  /** Warum Stornieren nicht geht; `null` = geht. */
  blockedReason: string | null;
}

/** Ticket 42: Angebot „Freigeben“ (nur aus dem gespeicherten Ampel-Ergebnis). */
export type OrderReleaseOffer = { kind: 'release'; steps: ('hold' | 'partial')[]; sentence: string } | { kind: 'blocked'; checkId: string; sentence: string };

export interface OrderCancellationResult {
  version: number;
  cancellationId: string;
  refund: OrderMoney | null;
}

export interface MergedOrders {
  orderId: string;
  number: string;
  version: number;
  origins: { orderId: string; number: string }[];
  shippingCostsTaken: number;
}

export interface SplitOrderResult {
  originOrderId: string;
  originNumber: string;
  version: number;
  parts: { orderId: string; number: string; version: number }[];
}

/** Lieferschein eines Auftrags (Story 11.23, `GET /api/v1/orders/{id}/delivery-notes`). */
export interface DeliveryNote {
  id: string;
  number: string;
  documentId: string;
  versionId: string | null;
  versionNo: number | null;
  status: 'rendering' | 'sealed' | 'invalid' | null;
  language: 'de' | 'en' | null;
  pdfAvailable: boolean;
  runMode: 'live' | 'probe';
  lineCount: number;
  createdAt: string;
  /** Story 11.25: ungültig abgelegt; `null` = gültig. */
  voided: DeliveryNoteVoid | null;
  /** Story 11.25: Label angefragt oder erzeugt – kann nicht mehr ungültig werden. */
  labelled: boolean;
  /** Story 11.27: Druckstand aus bestätigten Drucken; `null` = kein Druck bestätigt (nächster Druck = Original). */
  printed: DeliveryNotePrinted | null;
  /** Story 11.27 ([RUDOLF] 22.10.2026): jüngster gesendeter, unbestätigter Druck (zählt nicht); `null` = keiner. */
  sentUnconfirmedAt: string | null;
}

/** Druckstand eines Lieferscheins (Story 11.27). */
export interface DeliveryNotePrinted {
  count: number;
  firstAt: string;
  lastAt: string;
  lastKind: 'original' | 'copy';
}

/** Vermerk „ungültig“ eines Lieferscheins (Story 11.25). */
export interface DeliveryNoteVoid {
  at: string;
  reason: string;
  reasonCode: 'order_changed' | 'manual';
  successorId: string | null;
  successorNumber: string | null;
}

/** Antwort von `POST /api/v1/shipping/delivery-notes/{id}/void` bzw. `…/replace` (Story 11.25). */
export interface VoidedDeliveryNote {
  voided: { id: string; number: string; documentId: string };
  successor: DeliveryNote | null;
  message: string;
}

/**
 * Eintrag der Beleg-Liste eines Datensatzes (Story 11.24, `GET /api/v1/chatter/{entityType}/{entityId}/documents`):
 * jüngste Fassung je Beleg – eigene Belege des Datensatzes plus die der angemeldeten Quellen (z. B. Lieferscheine).
 */
export interface RecordDocument {
  documentId: string;
  number: string;
  kind: string;
  kindLabel: string;
  issuedAt: string;
  runMode: 'live' | 'probe';
  versionId: string;
  versionNo: number;
  status: 'rendering' | 'sealed' | 'invalid';
  language: 'de' | 'en';
  purged: boolean;
  /** Story 11.25: Vermerk „ungültig“ (Lieferschein); PDF bleibt öffnenbar. */
  voided: boolean;
  pdfAvailable: boolean;
}

/** Antwort von `POST /api/v1/shipping/delivery-notes`: `created: false` = vorhandener Lieferschein. */
export interface CreatedDeliveryNote {
  created: boolean;
  deliveryNote: DeliveryNote;
}

export interface OrderDetail {
  /** `draft` = Entwurf (Story 9.1); `merged`/`split` = zusammengeführt/geteilt (Story 7.11, schreibgeschützt). */
  status: 'open' | 'cancelled' | 'draft' | 'merged' | 'discarded';
  fulfilmentState: 'open' | 'label_pending' | 'labelled' | 'ready_for_pickup' | 'shipped';
  carrierRequestSent: boolean;
  /** Story 11.7: Vorgabe DHL-Produkt am Auftrag (`rule` = von einer Regel, `user` = von einer Person); `null` = keine. */
  carrierProduct: { code: string; name: string; express: boolean; source: 'rule' | 'user'; by: string | null } | null;
  /** Story 11.7: Sendungsnummern aller gültigen Labels (je Lieferschein und Paket); Story 11.34: `weightKg` = Gewicht des Pakets („1.85“, `null` = keins). */
  trackingNumbers: { trackingNumber: string; deliveryNoteNumber: string; seq: number; product: string; weightKg: string | null }[];
  id: string;
  number: string;
  shopOrderNumber: string;
  /** „Ihre Bestellnummer“ (Story 6.22); `null` = keine. */
  customerOrderNumber: string | null;
  /** Verkaufskanal im Shop (Story 6.22); `null` = unbekannt. */
  salesChannel: string | null;
  source: 'shopware' | 'ebay' | 'pdf' | 'manual';
  sourceLabel: string;
  externalId: string;
  revision: string;
  orderedAt: string;
  receivedAt: string;
  runMode: 'live' | 'probe';
  runModeLabel: string;
  example: boolean;
  language: string | null;
  priceMode: 'gross' | 'net' | 'unclear';
  priceModeLabel: string;
  /** Offener Klärfall „Preis-Modus unklar“ (Story 16); `null` = keiner. */
  priceModeCase: { id: string } | null;
  taxStatus: string | null;
  currency: string;
  currencyRate: string;
  /** Story 9.6: `EZB` bei Fremdwährungsauftrag im ERP, sonst `null`. */
  currencyRateSource: 'EZB' | null;
  currencyRateDate: string | null;
  paymentMethod: string;
  shippingMethod: string | null;
  customer: {
    id: string;
    number: string;
    name: string;
    match: 'linked' | 'matched_by_email' | 'created';
    matchLabel: string;
    exists: boolean;
    possibleDuplicateOf: string | null;
  };
  billingAddress: OrderAddress;
  shippingAddress: OrderAddress | null;
  lines: OrderLine[];
  shipping: { method: string | null; total: OrderMoney; taxes: OrderTax[]; comparison: OrderShippingComparison | null };
  /** `null` = nicht übermittelt. */
  rounding: { item: OrderRounding | null; total: OrderRounding | null };
  totals: {
    positions: OrderMoney;
    shipping: OrderMoney;
    net: OrderMoney;
    total: OrderMoney;
    taxes: OrderTax[];
  };
  /** Zusatzfelder der Bestellung wie im Shop (Story 16). */
  customFields: Record<string, unknown>;
  marketing: { affiliateCode: string | null; campaignCode: string | null; tags: string[]; shopSource: string | null };
  internalComment: string | null;
  /** Kommentar des Kunden zur Bestellung (Story 7.18); `null` = keiner. Immer als Text zeigen. */
  customerComment: string | null;
  delivery: { earliest: string | null; latest: string | null };
  /** Zahlungen, älteste zuerst (Story 6.7). */
  payments: OrderPayment[];
  /** Story 12.6: Zahlstand je Rechnung, älteste zuerst. */
  invoicePayments?: OrderInvoicePayment[];
  examples: boolean;
  /** Tags in der Reihenfolge des Setzens (z. B. „Express“ durch eine Regel). */
  tags: string[];
  /** Auftragsampel; `null` = noch nicht berechnet (Aufträge von vor der Ampel). */
  checks: OrderChecks | null;
  /** Story 7.9: Stand des Auftrags für Änderungen (AD-4). */
  version: number;
  /** Herkunft des aktuellen Adress-Stands; `null` = wie bei der Bestellung. */
  addressOrigin: 'inbound' | 'erp_edit' | 'erp_suggestion' | null;
  /** Story 9.26/9.32: einzige Regel „bearbeitbar“ – Positionen, Mengen, Preise, Versand-/Zahlart und Adresse (bis Versand). */
  editable: boolean;
  /** Story 9.26: Satz, warum nicht bearbeitbar; `null` = bearbeitbar. */
  editLockReason: string | null;
  /** Story 9.32: Hinweis bei manueller Sperre (sperrt das Bearbeiten nicht); `null` = kein Hinweis. */
  editNotice: string | null;
  /** Story 9.26: nach der letzten Bearbeitung keine neue AB (Band „Auftrag geändert – AB neu senden?“). */
  confirmationOutdated: boolean;
  /** Manuelle Sperre; `null` = nicht gesperrt. */
  manualHold: { reason: string; byName: string; at: string } | null;
  /** Ergebnis der Adressprüfung für den aktuellen Stand; `null` = nicht geprüft. */
  addressCheck: { revisionNo: number; status: 'ok' | 'suggestion' | 'undeliverable'; suggestion: OrderAddress | null; suggestionText: string | null } | null;
  /** Story 11: Beleg Auftragsbestätigung und AB-Adresse des Kunden. */
  confirmation: OrderConfirmation;
  /** Story 6.9: offene Änderungen aus Shopware je Gruppe (vorher/nachher). */
  inboundChanges: OrderInboundChanges;
  /** Story 6.13: Statusmeldungen an den Kanal mit ihrem Stand, älteste zuerst. */
  statusSync: OrderStatusSyncItem[];
  /** Story 7.10: angebotene Steuer-Entscheidungen (leer = nichts zu entscheiden) und die letzte Entscheidung. */
  taxDecision: OrderTaxDecisionState;
  /** Story 9.2: Herkunft der aus der PDF-Bestellung gelesenen Angaben; `null` = kein PDF-Entwurf oder ohne Fundstellen. */
  pdfReading: PdfDraftReading | null;
  /** Story 9.3: Regel-Automatik im Entwurf aus PDF; `null` = kein PDF-Entwurf oder vor Story 9.3. */
  draftRules: PdfDraftRules | null;
  /** Story 7.11. */
  origin: OrderOrigin;
  merge: OrderMergeOptions;
  /** Story 7.12. */
  fulfilment: OrderFulfilment;
  /**
   * Ticket 42 (A1): Knopf „Freigeben“ am Klärfall-Auftrag (`release`, löst Sperre und Fehlbestand) oder nur ein Satz
   * (`blocked`, z. B. Steuer – die gibt „Freigeben“ nie frei); `null` = kein Klärfall.
   */
  releaseOffer: OrderReleaseOffer | null;
  /**
   * Story 9.24: je Position ein laufender Rahmenauftrag des Kunden für denselben Artikel (Shop-/eBay-Position, noch nicht
   * als Abruf gezählt); Satz „Rahmenauftrag RA-… für TT-GB-M8 (offen 70) · als Abruf zählen“. Leer = keiner.
   */
  blanketHints?: OrderBlanketHint[];
}

/** Story 9.24: Hinweis an einer Auftragsposition auf einen laufenden Rahmenauftrag. */
export interface OrderBlanketHint {
  lineId: string;
  blanketOrderId: string;
  blanketLineId: string;
  number: string;
  unitPrice: string;
  open: number;
  hint: string;
}

/** Story 9.3: Versand, Steuer (aus `decideTax`) mit Versionen und je Position ERP-Preis, PDF-Preis und Fehlbestand. */
export interface PdfDraftRules {
  status: 'decided' | 'decision_required' | 'not_assessed';
  reason: string;
  taxCase: string | null;
  rate: string | null;
  notes: string[];
  versions: { decideTax: string | null; taxRateTable: string | null; shippingCostTable: string | null; documentTotals: string | null; shippingMethods: number };
  shipping: { method: string | null; status: 'none' | 'rule_value' | 'no_rule_value'; amount: OrderMoney | null; reason: string | null };
  lines: { position: number; productNumber: string | null; quantity: number; erpUnitPrice: string | null; pdfUnitPrice: string | null; pdfDiffers: boolean; missing: number }[];
}

/** Story 9.14: Ausschnitt im Foto der Bestellung (Rechteck in Prozent des Bildes). */
export interface PdfReadingRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Story 9.14: Artikel über die Nummer des Kunden aus seinem früheren Auftrag zugeordnet. */
export interface PdfLineMatchedFrom {
  orderNumber: string;
  /** Datum des früheren Auftrags (`YYYY-MM-DD`). */
  orderedOn: string;
}

/** Story 9.2: Fundstelle (Seite/Zeile) und Sicherheit einer aus dem PDF gelesenen Angabe; `null` = nicht gefunden. */
export interface PdfReadingSource {
  page: number | null;
  line: number | null;
  confidence: number | null;
  /** Story 9.14: Zelle in der Excel-Bestellung (z. B. `C7`). */
  cell?: string;
  /** Story 9.14: Ausschnitt im Foto. */
  region?: PdfReadingRegion;
}

/** Story 9.2: Herkunft der gelesenen Angaben eines Auftragsentwurfs aus PDF. */
export interface PdfDraftReading {
  prompt: string;
  customer: PdfReadingSource;
  orderReference: PdfReadingSource;
  deliveryAddress: PdfReadingSource & { found: boolean; used: boolean; sameAsBilling?: boolean };
  requestedDate: PdfReadingSource & { date: string | null; dayEstimated: boolean };
  lines: (PdfReadingSource & { position: number; recognized: boolean; matchedFrom?: PdfLineMatchedFrom })[];
  /** Story 9.14: Foto oder Excel; fehlt bei PDF. */
  documentKind?: 'pdf' | 'image' | 'excel';
}

/** Story 7.10: eine Steuer-Entscheidung, die der Server für die heutige Lage anbietet (aus `decideTax` bzw. Abweichung Shop/Regel). */
export interface OrderTaxDecisionOption {
  decision: 'request_vat_id' | 'german_vat' | 'proof_at_pickup' | 'confirm_intra_community_supply' | 'recalculate_tax' | 'confirm_shop_value';
  label: string;
}

/** Story 7.10: Steuer-Entscheidung am Auftrag; `latest.applies = false` = galt für eine frühere Lage. */
export interface OrderTaxDecisionState {
  options: OrderTaxDecisionOption[];
  latest: { decision: OrderTaxDecisionOption['decision']; label: string; reason: string; byName: string; at: string; ruleVersion: string; applies: boolean } | null;
  /** Story 12.18: Vorschau der Folgen nach „Steuer nachberechnen“ bzw. „mit deutscher Umsatzsteuer abrechnen“; `null` = keine. */
  recalculation: OrderTaxRecalculation | null;
}

/** Story 12.18: eine Lesart der Nachberechnung (bzw. der bezahlte Stand); Beträge exakt als Zeichenfolge. */
export interface OrderTaxRecalculationAmounts {
  net: OrderMoney;
  tax: OrderMoney;
  gross: OrderMoney;
  /** Brutto minus bezahltes Brutto: positiv = Kunde zahlt nach, negativ = Erstattung; `null` beim bezahlten Stand. */
  difference: OrderMoney | null;
  taxBreakdown: { taxCategory: string; taxRate: string; taxableAmount: OrderMoney; taxAmount: OrderMoney }[];
}

/** Story 12.18: Vorschau „Netto bleibt“ / „Brutto bleibt“ – nur Anzeige, das ERP ändert nichts. */
export interface OrderTaxRecalculation {
  decision: 'recalculate_tax' | 'german_vat';
  decisionLabel: string;
  rateText: string;
  ruleReason: string;
  versions: { decideTax: string; taxRateTable: string; recalculation: string | null; documentTotals: string | null };
  /** Grund, warum keine Vorschau möglich ist; dann sind die Beträge `null`. */
  blockedReason: string | null;
  targetRate: string | null;
  paid: OrderTaxRecalculationAmounts | null;
  keepNet: OrderTaxRecalculationAmounts | null;
  keepGross: OrderTaxRecalculationAmounts | null;
}

/** Story 6.13: eine Statusmeldung an Shopware bzw. eBay mit ihrem Stand. */
export interface OrderStatusSyncItem {
  statusSeq: number;
  /** ERP-Ereignis, z. B. „Auftrag eingegangen“. */
  event: string;
  /** Ziel, z. B. „Shopware“. */
  target: string;
  /** Was gemeldet wird, z. B. „Bestellung: In Bearbeitung“; `null` = nichts zu melden bzw. offen. */
  change: string | null;
  state: 'sent' | 'shadowed' | 'off' | 'waiting' | 'queued' | 'retrying' | 'failed' | 'nothing' | 'pending';
  /** Stand als kurzer Satz, z. B. „Probe – würde senden“. */
  text: string;
  detail: string | null;
  at: string | null;
}

/** Gruppe offener Änderungen aus Shopware: Adressen übernehmbar, Positionen und Summe nur verwerfen. */
export interface OrderInboundChangeGroup {
  group: 'deliveryAddress' | 'billingAddress' | 'lines' | 'total';
  label: string;
  acceptable: boolean;
  /** Adresse unvollständig/unkenntlich gemeldet: nur von Hand prüfen. */
  manualOnly: boolean;
  /** Neuere Meldung unlesbar/unvollständig: offen, nur von Hand prüfen. */
  followupUnreadable: boolean;
  /** Stand der Gruppe wie angezeigt (beim Übernehmen/Verwerfen mitschicken). */
  token: string;
  items: { id: string; field: string; label: string; before: string | null; after: string }[];
}

export interface OrderInboundChanges {
  groups: OrderInboundChangeGroup[];
  /** Label schon erstellt oder angefragt: das Paket geht an die alte Adresse. */
  labelExists: boolean;
  revisionNo: number;
}

/** Farbe einer Status-Pille (DESIGN „Status-Werte und Farbe“). */
export type StatusTone = 'ready' | 'check' | 'clarify' | 'waiting' | 'neutral';

/** Eine Prüfung der Auftragsampel. */
export interface OrderCheckItem {
  id: string;
  label: string;
  result: 'green' | 'yellow' | 'red';
  waiting: boolean;
  reason: string | null;
  /** Satz für den Ampel-Block. */
  explanation: string;
}

/** Story 7.9: Ergebnis von „Adresse ändern“ bzw. „Vorschlag übernehmen“. */
export interface ChangedOrderAddress {
  revisionNo: number;
  version: number;
  /** Rechnungsadresse geändert: „Steuer wird neu geprüft“. */
  taxRecheck: boolean;
}

/** Story 9.33: Kopfangaben eines Auftrags in „Auftrag bearbeiten“ (`GET /orders/{id}/edit`, Feld `head`). */
export interface OrderEditHead {
  billingAddress: OrderAddress;
  shippingAddress: OrderAddress | null;
  /** Wunschtermin ISO (JJJJ-MM-TT); `null` = keiner. */
  requestedDeliveryDate: string | null;
  deliveryMode: 'partial' | 'complete' | null;
  /** Story 9.37: Lieferbedingung (Kürzel Incoterms 2020); `null` = Auftrag vor Story 9.37. */
  deliveryTerms: Incoterm | null;
  customerOrderNumber: string | null;
  customerOrderNumberSource: string | null;
  headerText: string | null;
  /** Interne Notiz – nie auf dem Beleg. */
  internalNote: string | null;
  revisionNo: number;
  /** Story 9.36: Kunde des Auftrags (Auswahl „Lieferadresse“ aus dem Adressbuch). */
  customerId: string;
}

/** Story 9.33: Kopfangaben beim Speichern (`PUT /orders/{id}/edit`); fehlt ein Feld, bleibt es wie bisher. */
export interface OrderEditHeadInput {
  billingAddress?: OrderAddress;
  shippingAddress?: OrderAddress | null;
  requestedDeliveryDate?: string | null;
  deliveryMode?: 'partial' | 'complete' | null;
  /** Story 9.37: Lieferbedingung; kein Leeren. */
  deliveryTerms?: Incoterm;
  customerOrderNumber?: string | null;
  headerText?: string | null;
  internalNote?: string | null;
}

/** Story 7.9: aktueller Stand im 409 `order_changed`. */
export interface OrderEditState {
  version: number;
  revisionNo: number;
  billingAddress: OrderAddress;
  shippingAddress: OrderAddress | null;
  manualHold: { reason: string; byName: string } | null;
  changedBy: string | null;
}

export interface OrderChecks {
  version: string;
  pill: 'ready' | 'review' | 'waiting' | 'case' | 'cancelled';
  pillLabel: string;
  pillTone: StatusTone;
  reason: string | null;
  shippable: boolean;
  computedAt: string;
  open: number;
  total: number;
  items: OrderCheckItem[];
  /** Story 15: Klärfall-Erklärer kann laufen – dann „Warum?“ an roten/gelben Prüfungen; sonst kein Knopf. */
  explainAvailable: boolean;
}

/** Story 15: Stand der Erklärung „Warum?“ zu einer Prüfung (`GET /api/v1/orders/{id}/checks/{checkId}/explanation`). */
export interface OrderCheckExplanation {
  available: boolean;
  open: boolean;
  check: { id: string; label: string; result: 'green' | 'yellow' | 'red'; reason: string | null } | null;
  /** `off` noch nicht angefordert · `waiting` Lauf offen · `explained` Erklärung da · `failed` ohne Erklärung beendet. */
  state: 'off' | 'waiting' | 'explained' | 'failed';
  explanationId: string | null;
  explanation: string | null;
  planId: string | null;
  /** Werte und Optionen fest vom Server (nie von der KI). */
  values: { label: string; value: string }[];
  options: string[];
}

/** Auftragsbestätigung eines Auftrags (epic-auftragseingang Story 11). */
export interface OrderConfirmation {
  /** `null` = (noch) keine Auftragsbestätigung. */
  document: {
    documentId: string;
    number: string;
    kindLabel: string;
    versionId: string;
    versionNo: number;
    status: 'rendering' | 'sealed' | 'invalid';
    purged: boolean;
    language: 'de' | 'en';
    pdfAvailable: boolean;
  } | null;
  /** Grund, warum keine AB entstanden ist (offener Klärfall). */
  blocked: string | null;
  /** AB-Adresse; `null` = keine Adresse hinterlegt. */
  recipient: { address: string; source: 'purpose' | 'main' } | null;
}

export interface SentOrderConfirmation {
  mail: { id: string; created: boolean };
  confirmation: OrderConfirmation;
}

/** Stand von „Jetzt abholen“ je Kanal (Story 6.32, `GET /api/v1/orders/fetch-now`). */
export interface OrderFetchChannelState {
  switchMode: 'off' | 'shadow' | 'live';
  runMode: 'live' | 'probe' | null;
  /** Warum „Jetzt abholen“ für den Kanal nicht geht; `null` = geht. */
  blocked: string | null;
  running: { since: string; waiting: boolean } | null;
  lastReadAt: string | null;
  coveredUntil: string | null;
  /** Gelesene Bestellungen, die der Worker gerade in Aufträge übernimmt. */
  pendingIntake: number;
  /** Gelesene Bestellungen, die länger als die Rückstau-Grenze warten (Alarm „Outbox-Rückstand“). */
  stalledIntake: number;
}

/** Stempel der Auftragsampel (Story 7.39): Zeitpunkt der letzten Ampel-Änderung eines Auftrags. */
export interface OrderCheckStamp {
  orderId: string;
  changedAt: string;
  /** Ampel-Spalten der Listenzeile genau wie in der Liste (`orders.status`, `orders.hint`, `orders.checks`); `null` = keine Zeile. */
  list: Record<string, unknown> | null;
}

export interface OrderFetchState {
  shopware: OrderFetchChannelState;
  ebay: OrderFetchChannelState;
  /** Je angefragtem Auftrag (`?orders=`) der Ampel-Stempel; unbekannte Aufträge fehlen. */
  checks: OrderCheckStamp[];
}

/** Antwort auf „Jetzt abholen“ (`POST /api/v1/orders/fetch-now/{kanal}`). */
export interface OrderFetchResult {
  outcome: 'started' | 'running' | 'off' | 'unavailable';
  sideEffectId: string | null;
  message: string;
}

export interface OrderExamplesState {
  available: boolean;
  count: number;
  total: number;
}

export interface CreatedExampleOrders {
  created: number;
  existing: number;
  unreadable: number;
  count: number;
}

export interface RemovedExampleOrders {
  removed: number;
  customersRemoved: number;
  customersKept: string[];
  /** Entfernte Beispiel-Zahlungen (Story 6.7). */
  paymentsRemoved: number;
  count: number;
}

/** Ein Speicher im Verzeichnis der Personendaten (Story 18.2, Einstellungen › Datenschutz › Personendaten). */
export interface PersonalDataStoreItem {
  location: string;
  module: string;
  storage: 'column' | 'json' | 'file';
  storageLabel: string;
  dataKind: string;
  subject: 'customer' | 'user' | 'any';
  subjectLabel: string;
  access: string;
  anonymize: 'replace' | 'clear' | 'delete' | 'redact' | 'keep' | 'retain' | 'expire';
  anonymizeLabel: string;
  description: string | null;
}

export interface PersonalDataOverview {
  areas: { area: string; label: string; stores: PersonalDataStoreItem[] }[];
}

// ---------------------------------------------------------------------------
// Automatisierung (epic-regel-baukasten Story 1)

export interface RuleCatalogOption {
  value: string;
  label: string;
}

/** Kennzeichen jedes Katalog-Eintrags (Story 17.3): Gruppe, heute wählbar oder „folgt“, verdrahtende Story. */
export interface RuleCatalogEntry extends RuleCatalogOption {
  group: string;
  available: boolean;
  wiredIn: string;
}

export interface RuleCatalog {
  version: number;
  triggers: (RuleCatalogEntry & { kind: 'event' | 'time' })[];
  fields: (RuleCatalogEntry & { type: string; operators: (RuleCatalogOption & { available: boolean })[]; options: string[]; choices?: RuleCatalogOption[] })[];
  /** `options` (Story 17.5+17.6): Werte für die Angaben der Aktion (DHL-Produkte, Personen; N42: vorhandene Kunden-Tags als Vorschlag). */
  actions: (RuleCatalogEntry & { command: string; choices: RuleCatalogOption[]; options?: RuleCatalogOption[]; external: boolean; adminOnly: boolean })[];
  /** Arbeitsbereiche für „Klärfall anlegen“ und „Hinweis in der Glocke“ (Story 17.20); ältere Server ohne. */
  areas?: RuleCatalogOption[];
  /** Schritte zwischen den Aktionen (Story 17.21): `wait` „Warte X Tage“; ältere Server ohne. */
  steps?: (RuleCatalogOption & { available: boolean; wiredIn: string })[];
}

/** Strukturgrenzen einer Regel aus den Einstellungen (Story 17.20). */
export interface RuleLimits {
  maxDepth: number;
  maxConditions: number;
  maxActions: number;
  maxPriority: number;
  /** Höchstens so viele Tage je Warte-Schritt (Story 17.21, `rule_wait_days_max`). */
  maxWaitDays: number;
}

/** Regel-Definition nach dem Katalog (Version 3: Bedingungsbaum, 1–n Aktionen, Gruppensteuerung; Version 1 und 2 bleiben gültig). */
export type RuleDefinition = PlatformRuleDefinition;

export interface Rule {
  id: string;
  name: string;
  /** `off` (Story 17.5+17.6): veröffentlicht, aber ausgeschaltet. */
  status: 'draft' | 'published' | 'off';
  statusLabel: string;
  publishedVersion: number | null;
  hasUnpublishedChanges: boolean;
  /** Ein/Aus: eine veröffentlichte Version wirkt. */
  enabled: boolean;
  /** „Zuletzt gelaufen“; `null` = noch nie. */
  lastRunAt: string | null;
  /** Treffer der letzten `hitsDays` Tage. */
  hits: number;
  /** Story 17.21 (N42): Läufe, die an einem Warte-Schritt warten, und der früheste Fälligkeitstag; ältere Server ohne. */
  waiting?: number;
  nextWaitDueOn?: string | null;
  /** `null` = passt nicht mehr zum Katalog. */
  draft: RuleDefinition | null;
  sentence: string | null;
  publishedSentence: string | null;
  version: number;
  updatedAt: string;
}

export interface RulesView {
  catalog: RuleCatalog;
  rules: Rule[];
  canEdit: boolean;
  /** Zeitraum der Trefferzahl in Tagen. */
  hitsDays: number;
  /** Strukturgrenzen aus den Einstellungen (Story 17.20). */
  limits: RuleLimits;
}

export interface RuleSaved extends RulesView {
  ruleId: string;
  /** Bearbeitungsstand, den genau dieses Speichern hinterlassen hat (für das Veröffentlichen). */
  version: number;
}

export interface RulePublished extends RuleSaved {
  published: boolean;
  publishedVersion: number;
}

/** Ein/Aus geschaltet (`POST /rules/{id}/enabled`). */
export interface RuleSwitched extends RuleSaved {
  enabled: boolean;
  changed: boolean;
  publishedVersion: number;
}

export interface RuleExecutionAction {
  index: number | null;
  type: string;
  label: string;
  /** Warte-Schritt (`type` `wait`, Story 17.21): `waiting` wartet bis …, `waited` gewartet, `dropped` entfallen. */
  result: 'added' | 'already_present' | 'removed' | 'not_present' | 'set' | 'unchanged' | 'reverted' | 'created' | 'failed' | 'probe_skipped' | 'waiting' | 'waited' | 'dropped';
  text: string;
}

export interface RuleExecution {
  id: string;
  executedAt: string;
  ruleId: string;
  ruleName: string;
  version: number;
  triggerLabel: string;
  entityType: 'order' | 'customer';
  entityId: string;
  entityLabel: string;
  runMode: 'live' | 'probe';
  runModeLabel: string;
  outcome: 'executed' | 'condition_not_met' | 'skipped' | 'chain_aborted';
  resultText: string;
  actions?: RuleExecutionAction[];
}

export interface RuleExecutionPage {
  items: RuleExecution[];
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Einkauf (Story 10.1): Lieferanten mit Einkaufspreisen

export interface SupplierOption {
  id: string;
  number: string;
  name: string;
  defaultCurrency: string;
}

export interface SupplierArticle {
  id: string;
  supplierId: string;
  supplierNumber: string;
  supplierName: string;
  articleId: string;
  articleNumber: string;
  articleName: string;
  supplierItemNumber: string | null;
  /** Dezimaltext mit vier Nachkommastellen („28.4000“). */
  purchasePrice: string;
  currency: string;
  minimumOrderQuantity: number;
  leadTimeValue: number | null;
  leadTimeUnit: 'workdays' | 'weeks';
  /** Story 10.18: VPE (Stück je Verpackungseinheit); `null` = keine. */
  packSize: number | null;
  isMain: boolean;
  /** Ticket 1.270: Packeinheit des Artikels (Einzahl/Mehrzahl); `null` = keine → „Stück“. */
  packUnit: string | null;
  packUnitPlural: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierDetail {
  id: string;
  number: string;
  name: string;
  defaultCurrency: string;
  note: string | null;
  /** Sprache der Lieferantenbestellung (Story 10.3). */
  orderLanguage: 'de' | 'en';
  address: string | null;
  email: string | null;
  contactName: string | null;
  /** Story 5.84: Land (ISO Alpha-2, z. B. „CN“); bestimmt, ob Zoll im Einstandspreis gilt. `null` = fehlt. */
  country: string | null;
  /** Story 10.13: Sicherheitspuffer in % der Lieferzeit (Mindestbestand-Vorschlag); `null` = Einstellung. */
  safetyBufferPercent: number | null;
  /** Story 10.17: Mindestbestellwert (kleinste Einheit als Ganzzahl-Text, Währung = Standardwährung); `null` = keiner. */
  minimumOrderValue: { amountMinor: string; currency: string } | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  articles: SupplierArticle[];
}

/** Story 5.84: Einstandspreis eines Artikels mit Rechenweg (`GET /api/v1/purchasing/articles/{id}/landed-cost`). Beträge als Dezimaltext mit vier Stellen. */
export interface ArticleLandedCost {
  articleId: string;
  status: 'ok' | 'no_ek' | 'no_fx' | 'no_weight' | 'no_freight_rate';
  reason: string | null;
  note: string;
  version: string;
  landedCost: string | null;
  purchaseEur: string | null;
  freight: string | null;
  customs: string | null;
  other: string | null;
  freightSource: 'central' | 'article' | null;
  freightRate: string | null;
  centralRateVersion: number | null;
  customsBasis: 'article' | 'eu_supplier' | 'no_country' | null;
  customsRatePercent: string | null;
  otherKind: 'percent' | 'amount' | null;
  otherValue: string | null;
  purchaseAmount: string | null;
  purchaseCurrency: string | null;
  fx: { rate: string; date: string } | null;
  supplierCountry: string | null;
  supplierEu: boolean | null;
  weightKg: string | null;
  centralFreightRate: { rate: string; version: number } | null;
}

/** Lieferantenbestellung (Story 10.3): Entwurf → bestätigt (Nummer, PDF) → storniert; nur Nettobeträge. */
export interface PurchaseOrderLine {
  id: string;
  position: number;
  articleId: string;
  articleNumber: string;
  name: string;
  supplierItemNumber: string | null;
  quantity: number;
  unitPrice: string;
  lineNetMinor: string;
  /** Geliefert (Wareneingang); bis dahin 0. */
  receivedQuantity: number;
  /** Per ‚Rest stornieren‘ geschlossen (Story 10.5); 0 = nicht storniert. */
  cancelledQuantity: number;
  restCancelledAt: string | null;
  /** Offen = bestellt − geliefert − Rest storniert. */
  openQuantity: number;
  belowMinimum: number | null;
  /** Ticket 1.270: Packeinheit des Artikels (Einzahl/Mehrzahl); `null` = keine → „Stück“. */
  packUnit: string | null;
  packUnitPlural: string | null;
  /** Abweichender Liefertermin der Position (Story 10.12); `null` = Termin der Bestellung gilt. */
  ownExpectedDate: string | null;
  /** Wirksamer Liefertermin (eigener, sonst der Bestellung). */
  expectedDate: string | null;
}

export interface PurchaseOrder {
  id: string;
  supplierId: string;
  status: 'draft' | 'confirmed' | 'ordered' | 'cancelled';
  number: string | null;
  documentId: string | null;
  language: 'de' | 'en' | null;
  supplierNumber: string | null;
  supplierName: string | null;
  currency: string;
  currencyMinorUnits: number;
  netTotalMinor: string;
  lineCount: number;
  supplierNote: string | null;
  runMode: 'live' | 'probe';
  cancelReason: string | null;
  confirmedAt: string | null;
  /** Als bestellt markiert (Story 10.4). */
  orderedAt: string | null;
  orderedBy: string | null;
  /** Erwarteter Liefertermin JJJJ-MM-TT (Pflicht ab „bestellt“, Story 10.12). */
  expectedDate: string | null;
  cancelledAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  supplier: Omit<SupplierDetail, 'articles'>;
  lines: PurchaseOrderLine[];
  latestVersion: { versionId: string; versionNo: number; status: 'rendering' | 'sealed' | 'invalid'; purged: boolean } | null;
}

export interface ArticleSuppliers {
  articleId: string;
  articleNumber: string;
  isMainArticle: boolean;
  items: SupplierArticle[];
}

// Vorhaben der KI (Story 2.20, AD-36 Stufe 3)

export type AiPlanStatus = 'open' | 'approved' | 'discarded' | 'expired';

export interface AiPlanItem {
  id: string;
  title: string;
  /** Kurzfassung der Schritte, z. B. „E-Mail-Entwurf und neue Wiedervorlage in 7 Tagen“. */
  summary: string;
  meta: string;
  entityType: string;
  entityId: string;
  entityName: string;
  confidence: number;
  status: AiPlanStatus;
  agentId: string;
  agentLabel: string;
  createdAt: string;
  decidedAt: string | null;
  decidedByName: string | null;
  rejectReason: SuggestionRejectReason | null;
  rejectReasonLabel: string | null;
  version: number;
}

export interface AiPlanStep {
  id: string;
  position: number;
  command: string;
  kind: 'mail' | 'task' | 'task_done' | 'note' | null;
  label: string;
  input: Record<string, unknown>;
  editable: string[];
  status: 'planned' | 'applied' | 'skipped';
  changed: boolean;
  assigneeName: string | null;
}

export interface AiPlanDetail extends AiPlanItem {
  reason: string;
  preparedBy: string;
  preparation: string[];
  steps: AiPlanStep[];
  stale: boolean;
  /** Nie entschieden und älter als die Frist: Freigeben gesperrt. */
  expired: boolean;
  canApprove: boolean;
}

export interface AiPlanPage {
  items: AiPlanItem[];
  nextCursor: string | null;
  total: number | null;
}

/** Aufgaben › Freigaben (Story 2.22): ein offenes KI-Ergebnis (Vorschlag, Entwurf – Story 5.125 – oder Vorhaben). */
export interface AiApproval {
  source: 'suggestion' | 'plan' | 'draft';
  id: string;
  /** Version beim Lesen; Freigeben und Verwerfen binden sich an sie. */
  version: number;
  stage: 'suggestion' | 'draft' | 'plan';
  stageLabel: string;
  /** Gleiche Kennung = gleichartig (gemeinsam freigebbar). */
  similarKey: string;
  kindLabel: string;
  title: string;
  area: string | null;
  areaLabel: string;
  /** Arbeitsbereich, dem der Bereich zugeordnet ist; `null` = unter „Meine“ nur bei Admins. */
  workArea: string | null;
  confidence: number;
  entityType: string;
  entityId: string;
  entityName: string;
  meta: string;
  createdAt: string;
  bulkApprovable: boolean;
}

export interface AiApprovalOutcome {
  source: 'suggestion' | 'plan' | 'draft';
  id: string;
  ok: boolean;
  title: string | null;
  meta: string | null;
  status: number | null;
  code: string | null;
  detail: string | null;
}

/** Story 5.125: Entwurf eines neuen eBay-Angebots (`GET /articles/{id}/ebay-listing-draft`). */
export interface EbayListingDraft {
  id: string;
  version: number;
  status: 'generating' | 'draft' | 'approved' | 'listed' | 'rejected' | 'discarded' | 'failed';
  state: 'generating' | 'draft' | 'sending' | 'waiting' | 'off' | 'probe' | 'listed' | 'rejected' | 'failed';
  stateText: string;
  detail: string | null;
  confidence: number | null;
  title: string;
  descriptionHtml: string;
  aspects: { name: string; values: string[] }[];
  categoryKeywords: string;
  categoryId: string | null;
  categoryName: string | null;
  categoryOptions: { id: string; name: string; path: string; aspects: { name: string; required: boolean; values: string[] }[] }[];
  categoriesStatus: 'pending' | 'ready' | 'off' | 'failed';
  categoriesNote: string | null;
  /** Dezimaltext mit Punkt („46.29“). */
  price: string | null;
  priceFloor: string | null;
  currency: string;
  quantity: number;
  available: number;
  buffer: number | null;
  hasImage: boolean;
  setup: { paymentPolicy: boolean; returnPolicy: boolean; shippingPolicy: boolean; postalCode: boolean; dispatchDays: boolean; condition: boolean; businessSeller: boolean };
  /** USt im Angebot (`VATDetails`) aus der Steuerklasse des Artikels heute; `null` = kein Satz hinterlegt. */
  vatPercent: number | null;
  vatClassLabel: string | null;
  /** Kürzel der Steuerklasse des Artikels. */
  vatClass: string | null;
  check: { version: number; ready: boolean; problems: { field: string; text: string }[] };
  itemId: string | null;
  resultText: string | null;
  decidable: boolean;
  createdAt: string;
}

export interface EbayListingDraftView {
  articleId: string;
  listedItemId: string | null;
  canStart: boolean;
  draft: EbayListingDraft | null;
}

/** Bestellvorschlag mit Berechnung (Story 10.6, `GET /purchasing/reorder-proposals/{id}`). */
export interface ReorderProposal {
  id: string;
  runDate: string;
  /** Story 10.7: `accepted` in Lieferantenbestellung übernommen, `discarded` verworfen. */
  status: 'open' | 'superseded' | 'accepted' | 'discarded';
  /** Story 10.7: Lieferantenbestellung, in die der Vorschlag übernommen wurde. */
  purchaseOrderId: string | null;
  /** Story 10.7: berechnete Menge des Laufs (weicht nach Änderung von Hand von `quantity` ab). */
  computedQuantity: number;
  quantityEdited: boolean;
  articleId: string;
  articleNumber: string;
  articleName: string;
  supplierId: string;
  supplierNumber: string;
  supplierName: string;
  quantity: number;
  /** Einkaufspreis je Stück als Dezimaltext „28.4000“. */
  unitPrice: string;
  currency: string;
  /** Summe netto in der kleinsten Einheit als Ganzzahl-Text. */
  lineNetMinor: string;
  salesQuantity: number;
  available: number;
  openQuantity: number;
  minimumStock: number;
  minimumOrderQuantity: number | null;
  demand: number;
  remainder: number;
  minimumOrderApplied: boolean;
  explanation: string[];
  ruleVersion: string;
  computedAt: string;
  /** Story 10.18: Reichweite heute / nach Eingang in Arbeitstagen; `null` = ohne Verkäufe bzw. vor dem ersten Lauf. */
  reachWorkdays?: number | null;
  reachAfterWorkdays?: number | null;
  urgency?: ReorderUrgencyValue | null;
  leadTimeWorkdays?: number | null;
}

/** Story 10.18: Dringlichkeit eines Bestellvorschlags. */
export type ReorderUrgencyValue = 'waiting' | 'urgent' | 'soon' | 'enough';

/** Ansicht der Rechen-Leiste im Bestellvorschlag (Rückblick, Vorausplanung, Grundlagen). */
export interface ReorderViewChoice {
  salesMonths: number;
  planningMonths: number;
  sales: boolean;
  minimumStock: boolean;
}

/** `GET /api/v1/purchasing/reorder-proposals/preview` – offene Positionen in dieser Ansicht neu gerechnet (nur Lesen). */
export interface ReorderPreview extends ReorderViewChoice {
  defaults: { salesMonths: number; planningMonths: number };
  /** Grenzen der Rechen-Leiste aus Einstellungen › Einkauf › Bestellvorschlag. */
  limits: { salesMonths: { min: number; max: number }; planningMonths: { min: number; max: number } };
  items: {
    id: string;
    quantity: number | null;
    outcome: string;
    salesQuantity: number;
    /** Story 10.18: Arbeitstage im gewählten Rückblick; `null` = ungültig. */
    salesWorkdays: number | null;
    storedQuantity: number;
    quantityEdited: boolean;
    /** Story 10.18: Reichweite und Dringlichkeit in dieser Ansicht. */
    reachWorkdays?: number | null;
    reachAfterWorkdays?: number | null;
    urgency?: ReorderUrgencyValue | null;
  }[];
}

/** Verkaufsverlauf zu einem Bestellvorschlag (Story 10.11, `GET /purchasing/reorder-proposals/{id}/history`). */
export interface ReorderSalesHistory {
  runDate: string;
  articleNumber: string;
  /** 12 Monate bis zum Monat des Laufdatums, ältester zuerst; `priorYearQuantity` null = keine Vorjahresdaten. */
  months: { month: string; quantity: number; priorYearQuantity: number | null }[];
}

/** Je Lieferant offene Bestellvorschläge (Story 10.7, `GET /purchasing/reorder-proposals/approvals`). */
export interface ReorderApproval {
  supplierId: string;
  supplierName: string;
  supplierNumber: string;
  lineCount: number;
  /** Summe netto je Währung, Betrag in der kleinsten Einheit als Ganzzahl-Text. */
  totals: { currency: string; netMinor: string }[];
  /** Offene Positionen mit Menge und Einkaufspreis – genau so an ‚Bestätigen‘. */
  lines: ReorderApprovalLine[];
  /** Story 10.17: Mindestbestellwert des Lieferanten (Ampel); `null` = keiner, dann keine Ampel. */
  minimumOrderValue: { amountMinor: string; currency: string } | null;
  /** Story 10.18: EZB-Kurse „Einheiten je 1 EUR“ je Fremdwährung für „≈ … €“ (nur zur Orientierung); ohne Eintrag = Euro oder kein Kurs. */
  eurRates: { currency: string; unitsPerEur: string; on: string }[];
}

/** Position der Karte „Je Lieferant“ mit den Werten für Gruppenkopf und Mengenfeld (Story 10.18). */
export interface ReorderApprovalLine {
  proposalId: string;
  articleId: string;
  articleNumber: string;
  quantity: number;
  unitPrice: string;
  currency: string;
  /** Gewicht je Stück in kg (Dezimaltext); `null` = unbekannt. */
  weightKg: string | null;
  minimumOrderQuantity: number | null;
  /** VPE (Stück je Verpackungseinheit); `null` = keine. */
  packSize: number | null;
  available: number;
  openQuantity: number;
  salesQuantity: number;
  salesWorkdays: number | null;
  leadTimeWorkdays: number | null;
}

/** Story 10.17: Auffüll-Vorschläge bis zum Mindestbestellwert (`GET /purchasing/reorder-proposals/fill-up`). */
export interface ReorderFillUp {
  ruleVersion: string;
  currency: string;
  items: {
    articleId: string;
    articleNumber: string;
    articleName: string;
    quantity: number;
    unitPrice: string;
    currency: string;
    lineNetMinor: string;
    reason: string;
  }[];
}

export interface ReorderApprovals {
  items: ReorderApproval[];
  /** Zeitpunkt des letzten Laufs (Zeitjob oder Hand-Start); noch keiner → null. */
  lastRunAt: string | null;
}

/** Kachel „Aufträge warten“ (Ticket 1.303, `GET /purchasing/reorder-proposals/waiting`). */
export interface ReorderWaiting {
  /** Verschiedene Aufträge mit offener Fehlmenge auf die Artikel der wartenden Vorschläge. */
  orders: number;
  /** Verschiedene Artikel der offenen Vorschläge „Aufträge warten“. */
  articles: number;
}

/** Ergebnis von „Bestellvorschlag jetzt berechnen“ (`POST /purchasing/reorder-proposals/run`). */
export interface ReorderRunResult {
  runDate: string;
  articleCount: number;
  proposalCount: number;
  supplierCount: number;
  lastRunAt: string | null;
}

/** Ergebnis von ‚Bestätigen‘ (`POST /purchasing/reorder-proposals/confirm`). */
export interface ConfirmedReorderProposal {
  supplierId: string;
  purchaseOrderId: string;
  purchaseOrderNumber: string;
  lineCount: number;
}

/** Stand eines Laufs „Eigenschaften aus Datenblatt“ (Story 5.35, `…/property-extractions/{extractionId}`). */
export interface PropertyExtraction {
  extractionId: string;
  articleId: string;
  documentName: string;
  documentKind: 'pdf' | 'image';
  /** `running` = KI liest · `done` = fertig · `failed` = abgebrochen (Grund in `detail`). */
  state: 'running' | 'done' | 'failed';
  /** Angelegte KI-Vorschläge (Probe: 0, nur im Protokoll). */
  suggestions: number;
  /** Agent steht auf „Probe“: Ergebnisse nur im KI-Protokoll. */
  shadow: boolean;
  detail: string | null;
}

/** Datenblatt (PDF) oder Bild an den KI-Agenten „Eigenschaften aus Datenblatt“ geben (Story 5.35). */
export function startPropertyExtraction(getToken: TokenGetter, articleId: string, fileName: string, contentBase64: string): Promise<PropertyExtraction> {
  return apiFetch<PropertyExtraction>(getToken, `/api/v1/catalog/articles/${articleId}/property-extractions`, {
    method: 'POST',
    body: { fileName, contentBase64 },
    idempotencyKey: newIdempotencyKey(),
  });
}

export function fetchPropertyExtraction(getToken: TokenGetter, articleId: string, extractionId: string): Promise<PropertyExtraction> {
  return apiFetch<PropertyExtraction>(getToken, `/api/v1/catalog/articles/${articleId}/property-extractions/${extractionId}`);
}

/** Eintrag im Prüfbericht eines DATEV-Stapels (Story 16.1). */
export interface DatevReportEntry {
  /** Art des Eintrags (Story 16.5, `datev-rows-v2`); ältere Stapel haben keine – die Seite zeigt dann „Sonstiges“. */
  code?: string;
  /** Belegnummer, wenn sich der Eintrag auf einen Beleg bezieht. */
  document: string | null;
  text: string;
  /** Nur beim Hinweis „Debitorenbereich zu N % belegt“: Prozent (abgerundet), höchste Kundennummer, Bereich. */
  usage?: { percent: number; highest: number; from: number; to: number };
}

/** DATEV-Stapel aus `/api/v1/billing/datev-batches` (Story 16.1). Abnahme durch Steuerberater/Rudolf nötig. */
export interface DatevBatch {
  id: string;
  runMode: 'live' | 'probe';
  periodFrom: string;
  periodTo: string;
  origin: 'manual' | 'rule';
  createdAt: string;
  kernelVersion: string;
  report: { errors: DatevReportEntry[]; hints: DatevReportEntry[] };
  rowCount: number;
  /** Summen in Euro-Cent als Text. */
  debitMinor: string;
  creditMinor: string;
  blocked: boolean;
  fileStatus: 'blocked' | 'pending' | 'ready' | 'error';
  file: { path: string; sha256: string; size: number; createdAt: string } | null;
  fileError: string | null;
  /** Story 16.9: „Februar“ bzw. „01.02.–15.03.2027“. */
  periodLabel: string;
  /** Zurückgehalten (Story 16.9); `heldByName` = `null` bei einer Regel. */
  hold: { heldBy: string; heldByName: string | null; heldAt: string } | null;
  /** Sende-Aufträge an die Steuerberatung, neueste zuerst. */
  sends: DatevSend[];
  /** Letzte Ablehnung des Sendens mit Satz wie im Klärfall. */
  refusal: { reasonCode: string; errorCount: number | null; at: string; text: string } | null;
  sendStatus: 'created' | 'held' | 'sending' | 'sent' | 'not_sent';
}

/** Sende-Auftrag eines DATEV-Stapels (Story 16.9). */
export interface DatevSend {
  id: string;
  origin: 'manual' | 'rule';
  recipient: string;
  requestedBy: string | null;
  requestedByName: string | null;
  requestedAt: string;
  resendOf: string | null;
  mailId: string | null;
  /** Zustand der E-Mail (`sent`, `shadowed` = Probe …); `null` = noch nicht eingestellt. */
  mailStatus: string | null;
  mailError: string | null;
}

// ---------------------------------------------------------------------------------------------------------------------
// Retouren (epic-retouren-gutschriften Story 14.1)

/** Auswahl der Maske „Retoure anlegen“ und Zustand des Menüeintrags (`GET /api/v1/returns/draft?orderId=`). */
export interface ReturnDraft {
  order: { id: string; number: string; customerName: string; customerNumber: string };
  /** Mindestens eine Position ist versendet. */
  shipped: boolean;
  /** Satz, warum es keine Retoure geben kann; `null` = möglich. */
  blocked: string | null;
  lines: { orderLineId: string; position: number; productNumber: string | null; label: string; ordered: number; shipped: number; returned: number; max: number }[];
  reasons: { key: string; label: string; faultTill: boolean }[];
  reasonsVersion: number;
  wishes: { value: 'credit' | 'replacement'; label: string }[];
}

/** Antwort von `POST /api/v1/returns`. */
export interface CreatedReturn {
  returnId: string;
  number: string;
  created: boolean;
  /** Neu immer `created`; bei Wiederholung der aktuelle Status. */
  status: ReturnStatus;
}

/** Status einer Retoure: angelegt, eingegangen (Wareneingang gebucht), geprüft (Warenprüfung gebucht; Story 14.6). */
export type ReturnStatus = 'created' | 'received' | 'inspected';

/** Ergebnis der Warenprüfung je Position (Story 14.6). */
export interface ReturnLineInspection {
  soundQuantity: number;
  defectQuantity: number;
  bookedQuantity: number;
  movementId: string | null;
  /** `booked` eingebucht, `defect` defekt, `no_stock_article` / `probe` einwandfrei, aber nicht eingebucht. */
  outcome: 'booked' | 'defect' | 'no_stock_article' | 'probe';
  outcomeText: string;
  /** Notiz zur defekten Ware („was ist kaputt?“, Ticket 19); `null` = keine. */
  defectNote: string | null;
}

/** Stand des Rücksendeetiketts einer Retoure (Ticket 4); `pdfUrl` erst, wenn das Etikett vorliegt. */
export interface ReturnLabelState {
  id: string;
  status: 'requested' | 'pending_external' | 'skipped' | 'shadowed' | 'failed' | 'created' | 'rejected';
  statusText: string;
  trackingNumber: string | null;
  pdfUrl: string | null;
}

/** Vollansicht einer Retoure (`GET /api/v1/returns/{id}`). */
export interface ReturnDetail {
  id: string;
  number: string;
  runMode: 'live' | 'probe';
  status: ReturnStatus;
  statusLabel: string;
  /** Wareneingang (Story 14.6); `null` = noch nicht gebucht. */
  received: { on: string; at: string; by: { id: string; name: string | null } } | null;
  /** Warenprüfung (Story 14.6); `null` = noch nicht gebucht. */
  inspected: { on: string; at: string; by: { id: string; name: string | null } } | null;
  order: { id: string; number: string };
  customer: { id: string; number: string; name: string };
  customerWish: 'credit' | 'replacement';
  customerWishLabel: string;
  reason: { key: string; label: string; faultTill: boolean; settingVersion: number };
  /** Kostenloses Rücksendeetikett gewählt (Ticket 4). */
  /** `null` = Retoure von vor Ticket 4 (keine Wahl getroffen). */
  freeLabel: boolean | null;
  /** Stand des Rücksendeetiketts live aus shipping; `null` = kein Etikett. */
  returnLabel: ReturnLabelState | null;
  returnDate: string;
  createdAt: string;
  createdBy: { id: string; name: string | null };
  version: number;
  /** Höchstlänge der Notiz zum Defekt in Zeichen (Einstellung heute); nur Hilfe, der Server prüft. */
  defectNoteMaxLength: number;
  lines: {
    id: string;
    orderLineId: string;
    position: number;
    productNumber: string | null;
    label: string;
    quantity: number;
    /** Lagerartikel mit Bestand jetzt; `null` = kein Lagerartikel. */
    stock: { articleId: string; onHand: number } | null;
    /** `null` = noch nicht geprüft. */
    inspection: ReturnLineInspection | null;
  }[];
}

/** Wareneingang der Retoure buchen (`POST /api/v1/returns/{id}/receive`, Story 14.6). */
export function receiveReturnRequest(getToken: TokenGetter, returnId: string): Promise<{ returnId: string; number: string; status: ReturnStatus; receivedOn: string }> {
  return apiFetch(getToken, `/api/v1/returns/${encodeURIComponent(returnId)}/receive`, { method: 'POST', body: {}, idempotencyKey: newIdempotencyKey() });
}

/** Warenprüfung buchen (`POST /api/v1/returns/{id}/inspection`, Story 14.6): je Position die einwandfreie Menge. */
export function inspectReturnRequest(
  getToken: TokenGetter,
  returnId: string,
  lines: { returnLineId: string; soundQuantity: number; defectNote?: string | null }[],
): Promise<{ returnId: string; number: string; status: ReturnStatus; inspectedOn: string; lines: (ReturnLineInspection & { returnLineId: string })[] }> {
  return apiFetch(getToken, `/api/v1/returns/${encodeURIComponent(returnId)}/inspection`, { method: 'POST', body: { lines }, idempotencyKey: newIdempotencyKey() });
}

/** Auswahl „Retoure anlegen“ zu einem Auftrag (nur lesen). */
export function fetchReturnDraft(getToken: TokenGetter, orderId: string): Promise<ReturnDraft> {
  return apiFetch<ReturnDraft>(getToken, `/api/v1/returns/draft?orderId=${encodeURIComponent(orderId)}`);
}

// ---------------------------------------------------------------------------
// Zeiterfassung (Story 22.1, 22.5): `/api/v1/staff/…`

export type ClockKind = 'clock_in' | 'break_start' | 'break_end' | 'clock_out';
export type ClockState = 'absent' | 'present' | 'on_break';

export interface StaffDayEntry {
  type: 'clock_in' | 'clock_out' | 'break';
  from: string;
  to: string | null;
  text: string;
  /** Aus einem gültigen Nachtrag (Kennzeichen „nachgetragen“, Story 22.5). */
  amended: boolean;
  /** Ereignisse der Zeile (Ziel von „Ändern“). */
  eventIds: string[];
  /** Gehen nach Mitternacht (Folgetag). */
  nextDay: boolean;
  /** Angerechnete Uhrzeit `HH:MM` (Rundung, Story 22.17), nur wo sie von der echten abweicht. */
  credited: string | null;
}

/** Art eines Nachtrags (Story 22.5): Kommen, Gehen, Pause von–bis. */
export type AmendmentKind = 'clock_in' | 'clock_out' | 'break';

/** Verlauf je Tag (wer, wann, alt → neu, Grund; Story 22.5). */
export interface StaffAmendmentHistoryItem {
  id: string;
  kind: AmendmentKind;
  kindLabel: string;
  oldValue: string;
  newValue: string;
  reason: string;
  /** `pending` = wartet auf Freigabe (abgeschlossener Monat, Story 22.19). */
  status: 'valid' | 'pending' | 'rejected';
  statusLabel: string;
  createdBy: string;
  createdAt: string;
  text: string;
  rejectedText: string | null;
  rejectedBy: string | null;
  rejectedAt: string | null;
  canReject: boolean;
}

/** Zeile der Liste „Nachträge“ (nur Admin, Story 22.5). */
export interface StaffAmendment extends StaffAmendmentHistoryItem {
  employeeId: string;
  displayName: string;
  workDate: string;
  dayText: string;
  line: string;
}

export interface AmendTimeEntryBody {
  employeeId: string;
  date: string;
  kind: AmendmentKind;
  time: string;
  timeTo?: string | null;
  nextDay?: boolean;
  replacesEventIds?: string[];
  reason: string;
}

export interface AmendedTimeEntry {
  changed: boolean;
  amendmentId: string;
  employeeId: string;
  workDate: string;
  kind: AmendmentKind;
  oldValue: string;
  newValue: string;
  eventIds: string[];
  /** Story 22.19: Monat abgeschlossen → wartet auf Freigabe. */
  pending?: boolean;
  pendingText?: string | null;
}

export interface StaffDay {
  employee: { id: string; displayName: string; personnelNumber: string };
  date: string;
  entries: StaffDayEntry[];
  line: string;
  workMinutes: number;
  breakMinutes: number;
  workText: string;
  /** Story 22.4: Soll (0 = kein Soll), Saldo, „Ist 8:15 h · Soll 8:00 h · Saldo +0:15 h“, Feiertag. */
  targetMinutes: number;
  balanceMinutes: number;
  balanceText: string;
  holiday: string | null;
  /** Story 22.17: Ist mit echten Zeiten (gleich `workMinutes` ohne abweichende Rundung) und Versionen. */
  stampedMinutes: number;
  versions: { timeBalance: string; workdays: string; timeRounding: string; roundingSetting: number; halfDays: number[] };
  /** Verlauf der Nachträge des Tages (Story 22.5). */
  history: StaffAmendmentHistoryItem[];
}

export interface StaffToday {
  /** `null` = für die Person ist kein Mitarbeiter angelegt (keine Stempel-Karte auf der Startseite). */
  day: StaffDay | null;
  state: ClockState;
  buttons: { kind: ClockKind; label: string }[];
  sentence: string;
  forgotten: { date: string; text: string } | null;
  blocked: string | null;
  last: { id: string; kind: ClockKind; undoableUntil: string } | null;
}

export interface RecordedClockEvent {
  changed: boolean;
  eventId: string;
  kind: ClockKind;
  occurredAt: string;
  workDate: string;
  undoableUntil: string;
  state: ClockState;
}

export interface Employee {
  id: string;
  userId: string;
  userName: string | null;
  displayName: string;
  personnelNumber: string;
  entryDate: string;
  exitDate: string | null;
  status: 'active' | 'exited' | 'upcoming' | 'anonymized';
  statusLabel: string;
  /** Story 22.13: Ende der Aufbewahrung (31.12.); `null` ohne Austritt oder nach dem Anonymisieren. */
  retentionEnd: string | null;
  /** Tag, ab dem der Takt anonymisiert (Tag nach Fristende). */
  anonymizeOn: string | null;
  /** Zeitpunkt des Anonymisierens (ISO). */
  anonymizedAt: string | null;
}

export interface EmployeeList {
  items: Employee[];
  linkableUsers: { id: string; displayName: string; email: string }[];
  /** Aufbewahrung in Jahren ab Ende des Austrittsjahres (Einstellung, Story 22.13). */
  retentionYears: number;
}

/** Story 22.13: Vorschau der Auskunft je Mitarbeiter (Anzahl je Bereich). */
export interface EmployeePrivacyPreview {
  employeeId: string;
  displayName: string;
  personnelNumber: string;
  counts: {
    clockEvents: number;
    amendments: number;
    absences: number;
    sicknesses: number;
    leaveYears: number[];
    overtimeCorrections: number;
    pendingChanges: number;
    workModels: number;
    monthCloses: number;
  };
}

/** Story 22.13: gesicherte Monatssummen eines anonymisierten Mitarbeiters (Minuten). */
export interface RetainedMonthTotals {
  items: { month: string; targetMinutes: number; actualMinutes: number; balanceMinutes: number }[];
}

// Story 22.4: Arbeitszeitmodell, Soll/Ist/Saldo, Team, Arbeitszeitgesetz.

/** Reiter der Zeiterfassung (`GET /api/v1/staff/me/access`). */
export interface StaffAccess {
  team: boolean;
  workingTime: boolean;
  hasEmployee: boolean;
  /** Reiter „Genehmigen“ (Story 22.6): Admin oder Recht „Abwesenheiten genehmigen“. */
  approve: boolean;
}

export interface StaffPerson {
  id: string;
  displayName: string;
  personnelNumber: string;
}

export interface StaffMonthDay {
  date: string;
  weekday: string;
  dateLabel: string;
  holiday: string | null;
  /** Halber Arbeitstag (z. B. 24.12.): Soll halbiert (Story 22.25). */
  halfDay: boolean;
  counted: boolean;
  line: string;
  /** Zeilen der Tagesliste: echte Zeit und angerechnete Uhrzeit (nur wo sie abweicht, Story 22.17). */
  entries: { text: string; credited: string | null }[];
  targetMinutes: number;
  actualMinutes: number;
  /** Ist mit echten Zeiten („gestempelt“, Story 22.17). */
  stampedMinutes: number;
  balanceMinutes: number;
  text: string;
  /** Abwesenheit mit Wort („Urlaub“, „Urlaub ½“, „Urlaub beantragt“; Story 22.6); `null` = keine. */
  absence: string | null;
  /** Monat des Tags abgeschlossen (Story 22.19). */
  closed: boolean;
}

export interface StaffMonthWeek {
  key: string;
  label: string;
  targetMinutes: number;
  actualMinutes: number;
  balanceMinutes: number;
  balanceText: string;
  /** „Soll 40:00 h · Ist 42:45 h“. */
  sumsText: string;
  days: StaffMonthDay[];
}

/** Monat mit Soll/Ist/Saldo (`GET /api/v1/staff/me/months/{month}` bzw. `/employees/{id}/months/{month}`). */
export interface StaffMonth {
  employee: StaffPerson;
  /** Eintrittstag – Untergrenze der Monatswahl (Ticket 22.28). */
  entryDate: string;
  month: string;
  monthLabel: string;
  range: { from: string; to: string } | null;
  untilToday: boolean;
  hasModel: boolean;
  notice: string | null;
  targetMinutes: number;
  actualMinutes: number;
  stampedMinutes: number;
  balanceMinutes: number;
  headText: string;
  weeks: StaffMonthWeek[];
  versions: { timeBalance: string; workdays: string; timeRounding: string; roundingSetting: number; halfDays: number[] };
  /** Überstundenkonto sichtbar (eigene Person oder Admin, Story 22.10). */
  accountVisible: boolean;
  /** Überstundenkonto mit Stand Ende des Monats (laufender Monat: heute); `null` = nicht sichtbar oder noch keins. */
  account: OvertimeAccount | null;
  /** Monatsabschluss (Story 22.19): abgeschlossen, „nachträglich geändert“, wartende Änderungen. */
  closed: { closed: boolean; retroChanged: boolean; pending: number };
}

/** Wartende Änderung in einem abgeschlossenen Monat (Story 22.19, `GET /api/v1/staff/pending-changes`). */
export interface PendingChangeItem {
  id: string;
  kind: 'amendment' | 'absence' | 'sickness' | 'overtime';
  kindLabel: string;
  employee: { id: string; displayName: string };
  month: string;
  monthLabel: string;
  /** „Fr 26.02.2027 · Gehen 16:30 → 17:00“. */
  text: string;
  reason: string | null;
  createdBy: string;
  createdAt: string;
}

/** Monatsübersicht (Story 22.19, `GET /api/v1/staff/month-close?month=`, nur Admin). */
export interface MonthCloseView {
  month: string;
  monthLabel: string;
  enabled: boolean;
  over: boolean;
  status: 'open' | 'partial' | 'closed';
  statusLabel: string;
  total: number;
  closedCount: number;
  readyCount: number;
  pendingCount: number;
  targetMinutes: number;
  actualMinutes: number;
  balanceMinutes: number;
  headText: string;
  people: {
    employee: StaffPerson;
    targetMinutes: number;
    actualMinutes: number;
    balanceMinutes: number;
    targetText: string;
    actualText: string;
    balanceText: string;
    status: 'open' | 'closed' | 'reopened';
    statusLabel: string;
    retroChanged: boolean;
    pending: PendingChangeItem[];
    pendingText: string;
  }[];
  history: { at: string; dateText: string; text: string; tone: 'ready' | 'check' | 'info' }[];
}

/** Fassung der Monatsübersicht für das Lohnbüro (Story 22.12). */
export interface MonthSummaryItem {
  id: string;
  versionNo: number;
  /** „Fassung 2 – ersetzt Fassung 1 vom 01.03.2027“. */
  title: string;
  createdBy: string;
  createdAt: string;
  createdAtText: string;
  status: 'ready' | 'pending';
  statusLabel: string;
  valuesText: string;
  flagsText: string | null;
  pdfName: string;
  xlsxName: string;
}

/** Monatsübersicht für das Lohnbüro (Story 22.12, `GET /api/v1/staff/month-summaries?month=`, nur Admin). */
export interface MonthSummaryList {
  month: string;
  monthLabel: string;
  people: { employeeId: string; displayName: string; personnelNumber: string; summaries: MonthSummaryItem[] }[];
}

/** Ergebnis von `POST /api/v1/staff/month-summaries` (`created` = neue Fassung). */
export interface CreatedMonthSummaries {
  month: string;
  summaries: { id: string; employeeId: string; versionNo: number; created: boolean }[];
}

/** Hinweis am Überstundenkonto (Story 22.10): `check` = bernstein (Grenzen), `clarify` = rot (Minijob). Nie Sperre. */
export interface OvertimeHint {
  kind: 'minijob' | 'upper' | 'lower';
  limitMinutes: number;
  text: string;
  tone: 'check' | 'clarify';
}

/** Überstundenkonto (Story 22.10, FR-139, FR-141). Gerechnet nur auf dem Server (`computeTimeBalance`). */
export interface OvertimeAccount {
  standDate: string;
  /** „Stand 28.02.2027“. */
  standText: string;
  minutes: number;
  /** „+0:30 h“. */
  text: string;
  hints: OvertimeHint[];
  /** Saldo je Monat, neuester zuerst. */
  months: { month: string; label: string; balanceMinutes: number; text: string }[];
  /** Korrekturen mit Grund, neueste zuerst. */
  corrections: { id: string; date: string; minutes: number; text: string; reason: string }[];
  versions: { timeBalance: string; workdays: string; timeRounding: string; roundingSetting: number; settings: Record<string, number>; halfDays: number[] };
}

export interface StaffTeamMember {
  employee: StaffPerson;
  state: ClockState;
  stateLabel: string;
  month: { range: { from: string; to: string } | null; targetMinutes: number; actualMinutes: number; stampedMinutes: number; balanceMinutes: number; headText: string; hasModel: boolean };
  targetText: string;
  /** „161:30 h“, bei abweichender Rundung „161:15 h angerechnet“. */
  actualText: string;
  /** „161:35 h gestempelt“ nur bei abweichender Rundung (Story 22.17). */
  stampedText: string | null;
  balanceText: string;
  /** Abwesenheiten im Monat (Story 22.6): „Urlaub 22.03.–02.04.2027 · genehmigt“. */
  absences: { id: string; from: string; to: string; label: string; status: AbsenceStatus; text: string }[];
  /** Spalte „Konto“ (Story 22.10): nur für den Admin, sonst `null`. */
  account: Pick<OvertimeAccount, 'standDate' | 'standText' | 'minutes' | 'text' | 'hints' | 'versions'> | null;
}

/** Reiter „Team“ (`GET /api/v1/staff/team?month=`). */
export interface StaffTeam {
  month: string;
  monthLabel: string;
  today: string;
  /** Laufender Monat: Werte nur bis heute gerechnet („Stand heute“). */
  untilToday: boolean;
  members: StaffTeamMember[];
}

// Story 22.6: Urlaub & Abwesenheit, Genehmigen, Anspruch und Anfangsbestand.

export type AbsenceStatus = 'requested' | 'approved' | 'rejected' | 'cancel_requested' | 'cancelled';

/**
 * Übertrag-Karte (Story 22.9): offen (`info`), Vorwarnung (`check`, mit „noch 28 Tage“), rechtzeitig verbraucht
 * (`ready`), verfallen (`info`). Sätze vom Server.
 */
export interface LeaveCarryover {
  state: 'open' | 'warning' | 'used' | 'expired';
  days: number;
  fromYear: number;
  expiresOn: string;
  daysLeft: number;
  pastExpiry: boolean;
  warningDays: number;
  text: string;
  hint: string;
  countdownText: string | null;
  /** Name des Countdown-Rings (nur Vorwarnung). */
  countdownLabel: string | null;
}

/** Resturlaub-Zeile: „Resturlaub 2027: 25 von 33 Tagen“ mit Aufklappen. */
export interface LeaveLine {
  year: number;
  text: string;
  carryoverText: string | null;
  carryover: LeaveCarryover | null;
  details: { label: string; value: string }[];
  remainingDays: number;
  totalDays: number;
  versions: { leaveBalance: string; workdays: string };
}

/** Ein Antrag (eigene Liste und Genehmigen-Liste). */
export interface AbsenceItem {
  id: string;
  typeCode: string;
  typeLabel: string;
  from: string;
  to: string;
  firstDayHalf: boolean;
  lastDayHalf: boolean;
  rangeText: string;
  days: number;
  daysText: string;
  status: AbsenceStatus;
  statusLabel: string;
  reasonText: string | null;
  note: string | null;
  canCancel: boolean;
  version: number;
}

/** Reiter „Urlaub & Abwesenheit“ (`GET /api/v1/staff/me/leave?year=`). */
export interface MyLeave {
  year: number;
  hasEmployee: boolean;
  line: LeaveLine | null;
  absences: AbsenceItem[];
  types: { code: string; label: string; needsApproval: boolean; countsAgainstLeave: boolean }[];
}

/** Eintrag im Kalender (Story 22.8); geschwärzt = `masked`, nur „abwesend“, ohne Kennung. */
export interface StaffCalendarEntry {
  id: string | null;
  from: string;
  to: string;
  openEnd: boolean;
  firstDayHalf: boolean;
  lastDayHalf: boolean;
  label: string;
  masked: boolean;
  sickness: boolean;
  status: AbsenceStatus;
  tone: 'ready' | 'check' | 'clarify' | 'neutral';
  text: string;
}

/** Kalender (`GET /api/v1/staff/calendar?month=&view=`, Story 22.8). */
export interface StaffCalendar {
  month: string;
  monthLabel: string;
  view: 'me' | 'team';
  today: string;
  days: { date: string; weekday: string; day: number; holiday: string | null; free: boolean; halfDay: boolean; isToday: boolean }[];
  people: { employeeId: string; displayName: string; self: boolean; entries: StaffCalendarEntry[] }[];
  hasEmployee: boolean;
  countdown: string | null;
  nextVacation: { from: string; to: string; rangeText: string; inText: string; days: number } | null;
  notice: string | null;
  workdaysVersion: string;
  /** Betriebsurlaube im Monat (Story 22.20): Balken „Betriebsurlaub · Brückentag“ für alle. */
  companyLeaves: { id: string; from: string; to: string; label: string; rangeText: string; text: string }[];
}

/** Wer ist im Zeitraum sonst weg? (`GET /api/v1/staff/calendar/overlap?from=&to=`, Story 22.24). */
/** Antwort von `GET /api/v1/staff/calendar/absent` (Story 23.9): nur „abwesend“ je Person, ohne Art und Grund. */
export interface StaffAbsent {
  from: string;
  to: string;
  people: { employeeId: string; userId: string | null; displayName: string; self: boolean; ranges: { from: string; to: string; text: string; openEnd: boolean }[] }[];
}

export interface StaffAbsenceOverlap {
  from: string;
  to: string;
  rangeText: string;
  granularity: 'day' | 'week';
  columns: { from: string; to: string; label: string; weekday: string | null; holiday: string | null; free: boolean; isToday: boolean; title: string }[];
  people: {
    employeeId: string;
    displayName: string;
    coverage: 'full' | 'partial';
    coverageText: string;
    segments: { from: string; to: string; startColumn: number; endColumn: number; label: string; shortLabel: string; requested: boolean; tone: 'ready' | 'check' | 'clarify' | 'neutral'; text: string }[];
  }[];
  count: number;
  headline: string;
  workdaysVersion: string;
}

/** Live-Anzeige im Blatt „Urlaub beantragen“ (`POST /api/v1/staff/me/absences/preview`). */
export interface AbsencePreview {
  days: number;
  countsAgainstLeave: boolean;
  needsApproval: boolean;
  text: string;
  exceedsText: string | null;
  holidays: { date: string; name: string }[];
  remainingAfterDays: number | null;
  versions: { leaveDays: string; workdays: string; leaveBalance: string | null; settings: number };
}

/** Offener Antrag oder offene Stornierung (`GET /api/v1/staff/approvals`). */
export interface OpenAbsence extends AbsenceItem {
  employee: StaffPerson;
  kind: 'request' | 'cancel';
  remainingAfterText: string | null;
  /** Ablehnen-Blatt (Story 22.24): „Resturlaub danach: 15 Tage (bei Genehmigung wären es 12 Tage)“. */
  rejectRemainingText: string | null;
  /** Dieselben Werte für die Vergleichskacheln. */
  rejectRemainingParts: { rejectedText: string; approvedText: string } | null;
  overlapText: string | null;
  canDecide: boolean;
  blockedText: string | null;
}

/** Urlaub einer Person für den Admin (`GET /api/v1/staff/employees/{id}/leave`). */
export interface LeaveAdmin {
  employee: StaffPerson;
  entitlementDays: number | null;
  baseText: string;
  opening: { id: string; year: number; carryoverDays: number; takenDays: number; reason: string; text: string } | null;
  line: LeaveLine;
}

// Story 22.7: Krankheit (nur Admin).

export type SicknessStatus = 'active' | 'pending' | 'cancelled';

/** Krankheit einer Person (`GET /api/v1/staff/employees/{id}/sicknesses?year=`, nur Admin). */
export interface SicknessItem {
  id: string;
  typeCode: string;
  typeLabel: string;
  from: string;
  /** `null` = bis auf Weiteres. */
  to: string | null;
  /** „24.–25.03.2027“ bzw. „ab 24.03.2027 · bis auf Weiteres“. */
  rangeText: string;
  status: SicknessStatus;
  statusLabel: string;
  canEnd: boolean;
  canCancel: boolean;
  version: number;
}

export interface SicknessList {
  employee: StaffPerson;
  year: number;
  items: SicknessItem[];
  /** Arten „nur Admin“ (Krank, Kind krank). */
  types: { code: string; label: string; creditsLeave: boolean }[];
}

/** Vorschau `POST /api/v1/staff/employees/{id}/sicknesses/preview`: „2 Urlaubstage werden gutgeschrieben“. */
export interface SicknessPreview {
  creditDays: number;
  text: string;
  rangeText: string;
}

export interface SicknessResult {
  sicknessId: string;
  status: SicknessStatus;
  version: number;
  creditDays: number;
  /** Satz zur gespeicherten Gutschrift; `null` = unverändert. */
  creditText: string | null;
  /** Story 22.19: „Gespeichert – wartet auf Freigabe …“; sonst `null`. */
  pendingText?: string | null;
}

// Story 22.20: Betriebsurlaub (nur Admin).

/** Eingabe „Betriebsurlaub anlegen“ (Vorschau und Anlegen). */
export interface CompanyLeaveInput {
  from: string;
  to: string;
  label: string;
  firstDayHalf?: boolean;
  lastDayHalf?: boolean;
}

/** Betriebsurlaube eines Jahres (`GET /api/v1/staff/company-leaves?year=`). */
export interface CompanyLeaveList {
  year: number;
  today: string;
  items: {
    id: string;
    from: string;
    to: string;
    label: string;
    firstDayHalf: boolean;
    lastDayHalf: boolean;
    rangeText: string;
    people: number;
    days: number;
    /** „3 Personen · 3 Urlaubstage“. */
    summaryText: string;
    /** „Anna Test −1,5 Tage“; sonst `null`. */
    minusText: string | null;
    /** „in 36 Tagen · löschbar“, „läuft“, „vorbei“. */
    stateText: string;
    canDelete: boolean;
    version: number;
  }[];
}

/** Wirkung je Person (`POST /api/v1/staff/company-leaves/preview`). */
export interface CompanyLeavePreview {
  from: string;
  to: string;
  rangeText: string;
  people: {
    employeeId: string;
    displayName: string;
    days: number;
    daysText: string;
    marks: { date: string; mark: 'full' | 'half' | 'none' }[];
    reason: string | null;
    remainingAfterDays: number;
    remainingAfterText: string;
    negative: boolean;
  }[];
  notAffected: { employeeId: string; displayName: string; reason: string }[];
  totals: { people: number; days: number; negative: number };
  hintText: string | null;
}

export interface CreatedCompanyLeave {
  companyLeaveId: string;
  version: number;
  people: number;
  days: number;
  items: { employeeId: string; displayName: string; days: number; remainingAfterDays: number }[];
  hintText: string | null;
}

/** Auswertung nach Arbeitszeitgesetz (`GET /api/v1/staff/working-time?from=&to=`, nur Admin). */
export interface WorkingTimeReportView {
  from: string;
  to: string;
  items: { employee: StaffPerson; date: string; kind: 'over_max_daily' | 'break_too_short' | 'rest_too_short'; actualMinutes: number; limitMinutes: number; text: string }[];
  /** Story 22.21: Tage mit einer Schicht ohne „Gehen“ (ohne heute) – nicht geprüft. */
  unchecked: { employee: StaffPerson; date: string; reason: 'no_clock_out'; text: string }[];
  versions: { workingTime: string; settings: { from: string; to: string; versions: Record<string, number> }[] };
}

export interface WorkModel {
  id: string;
  validFrom: string;
  targetMinutes: number[];
  targetStarts: (string | null)[];
  minijob: boolean;
  templateName: string | null;
  version: number;
}

/** Arbeitszeitmodelle einer Person (`GET /api/v1/staff/employees/{id}/work-models`, nur Admin). */
export interface WorkModelList {
  employee: StaffPerson & { approvesAbsences: boolean; entryDate: string; exitDate: string | null };
  models: WorkModel[];
  templates: { name: string; targetMinutes: number[] }[];
}

// Story 22.17: Rundung je Mitarbeiter (nur Admin).

export type TimeRoundingDirection = 'up' | 'down' | 'nearest';

export interface TimeRoundingVersion {
  validFrom: string;
  isDefault: boolean;
  fromTargetStart: boolean;
  clockIn: { grid: number; direction: TimeRoundingDirection };
  clockOut: { grid: number; direction: TimeRoundingDirection };
  /** „minutengenau“, „Kommen ab Soll-Beginn · Gehen 15 min abrunden“. */
  text: string;
}

/** Rundung aller Mitarbeiter (`GET /api/v1/staff/time-rounding`). */
export interface StaffTimeRounding {
  /** Version der Einstellung `staff_time_rounding` (für `expectedVersion`). */
  version: number;
  defaultText: string;
  employees: {
    employee: StaffPerson;
    current: TimeRoundingVersion | null;
    /** „gilt jetzt: minutengenau (Startwert)“. */
    currentText: string;
    planned: (TimeRoundingVersion & { label: string })[];
  }[];
}

/** Vorschau der Rundung mit ungespeicherten Werten (`GET /api/v1/staff/employees/{id}/time-rounding/preview`, Story 22.23). */
export interface TimeRoundingPreview {
  date: string;
  dateText: string;
  targetStart: string | null;
  exampleIn: string;
  exampleOut: string;
  breakMinutes: number;
  creditedIn: string;
  creditedOut: string;
  stampedMinutes: number;
  creditedMinutes: number;
  exact: boolean;
  noTargetStart: boolean;
  /** „Kommen 07:50 → angerechnet 08:00“. */
  comingText: string;
  goingText: string;
  /** „8:35 h gestempelt → 8:15 h angerechnet“. */
  hoursText: string;
  /** Hinweis („Ohne Arbeitsbeginn …“, „keine Rundung: angerechnet = gestempelt“) oder `null`. */
  note: string | null;
  roundingVersion: string;
}

/** Ticket 37: Versand- bzw. Zahlart aus Shopware (`GET /api/v1/orders/shop-methods`). */
export interface ShopMethodEntry {
  kind: 'shipping' | 'payment';
  /** `null` bei „von Hand“ (nur in der Zuordnung). */
  shopwareId: string | null;
  name: string;
  active: boolean;
  origin: 'shopware' | 'removed' | 'manual';
  /** Zuordnung laut Einstellung; `null` = noch nicht zugeordnet. */
  mapping: { value: string; label: string } | null;
  /** Offener Konflikt der Umbenennung mit der Zuordnung (nichts geändert, bitte prüfen); `null` = keiner. */
  conflict: { reason: 'occupied' | 'ambiguous'; name: string; from: string; current: string; previous: string } | null;
}

export type ShopMethodReadState = 'running' | 'waiting' | 'done' | 'incomplete' | 'aborted' | 'skipped' | 'failed';

export interface ShopMethodsView {
  shipping: ShopMethodEntry[];
  payment: ShopMethodEntry[];
  readAt: string | null;
  readSwitch: 'off' | 'shadow' | 'live';
  canRead: boolean;
  lastRead: {
    trigger: 'manual' | 'scheduled';
    state: ShopMethodReadState;
    text: string;
    requestedAt: string;
    finishedAt: string | null;
    /** Gelesen im Probebetrieb: nur berechnet, nichts geändert. */
    probe: boolean;
    /** Was sich geändert hat bzw. (Probe) ändern würde. */
    changes: string | null;
  } | null;
}

/** Antwort auf „Jetzt aus Shopware lesen“ (`POST /api/v1/orders/shop-methods/read`). */
export interface ShopMethodReadAnswer {
  requested: boolean;
  text: string | null;
  view: ShopMethodsView;
}

/** Heute automatisch gebuchter Kontoumsatz (Story 13.1, `GET /api/v1/billing/bank-payments/done-today`). */
export interface BankPaymentDoneToday {
  bankPaymentId: string;
  runMode: 'live' | 'probe';
  account: string;
  bookingDate: string;
  /** Cent mit Vorzeichen als Dezimaltext. */
  amountMinor: string;
  currency: string;
  documentNumbers: string[];
  customerId: string | null;
  customerLabel: string | null;
  /** Auftraggeber und Verwendungszweck des Umsatzes (Spalte „Auftraggeber · Verwendungszweck“). */
  counterpartyName: string | null;
  remittance: string;
  reason: string;
  matchedAt: string;
  /** `checkout` = Checkout-Zahlung, schon zugeordnet (Story 13.3, Z-15): nichts gebucht, Beleg = Checkout-Rechnung. */
  kind: 'booked' | 'checkout';
  /** PayPal-Gebühr in Cent (negativ, Dezimaltext); nur Information, nie gebucht. `null` = keine. */
  feeMinor: string | null;
}

/** Transit-Umsatz (Story 13.3, `GET /api/v1/billing/bank-payments/transit`): eigenes Geld zwischen zwei Konten. */
export interface BankPaymentTransit {
  bankPaymentId: string;
  runMode: 'live' | 'probe';
  account: string;
  bookingDate: string;
  /** Cent mit Vorzeichen als Dezimaltext. */
  amountMinor: string;
  currency: string;
  feeMinor: string | null;
  /** Gegenseite laut Konto (Personendaten möglich, nur zur Anzeige). */
  counterpartyName: string | null;
  remittance: string;
  reason: string;
  matchedAt: string;
}

export interface BankPaymentsTransit {
  /** Einstellung `billing_transit_pair_max_days`; `null` = fehlt, die Ansicht bildet keine Paare. */
  pairMaxDays: number | null;
  items: BankPaymentTransit[];
}

/** Stand „Jetzt abrufen“ für PayPal (`GET /api/v1/paypal/read-now`, Story 13.3); kein Tageskontingent. */
export interface PaypalReadNowState {
  switchMode: 'off' | 'shadow' | 'live';
  runMode: 'live' | 'probe' | null;
  blocked: string | null;
  running: boolean;
  lastReadAt: string | null;
  lastRead: number;
  lastNew: number;
  lastSkipped: number;
  /** Umsätze mit Status „ausstehend“ im letzten Abruf. */
  lastPending: number;
}

/**
 * Story 13.7 (Z-07): offener Vorschlag „Skonto nach Frist“ in „Braucht dich“ (`GET /api/v1/billing/bank-payments/suggestions`).
 * Nichts davon ist gebucht. Beträge in Cent als Dezimaltext. Abnahme durch Steuerberater/Rudolf nötig.
 */
export interface CashDiscountSuggestion {
  matchId: string;
  runMode: 'live' | 'probe';
  bankPaymentId: string;
  account: string;
  bookingDate: string;
  /** Gezahlter Betrag (mit Skontoabzug). */
  amountMinor: string;
  currency: string;
  /** Auftraggeber laut Bank (Personendaten, nur zur Anzeige). */
  counterpartyName: string | null;
  openItemId: string;
  documentNumber: string;
  invoiceId: string | null;
  /** Beleg der Rechnung (Link); `null` bei einem Xentral-Posten. */
  documentId: string | null;
  customerId: string;
  customerLabel: string | null;
  /** Skonto laut Rechenkern = was ohne Gewähren offen bliebe. */
  discountMinor: string;
  cashDiscountUntil: string | null;
  /** Letzter Tag mit Kulanz (Einstellung „Skonto-Kulanz“). */
  acceptedUntil: string | null;
  openMinor: string;
  /** Rechnung hat sich seit dem Zahlungseingang geändert – keine Entscheidung möglich. */
  stale: boolean;
  title: string;
  matchedAt: string;
  /** Verwendungszweck der Zahlung (Personendaten, nur zur Anzeige). */
  remittance: string;
}

export interface CashDiscountSuggestions {
  items: CashDiscountSuggestion[];
}

/** Antwort auf `POST /api/v1/billing/bank-payments/suggestions/{matchId}/decision` (Story 13.7). */
export interface CashDiscountSuggestionDecided {
  decision: 'cash_discount_granted' | 'rest_open' | 'dismissed';
  openMinor: string;
  cashDiscountMinor: string;
  decidedOn: string;
}

/**
 * Story 13.8: ein angebotener Beleg einer Zahlungszuordnung (`GET …/suggestions/{matchId}/options`). Beträge in Cent als
 * Dezimaltext. Abnahme durch Steuerberater/Rudolf nötig.
 */
export interface PaymentSuggestionOption {
  openItemId: string;
  kind: 'invoice' | 'proforma';
  documentNumber: string;
  openMinor: string;
  currency: string;
  invoiceDate: string | null;
  dueDate: string | null;
  documentId: string | null;
  /** Proforma: Bestätigen gibt den Auftrag sofort frei, kein „Rückgängig“ (die Seite fragt vorher nach). */
  releasesOrder: boolean;
}

/** Was „Zuordnung bestätigen“, „Anderen Beleg wählen“ und der KI-Kasten anbieten (Story 13.8). */
export interface PaymentSuggestionOptions {
  matchId: string;
  runMode: 'live' | 'probe';
  bankPaymentId: string;
  code: string;
  amountMinor: string;
  currency: string;
  /** Vorschlag des Rechenkerns. */
  proposed: PaymentSuggestionOption[];
  /** Andere passende Rechnungen (nur bei „nicht eindeutig“, älteste zuerst). */
  alternatives: PaymentSuggestionOption[];
  /** Offener KI-Vorschlag (nur Ansehen, nie gebucht); `null` ohne. */
  ai: { suggestionId: string; reason: string; confidence: number; options: PaymentSuggestionOption[] } | null;
  rejected: boolean;
  /** Höchstzahl Rechnungen, die eine Zahlung ausgleichen darf (Grenze des Servers). */
  maxInvoices: number;
  /** Gültige Entscheidung (auch nach dem Neuladen): bestätigt mit Frist für „Rückgängig“ bzw. abgelehnt; `null` = offen. */
  decision: { state: 'confirmed'; decisionId: string; decidedAt: string; undoableUntil: string | null; undoRemainingMs: number | null } | { state: 'rejected' } | null;
}

/**
 * Story 13.8 (HS 04.11.2026): Treffer der Belegsuche „Anderen Beleg wählen“
 * (`GET …/suggestions/{matchId}/open-invoices?q=`, höchstens 20, passende Beträge zuerst; leere Suche = die nächsten offenen
 * Rechnungen). Beträge in Cent als Dezimaltext.
 */
export interface OpenInvoiceHit {
  openItemId: string;
  documentNumber: string;
  openMinor: string;
  currency: string;
  dueDate: string;
  customerNumber: string | null;
  customerName: string | null;
  /** Offen = Betrag der Zahlung. */
  exact: boolean;
  /** Beleg zum Öffnen und Rechnungsdatum; `null` ohne Rechnung. */
  documentId: string | null;
  invoiceDate: string | null;
}

export interface OpenInvoiceSearch {
  items: OpenInvoiceHit[];
}

/** Antwort auf `POST …/suggestions/{matchId}/confirm` (Story 13.8): gebucht, „Rückgängig“ bis `undoableUntil`. */
export interface ConfirmedPaymentMatch {
  matchId: string;
  bankPaymentId: string;
  documentNumbers: string[];
  amountMinor: string;
  currency: string;
  decidedOn: string;
  /** Genau diese Bestätigung; „Rückgängig“ schickt sie mit (sonst könnte es eine spätere treffen). */
  decisionId: string;
  /** Bis wann „Rückgängig“ geht; `null` = kein Rückgängig (z. B. Anrechnung auf eine Proforma). */
  undoableUntil: string | null;
  /** Restzeit für „Rückgängig“ in ms laut Uhr des ERP (unabhängig von der Uhr des Browsers); `null` = keins. */
  undoRemainingMs: number | null;
}

export interface BankPaymentsDoneToday {
  today: string;
  lastReadAt: string | null;
  items: BankPaymentDoneToday[];
}

/** Zeile des Probelaufs Umbuchungen (Story 13.18, `GET /api/v1/billing/bank-payments/transit-preview`). */
export interface TransitPreviewItem {
  id: string;
  runMode: 'live' | 'probe';
  bookingDate: string;
  account: string;
  /** Auftraggeber und Verwendungszweck laut Bank (Personendaten, nur zur Anzeige). */
  counterpartyName: string | null;
  remittance: string;
  /** Cent mit Vorzeichen als Dezimaltext. */
  amountMinor: string;
  currency: string;
  /** Gespeichertes Transit-Muster, mit dem `matchPayment` die Buchung als Umbuchung einordnet. */
  pattern: { account: string; counterparty: string };
}

export interface TransitPreview {
  from: string;
  to: string;
  days: number;
  items: TransitPreviewItem[];
}

/** Stand von „Jetzt abrufen“ für die VR-Bank über EBICS (`GET /api/v1/ebics/read-now`, Story 13.22). */
export interface EbicsReadNowState {
  switchMode: 'off' | 'shadow' | 'live';
  runMode: 'live' | 'probe' | null;
  blocked: string | null;
  running: boolean;
  lastReadAt: string | null;
  /** Die Bank hat den Zugang gesperrt (Satz mit EBICS-Code); `null` = nicht gesperrt. */
  locked: string | null;
  /** Vorgemerkte, noch nicht gebuchte Umsätze (PDNG) im letzten gelungenen Abruf – nie als Zahlung übernommen. */
  lastPending: number;
  /** Gebuchte Umsätze aus camt.052 im letzten gelungenen Abruf – nur Anzeige, gebucht wird mit dem Tagesauszug. */
  lastIntraday: number;
  quota: { used: number; limit: number | null };
}

/** Antwort auf „Jetzt abrufen“ für PayPal (`POST /api/v1/paypal/read-now`, Story 13.3); kein Tageskontingent. */
export interface PaypalReadNowResult {
  outcome: 'started' | 'running' | 'off' | 'unavailable';
  sideEffectId: string | null;
  message: string;
}

/** Zustand der Einrichtung „Bank (EBICS)“ (`GET /api/v1/ebics/setup`, Story 13.22). Nur öffentliche Teile, nie ein privater Schlüssel. */
export interface EbicsSetupState {
  state: 'new' | 'keys_sent' | 'letter_sent' | 'bank_keys_fetched' | 'active' | 'locked';
  stateLabel: string;
  missingSettings: string[];
  writeMode: 'off' | 'shadow' | 'live';
  readMode: 'off' | 'shadow' | 'live';
  keysStored: boolean;
  version: 'H004' | 'H005' | null;
  ownDigests: { A006: string; X002: string; E002: string } | null;
  bankDigests: { X002: string; E002: string } | null;
  iniSentAt: string | null;
  hiaSentAt: string | null;
  letterPrintedAt: string | null;
  bankKeysFetchedAt: string | null;
  confirmedAt: string | null;
  lockedAt: string | null;
  lastCode: string | null;
  running: { sendKeys: boolean; fetchBankKeys: boolean };
  canChange: boolean;
}

/** Antwort auf „Schlüssel senden“ / „Bankschlüssel abholen“ (`POST /api/v1/ebics/setup/…`). */
export interface EbicsSetupRequestResult {
  outcome: 'started' | 'running' | 'off' | 'shadow';
  sideEffectId: string | null;
  message: string;
}

/** Antwort auf „Jetzt abrufen“ für die VR-Bank (`POST /api/v1/ebics/read-now`). */
export interface EbicsReadNowResult {
  outcome: 'started' | 'running' | 'off' | 'unavailable';
  sideEffectId: string | null;
  message: string;
}

/** Story 12.2: Rechnungen des Kunden, die auf die Leitweg-ID warten (`GET /billing/customers/{id}/waiting-e-invoices`). */
export interface WaitingEInvoices {
  items: { documentId: string; number: string; versionNo: number; documentDate: string; grossMinor: string; currency: string; amountText: string }[];
}

/** Ein Auftrag, zu dem eine Retoure möglich ist (Auswahl „Retoure zu welchem Auftrag?“). */
export interface ReturnableOrder {
  id: string;
  number: string;
  customerName: string;
  customerNumber: string;
  orderedAt: string;
}

/** Aufträge suchen, zu denen eine Retoure möglich ist (`GET /api/v1/returns/orders?q=`, nur lesen). */
export function fetchReturnableOrders(getToken: TokenGetter, q: string): Promise<{ items: ReturnableOrder[] }> {
  return apiFetch<{ items: ReturnableOrder[] }>(getToken, `/api/v1/returns/orders?q=${encodeURIComponent(q)}`);
}
